import { useState, useEffect, useRef, useMemo, Fragment, lazy, Suspense } from "react";
import { Wordmark } from "./components/Icon.jsx";
import { Routes, Route, useNavigate, useParams, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { SUPA_URL, SUPA_KEY, BACKEND_URL, SH, supa } from "./config/supabase.js";
import { BH, api } from "./config/api.js";
import { normalizeCart } from "./utils/cartMerge.js";
import { Session, CustSession } from "./config/session.js";
import { useIsMobile } from "./hooks/useIsMobile.js";
import { useRealtimeOrders } from "./hooks/useRealtimeOrders.js";
import { simpleHash, today, cleanInstaHandle, slugToSubdomain } from "./utils/helpers.js";
import { thumbUrl } from "./utils/imageUpload.js";
import { buildUpiUrl, buildQrUrl, isValidUpi, UPI_APPS } from "./utils/upi.js";
import { gwAbsorbed, sellerPayout } from "./utils/fees.js"; // split fee-payer: payout keys off the stamped gw_absorbed flag, not tsf>0
import { DEMO_ID, DEMO_USER, DEMO_PRODUCTS, DEMO_ORDERS } from "./utils/demo.js";
import FontLoader from "./components/FontLoader.jsx";
import { Icon, BloomMark } from "./components/Icon.jsx";
import { ConfirmModal } from "./components/ConfirmModal.jsx";
import { ProBanner } from "./components/ProBanner.jsx";
import { UpiCheckoutModal } from "./components/UpiCheckoutModal.jsx";
import { Nav } from "./components/Nav.jsx";
import { MaintenancePage } from "./components/MaintenancePage.jsx";
import { dsAllowed } from "./config/dsAccess.js";
// Design Studio (PRO-gated, restricted-rollout allowlist — see src/config/dsAccess.js).
// Lazy-loaded: its bundle (dnd-kit, framer-motion, the whole builder) is only fetched by a
// store that's actually allowed to open it, not shipped to every visitor.
const DesignStudioGate = lazy(() => import("./pages/DesignStudio/DesignStudioGate.jsx"));
// PAGE-LEVEL CODE SPLITTING. These were all imported statically, so a shopper opening one store page also downloaded and
// parsed every seller dashboard (Orders, Products, Settings…), the legal pages and the admin tools — hundreds of KB of
// JavaScript they never use, on phones, before anything could respond. Now each page is fetched only when its route is
// opened (and prefetched in the background for logged-in sellers so their tabs still open instantly).
const Dashboard = lazy(() => import("./pages/Dashboard.jsx").then(m => ({ default: m.Dashboard })));
const Products = lazy(() => import("./pages/Products.jsx").then(m => ({ default: m.Products })));
const Orders = lazy(() => import("./pages/Orders.jsx").then(m => ({ default: m.Orders })));
const Settings = lazy(() => import("./pages/Settings.jsx").then(m => ({ default: m.Settings })));
const RestockRequests = lazy(() => import("./pages/RestockRequests.jsx").then(m => ({ default: m.RestockRequests })));
const Marketing = lazy(() => import("./pages/Marketing.jsx").then(m => ({ default: m.Marketing })));
const MarketingCompose = lazy(() => import("./pages/MarketingCompose.jsx").then(m => ({ default: m.MarketingCompose })));
const StoreReviewsQNA = lazy(() => import("./pages/StoreReviewsQNA.jsx").then(m => ({ default: m.StoreReviewsQNA })));
const StoreDirectory = lazy(() => import("./pages/StoreDirectory.jsx").then(m => ({ default: m.StoreDirectory })));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard.jsx").then(m => ({ default: m.CustomerDashboard })));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.jsx").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/ResetPassword.jsx").then(m => ({ default: m.ResetPassword })));
const HealthMonitor = lazy(() => import("./pages/HealthMonitor.jsx").then(m => ({ default: m.HealthMonitor })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.jsx").then(m => ({ default: m.PrivacyPolicy })));
const About = lazy(() => import("./pages/About.jsx").then(m => ({ default: m.About })));
const Terms = lazy(() => import("./pages/Terms.jsx").then(m => ({ default: m.Terms })));
const Refund = lazy(() => import("./pages/Refund.jsx").then(m => ({ default: m.Refund })));
const ReviewOrder = lazy(() => import("./pages/ReviewOrder.jsx").then(m => ({ default: m.ReviewOrder })));
const CategoryPage = lazy(() => import("./pages/CategoryPage.jsx").then(m => ({ default: m.CategoryPage })));
const SELLER_PAGES = [() => import("./pages/Dashboard.jsx"), () => import("./pages/Products.jsx"), () => import("./pages/Orders.jsx"), () => import("./pages/Settings.jsx"), () => import("./pages/RestockRequests.jsx"), () => import("./pages/Marketing.jsx"), () => import("./pages/MarketingCompose.jsx"), () => import("./pages/StoreReviewsQNA.jsx")];
const prefetchSellerPages = () => SELLER_PAGES.forEach((load) => { load().catch(() => {}); });
const RouteFallback = () => <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#9a9aa2", fontFamily: "inherit", fontSize: 14 }}>Loading…</div>;
import { Auth } from "./pages/Auth.jsx";
import { StorefrontPage } from "./pages/Storefront.jsx";
import { Cart } from "./pages/Cart.jsx";
import { ProductPage } from "./pages/ProductPage.jsx";
import { CustomerAuth } from "./pages/CustomerAuth.jsx";
import { Marketplace } from "./pages/Marketplace.jsx";
import OrderSuccess from "./pages/OrderSuccess.jsx";
import PaymentFailed from "./pages/PaymentFailed.jsx";
import { syncHostedPayFlag } from "./utils/razorpay.js";
import { syncMopPreviewFlag } from "./hooks/useMopConfig.js";
import { checkPendingCheckouts } from "./utils/pendingCheckouts.js";
import { setVisibleInterval } from "./utils/visibleInterval.js";
import { paidIndexOf, storePayState } from "./utils/settlementPaid.js";
import { navTemplateFor } from "./utils/navTheme.js";

// Store subdomain detection: storename.bloomhq.in → "storename". Returns null on the
// apex/www, the Vercel domain, and localhost, so the main site is completely unaffected.
function getStoreSubdomain() {
  const h = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  const m = h.match(/^([a-z0-9-]+)\.bloomhq\.in$/);
  if (!m) return null;
  const sub = m[1];
  if (["www", "admin", "api", "app", "mail", "ftp", "cdn"].includes(sub)) return null;
  return sub;
}

// ─── MARKETPLACE MANAGER COMPONENT ───────────────────────────────────────────
function MarketplaceManager({ stores, orders, loading, saving, dirty, dragId, onLoad, onSave, onChange, onDragId }) {
  const isMobile = useIsMobile();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [poolSort,    setPoolSort]    = useState("name"); // "name"|"products"|"orders"|"revenue"
  const [poolSearch,  setPoolSearch]  = useState("");
  const [poolFilters, setPoolFilters] = useState(new Set()); // "has-upi"|"no-upi"|"has-orders"|"no-orders"
  const togglePoolFilter = (f) => setPoolFilters(prev => { const s = new Set(prev); s.has(f) ? s.delete(f) : s.add(f); return s; });

  // Load on mount
  useEffect(() => { if (!stores.length) onLoad(); }, []);

  const TIERS = [
    { key: "top",    label: "Top Picks",   desc: "Shown first, premium placement",    color: "#2F63DD" },
    { key: "mid",    label: "Featured",     desc: "Main marketplace section",           color: "#2F63DD" },
    { key: "bottom", label: "Explore",      desc: "Lower section, still discoverable",  color: "#bbb0f4" },
  ];

  const tierStores = (tier) =>
    stores.filter(s => s.marketplace_tier === tier).sort((a,b) => (a.marketplace_rank||999) - (b.marketplace_rank||999));

  // Per-store order stats derived from admin orders array
  const storeStats = useMemo(() => {
    const map = {};
    (orders || []).forEach(o => {
      if (o.status !== "confirmed") return;
      if (!map[o.store_id]) map[o.store_id] = { orders: 0, revenue: 0 };
      map[o.store_id].orders++;
      map[o.store_id].revenue += Number(o.amount || 0);
    });
    return map;
  }, [orders]);

  const unassignedRaw = stores.filter(s => {
    if (s.marketplace_tier) return false;
    if (poolSearch) { const q = poolSearch.toLowerCase(); if (!(s.store_name||"").toLowerCase().includes(q)) return false; }
    if (poolFilters.has("has-upi")    && !s.upi_id)                      return false;
    if (poolFilters.has("no-upi")     &&  s.upi_id)                      return false;
    if (poolFilters.has("has-orders") && !(storeStats[s.id]?.orders > 0)) return false;
    if (poolFilters.has("no-orders")  &&   storeStats[s.id]?.orders > 0)  return false;
    return true;
  });
  const unassigned = [...unassignedRaw].sort((a, b) => {
    if (poolSort === "products") return (b.product_count||0) - (a.product_count||0);
    if (poolSort === "orders")   return (storeStats[b.id]?.orders||0) - (storeStats[a.id]?.orders||0);
    if (poolSort === "revenue")  return (storeStats[b.id]?.revenue||0) - (storeStats[a.id]?.revenue||0);
    return (a.store_name||"").localeCompare(b.store_name||"");
  });

  // Move one or many store IDs to a tier
  const moveToTier = (storeIdOrIds, tier) => {
    const ids = Array.isArray(storeIdOrIds) ? storeIdOrIds : [storeIdOrIds];
    let updated = [...stores];
    ids.forEach(id => {
      const tierItems = updated.filter(s => s.marketplace_tier === tier);
      const maxRank = tierItems.length ? Math.max(...tierItems.map(s => s.marketplace_rank||0)) + 1 : 0;
      updated = updated.map(s => s.id === id ? { ...s, marketplace_tier: tier, marketplace_rank: maxRank } : s);
    });
    onChange(updated);
    setSelectedIds(new Set());
  };

  const removeFromTier = (storeId) => {
    onChange(stores.map(s => s.id === storeId ? { ...s, marketplace_tier: null, marketplace_rank: 999 } : s));
  };

  const toggleApproved = (storeId) => {
    onChange(stores.map(s => s.id === storeId ? { ...s, approved: !s.approved } : s));
  };

  // Selection helpers
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectAll   = () => setSelectedIds(new Set(unassigned.map(s => s.id)));
  const clearSelect = () => setSelectedIds(new Set());
  const allSelected = unassigned.length > 0 && selectedIds.size === unassigned.length;

  // Drag handlers (single store drag still works)
  const handleDragStart = (e, storeId) => { onDragId(storeId); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop      = (e, targetTier) => {
    e.preventDefault();
    if (dragId) moveToTier(dragId, targetTier);
    onDragId(null);
  };

  const reorder = (storeId, tier, dir) => {
    const tierItems = [...stores.filter(s => s.marketplace_tier === tier)].sort((a,b) => (a.marketplace_rank||999) - (b.marketplace_rank||999));
    const idx = tierItems.findIndex(s => s.id === storeId);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= tierItems.length) return;
    const newItems = [...tierItems];
    [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
    const updated = stores.map(s => {
      const found = newItems.find(x => x.id === s.id);
      return found ? { ...s, marketplace_rank: newItems.indexOf(found) } : s;
    });
    onChange(updated);
  };

  if (loading) return (
    <div style={{ textAlign:"center", padding:"60px 20px" }}>
      <div style={{ width:36, height:36, border:"3px solid var(--border)", borderTopColor:"var(--teal)", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 12px" }}/>
      <p style={{ color:"var(--ink-soft)", fontSize:14 }}>Loading stores…</p>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:22, margin:"0 0 4px", fontWeight:800 }}><Icon n="bag" size="1.1em" inline /> Marketplace Manager</h2>
          <p style={{ margin:0, fontSize:13, color:"var(--ink-soft)" }}>
            Drag stores between tiers · Toggle visibility · {stores.filter(s=>s.approved).length} live on marketplace
          </p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onLoad} style={{ padding:"9px 16px", borderRadius:4, border:"1.5px solid var(--border-mid)", background:"var(--surface)", fontSize:12, fontWeight:700, cursor:"pointer", color:"var(--ink)" }}>
            ↺ Refresh
          </button>
          <button onClick={onSave} disabled={!dirty || saving}
            style={{ padding:"9px 20px", borderRadius:4, border:"none", background: dirty ? "var(--teal)" : "var(--surface)", color: dirty ? "#fff" : "var(--ink-soft)", fontSize:13, fontWeight:700, cursor: dirty ? "pointer" : "default", opacity: saving ? 0.7 : 1, transition:"all 0.18s" }}>
            {saving ? <><Icon n="pending" size="1.1em" inline />{" Auto-saving…"}</> : dirty ? <><Icon n="save" size="1.1em" inline />{" Save now"}</> : "✓ Saved"}
          </button>
        </div>
      </div>

      {/* Three tier columns */}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)", gap:16, marginBottom:28 }}>
        {TIERS.map(tier => {
          const items = tierStores(tier.key);
          return (
            <div key={tier.key}
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, tier.key)}
              style={{ background:"var(--surface)", border:`2px dashed ${dragId ? tier.color : "var(--border)"}`, borderRadius:4, padding:16, minHeight:200, transition:"border-color 0.18s" }}>
              {/* Tier header */}
              <div style={{ marginBottom:14 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:16, fontWeight:800, color: tier.color }}>{tier.label}</span>
                  <span style={{ fontSize:11, fontWeight:700, background: tier.color+"22", color: tier.color, borderRadius:4, padding:"2px 8px" }}>{items.length}</span>
                </div>
                <p style={{ margin:0, fontSize:11, color:"var(--ink-soft)" }}>{tier.desc}</p>
              </div>

              {/* Store cards in tier */}
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {items.map((s, si) => (
                  <div key={s.id} draggable
                    onDragStart={e => handleDragStart(e, s.id)}
                    style={{ display:"flex", flexDirection:"column", gap:8, padding:"10px 12px", background:"var(--card)", borderRadius:4, border:"1.5px solid var(--border)", cursor:"grab", userSelect:"none", opacity: s.approved ? 1 : 0.55 }}>
                    {/* Row 1: avatar + name + reorder/remove controls */}
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      {s.store_image
                        ? <img src={thumbUrl(s.store_image, 80)} style={{ width:32, height:32, borderRadius:"50%", objectFit:"cover", flexShrink:0 }} alt=""/>
                        : <div style={{ width:32, height:32, borderRadius:"50%", background:"var(--teal-light)", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15 }}><BloomMark /></div>}
                      <div style={{ fontWeight:700, fontSize:14, flex:1, lineHeight:1.35, wordBreak:"break-word" }}>{s.store_name}</div>
                      <div style={{ display:"flex", alignItems:"center", gap:3, flexShrink:0 }}>
                        <button onClick={() => reorder(s.id, tier.key, "up")} disabled={si===0}
                          style={{ background:"none", border:"none", cursor:si===0?"not-allowed":"pointer", fontSize:14, opacity:si===0?0.15:0.5, padding:"2px 3px", lineHeight:1 }}>↑</button>
                        <button onClick={() => reorder(s.id, tier.key, "down")} disabled={si===items.length-1}
                          style={{ background:"none", border:"none", cursor:si===items.length-1?"not-allowed":"pointer", fontSize:14, opacity:si===items.length-1?0.15:0.5, padding:"2px 3px", lineHeight:1 }}>↓</button>
                        <button onClick={() => removeFromTier(s.id)}
                          style={{ background:"none", border:"none", cursor:"pointer", fontSize:17, color:"var(--ink-faint)", padding:"2px 3px", lineHeight:1 }}>×</button>
                      </div>
                    </div>
                    {/* Row 2: status badges + ON/OFF toggle */}
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                      <span style={{ fontSize:10, color: s.approved ? "#16a34a" : "var(--ink-faint)", fontWeight:700 }}>{s.approved ? <><Icon n="dot" size="1.1em" inline />{" Live"}</> : <><Icon n="dot" size="1.1em" inline />{" Hidden"}</>}</span>
                      <span style={{ fontSize:10, fontWeight:700, background: s.upi_id?"#dcfce7":"#f3f4f6", color: s.upi_id?"#166534":"#9ca3af", borderRadius:4, padding:"2px 7px" }}>{s.upi_id?<><Icon n="card" size="1.1em" inline />{" UPI"}</>:"No UPI"}</span>
                      <span style={{ fontSize:10, fontWeight:700, background:"#eff6ff", color:"#1d4ed8", borderRadius:4, padding:"2px 7px" }}><Icon n="package" size={10} inline /> {s.product_count||0}</span>
                      <button onClick={() => toggleApproved(s.id)}
                        style={{ marginLeft:"auto", padding:"3px 10px", borderRadius:4, border:"1.5px solid "+(s.approved?"var(--teal)":"var(--border-mid)"), background:s.approved?"var(--teal-light)":"transparent", color:s.approved?"var(--teal-deep)":"var(--ink-soft)", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                        {s.approved ? "ON" : "OFF"}
                      </button>
                    </div>
                  </div>
                ))}
                {items.length === 0 && (
                  <div style={{ textAlign:"center", padding:"20px 0", color:"var(--ink-faint)", fontSize:12, fontStyle:"italic" }}>
                    Drop stores here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Unassigned stores pool */}
      <div style={{ background:"var(--surface)", border:"1.5px solid var(--border)", borderRadius:4, padding:20 }}>
        {/* Pool header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexWrap:"wrap", gap:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {/* Select all checkbox */}
            {unassigned.length > 0 && (
              <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", userSelect:"none" }}>
                <input type="checkbox" checked={allSelected} onChange={allSelected ? clearSelect : selectAll}
                  style={{ width:15, height:15, cursor:"pointer", accentColor:"var(--teal)" }}/>
                <span style={{ fontSize:12, color:"var(--ink-soft)", fontWeight:600 }}>
                  {allSelected ? "Deselect all" : "Select all"}
                </span>
              </label>
            )}
            <div>
              <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}><Icon n="package" size="1.1em" inline /> Not on Marketplace</h3>
              <p style={{ margin:0, fontSize:12, color:"var(--ink-soft)" }}>
                {selectedIds.size > 0 ? `${selectedIds.size} selected — assign to a tier below` : "Select stores then assign to a tier, or drag individually"}
              </p>
            </div>
          </div>
          <span style={{ fontSize:12, fontWeight:700, background:"var(--surface)", border:"1.5px solid var(--border-mid)", borderRadius:4, padding:"4px 12px", color:"var(--ink-soft)", flexShrink:0 }}>{unassigned.length} stores</span>
        </div>

        {/* Bulk assign bar — shown when stores are selected */}
        {selectedIds.size > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"var(--teal-light)", border:"1.5px solid var(--teal)", borderRadius:4, marginBottom:14, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:700, color:"var(--teal-deep)", flex:1 }}>
              Move {selectedIds.size} store{selectedIds.size > 1 ? "s" : ""} to:
            </span>
            {TIERS.map(tier => (
              <button key={tier.key} onClick={() => moveToTier([...selectedIds], tier.key)}
                style={{ padding:"6px 14px", borderRadius:4, border:`1.5px solid ${tier.color}`, background:tier.color, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", transition:"opacity 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.opacity="0.85"}
                onMouseLeave={e => e.currentTarget.style.opacity="1"}>
                {tier.label}
              </button>
            ))}
            <button onClick={clearSelect}
              style={{ padding:"6px 12px", borderRadius:4, border:"1.5px solid var(--border-mid)", background:"transparent", color:"var(--ink-soft)", fontSize:12, cursor:"pointer" }}>
              Cancel
            </button>
          </div>
        )}

        {/* Filter + sort bar */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:14, padding:"10px 14px", background:"var(--card)", borderRadius:4, border:"1.5px solid var(--border-mid)" }}>
          <input className="inp" placeholder="Search stores…" value={poolSearch} onChange={e => setPoolSearch(e.target.value)}
            style={{ flex:"1 1 160px", minWidth:120, fontSize:13 }}/>
          <select value={poolSort} onChange={e => setPoolSort(e.target.value)}
            style={{ border:"1.5px solid var(--border-mid)", borderRadius:4, padding:"7px 10px", fontSize:12, fontWeight:600, background:"var(--surface)", color:"var(--ink)", cursor:"pointer" }}>
            <option value="name">A – Z</option>
            <option value="products"> Most products</option>
            <option value="orders"> Most orders</option>
            <option value="revenue"> Most revenue</option>
          </select>
          {[
            { key:"has-upi",    label:"UPI ✓" },
            { key:"no-upi",     label:"No UPI" },
            { key:"has-orders", label:"Has orders" },
            { key:"no-orders",  label:"No orders" },
          ].map(f => {
            const on = poolFilters.has(f.key);
            return (
              <button key={f.key} onClick={() => togglePoolFilter(f.key)}
                style={{ background:on?"var(--teal)":"var(--surface)", color:on?"#fff":"var(--ink)", border:"1.5px solid "+(on?"var(--teal)":"var(--border-mid)"), borderRadius:4, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", transition:"all 0.15s" }}>
                {f.label}
              </button>
            );
          })}
          {(poolFilters.size > 0 || poolSearch) && (
            <button onClick={() => { setPoolFilters(new Set()); setPoolSearch(""); }}
              style={{ background:"#FEE2E2", color:"#C00", border:"none", borderRadius:4, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer" }}>✕ Clear</button>
          )}
          <span style={{ fontSize:11, color:"var(--ink-soft)", marginLeft:"auto", whiteSpace:"nowrap" }}>{unassigned.length} of {stores.filter(s=>!s.marketplace_tier).length}</span>
        </div>

        {/* Store grid */}
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(280px,1fr))", gap:10 }}>
          {unassigned.map(s => {
            const isSelected = selectedIds.has(s.id);
            return (
              <div key={s.id} draggable={!isSelected}
                onDragStart={e => !isSelected && handleDragStart(e, s.id)}
                onClick={() => toggleSelect(s.id)}
                style={{ display:"flex", flexDirection:"column", gap:8, padding:"10px 12px", background: isSelected ? "var(--teal-light)" : "var(--card)", borderRadius:4, border:`1.5px solid ${isSelected ? "var(--teal)" : "var(--border)"}`, cursor:"pointer", userSelect:"none", transition:"all 0.15s" }}>
                {/* Row 1: checkbox + avatar + store name */}
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(s.id)}
                    onClick={e => e.stopPropagation()}
                    style={{ width:15, height:15, flexShrink:0, cursor:"pointer", accentColor:"var(--teal)" }}/>
                  {s.store_image
                    ? <img src={thumbUrl(s.store_image, 80)} style={{ width:30, height:30, borderRadius:"50%", objectFit:"cover", flexShrink:0 }} alt=""/>
                    : <div style={{ width:30, height:30, borderRadius:"50%", background:"var(--teal-light)", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, border:"1.5px solid var(--border)" }}><BloomMark /></div>}
                  <div style={{ fontWeight:700, fontSize:14, flex:1, lineHeight:1.35, wordBreak:"break-word" }}>{s.store_name}</div>
                </div>
                {/* Row 2: badges + quick-assign buttons */}
                <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", paddingLeft:23 }} onClick={e => e.stopPropagation()}>
                  <span style={{ fontSize:10, fontWeight:700, background: s.upi_id?"#dcfce7":"#f3f4f6", color: s.upi_id?"#166534":"#9ca3af", borderRadius:4, padding:"2px 7px" }}>{s.upi_id?<><Icon n="card" size="1.1em" inline />{" UPI"}</>:"No UPI"}</span>
                  <span style={{ fontSize:10, fontWeight:700, background:"#eff6ff", color:"#1d4ed8", borderRadius:4, padding:"2px 7px" }}><Icon n="package" size={10} inline /> {s.product_count||0}</span>
                  {(storeStats[s.id]?.orders||0) > 0 && <span style={{ fontSize:10, fontWeight:700, background:"#fef9c3", color:"#854d0e", borderRadius:4, padding:"2px 7px" }}><Icon n="bag" size={10} inline /> {storeStats[s.id].orders}</span>}
                  {(storeStats[s.id]?.revenue||0) > 0 && <span style={{ fontSize:10, fontWeight:700, background:"#f0fdf4", color:"#166534", borderRadius:4, padding:"2px 7px" }}>₹{storeStats[s.id].revenue.toLocaleString("en-IN",{maximumFractionDigits:0})}</span>}
                  {selectedIds.size === 0 && (<>
                    <span style={{ flex:1 }}/>
                    {TIERS.map(tier => (
                      <button key={tier.key} onClick={() => moveToTier(s.id, tier.key)}
                        style={{ padding:"3px 8px", borderRadius:4, border:`1.5px solid ${tier.color}55`, background:`${tier.color}15`, color:tier.color, fontSize:10, fontWeight:700, cursor:"pointer", transition:"all 0.15s" }}
                        onMouseEnter={e => { e.currentTarget.style.background=`${tier.color}30`; }}
                        onMouseLeave={e => { e.currentTarget.style.background=`${tier.color}15`; }}>
                        {tier.key === "top" ? "TOP" : tier.key === "mid" ? "MID" : "BOT"}
                      </button>
                    ))}
                    <button onClick={() => toggleApproved(s.id)}
                      style={{ padding:"3px 8px", borderRadius:4, border:"1.5px solid "+(s.approved?"var(--teal)":"var(--border-mid)"), background:s.approved?"var(--teal-light)":"transparent", color:s.approved?"var(--teal-deep)":"var(--ink-soft)", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                      {s.approved ? "ON" : "OFF"}
                    </button>
                  </>)}
                </div>
              </div>
            );
          })}
          {unassigned.length === 0 && (
            <p style={{ color:"var(--ink-faint)", fontSize:13, margin:0 }}>All stores are assigned to a tier <Icon n="success" size="1.1em" inline /></p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── BLOOM EARNINGS (admin) ───────────────────────────────────────────────────
// Bloom's income from the tiered platform fee. Only platform_fee counts as earnings —
// the gateway tsf is a Razorpay pass-through, shown muted but never summed in.
function BloomEarnings() {
  const [data, setData]       = useState(null);   // { totals, orders }
  const [err, setErr]         = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/platform-earnings`, { headers: { "x-admin-token": ADMIN_ID } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
      } catch (e) { setErr(e.message || "Failed to load"); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{textAlign:"center",padding:"60px 0",color:"var(--ink-soft)"}}>Loading Bloom earnings…</div>;
  if (err)     return <div style={{textAlign:"center",padding:"40px 0",color:"var(--red)"}}>Couldn't load earnings: {err}</div>;

  const { totals, orders, monthly = [] } = data;
  const card = (label, val, hint, color = "var(--teal-deep)") => (
    <div style={{ flex:1, minWidth:150, background:"#fff", border:"1px solid var(--border)", borderRadius:4, padding:"18px 20px", boxShadow:"var(--shadow-sm)" }}>
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.06em", color:"var(--ink-soft)", textTransform:"uppercase" }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:800, color, marginTop:6 }}>₹{Number(val||0).toLocaleString("en-IN")}</div>
      {hint && <div style={{ fontSize:11, color:"var(--ink-soft)", marginTop:2 }}>{hint}</div>}
    </div>
  );
  const fmtDate = (o) => {
    const d = o.created_at ? new Date(o.created_at) : (o.order_date ? new Date(o.order_date) : null);
    return d ? d.toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : (o.order_date || "—");
  };

  return (
    <div>
      <h2 style={{ fontSize:22, margin:"0 0 4px", fontWeight:800 }}><BloomMark /> Bloom Earnings</h2>
      <p style={{ color:"var(--ink-soft)", fontSize:13, margin:"0 0 20px" }}>Bloom's income from the platform fee on <strong>confirmed</strong> orders. <strong>Received in bank</strong> = fees on orders Razorpay has already settled to you; the rest is awaiting settlement (run Sync Settlements to keep it current). The gateway fee is a Razorpay pass-through and is <strong>not</strong> counted here.</p>

      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:24 }}>
        {card("Total earned", totals.total, `${orders.length} order${orders.length===1?"":"s"}`)}
        {card("Received in bank", totals.received || 0, "settled to Bloom by Razorpay", "var(--green)")}
        {card("Awaiting settlement", totals.pending != null ? totals.pending : (Number(totals.total||0) - Number(totals.received||0)), "still in the Razorpay pipeline", "var(--ink-soft)")}
        {card("This month", totals.month, "IST · calendar month")}
        {card("This week", totals.week, "IST · since Monday")}
        {card("Today", totals.today, "IST")}
      </div>

      {monthly.length > 0 && (
        <div style={{ marginBottom:28 }}>
          <h3 style={{ fontSize:14, fontWeight:800, margin:"0 0 10px", color:"var(--ink)" }}>Earnings history · by month</h3>
          <div style={{ background:"#fff", border:"1px solid var(--border)", borderRadius:4, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"var(--surface)" }}>
                  {["Month","Orders","Bloom earned"].map((h,i)=>(
                    <th key={i} style={{ padding:"9px 14px", fontSize:11, fontWeight:700, color:"var(--ink-soft)", letterSpacing:"0.04em", textTransform:"uppercase", textAlign:i===0?"left":"right" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthly.map(m => (
                  <tr key={m.month} style={{ borderTop:"1px solid var(--border)" }}>
                    <td style={{ padding:"9px 14px", fontWeight:600 }}>{m.label}</td>
                    <td style={{ padding:"9px 14px", textAlign:"right", color:"var(--ink-soft)" }}>{m.count}</td>
                    <td style={{ padding:"9px 14px", textAlign:"right", fontWeight:800, color:"var(--teal-deep)" }}>₹{Number(m.total||0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:"var(--ink-soft)" }}>No platform-fee orders yet — this starts at zero and grows as orders come in after launch.</div>
      ) : (
        <div style={{ background:"#fff", border:"1px solid var(--border)", borderRadius:4, overflow:"hidden" }}>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:660 }}>
              <thead>
                <tr style={{ background:"var(--surface)" }}>
                  {["Order","Date","Store","Customer paid","Order value","Bloom fee","Gateway (pass-through)"].map((h,i)=>(
                    <th key={i} style={{ padding:"10px 14px", fontSize:11, fontWeight:700, color:"var(--ink-soft)", letterSpacing:"0.04em", textTransform:"uppercase", whiteSpace:"nowrap", textAlign: i>=3?"right":"left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} style={{ borderTop:"1px solid var(--border)" }}>
                    <td style={{ padding:"10px 14px", fontFamily:"monospace", fontSize:11.5 }}>{o.id}</td>
                    <td style={{ padding:"10px 14px", whiteSpace:"nowrap", color:"var(--ink-soft)" }}>{fmtDate(o)}</td>
                    <td style={{ padding:"10px 14px" }}>{o.store_name}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", whiteSpace:"nowrap" }}>₹{Number(o.customer_paid||0).toLocaleString("en-IN")}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", whiteSpace:"nowrap" }}>₹{Number(o.order_value||0).toLocaleString("en-IN")}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", whiteSpace:"nowrap", fontWeight:800, color:"var(--teal-deep)" }}>₹{Number(o.platform_fee||0).toLocaleString("en-IN")}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", whiteSpace:"nowrap", color:"#9ca3af" }}>₹{Number(o.gateway_tsf||0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ADMIN PRO SUBSCRIPTIONS ────────────────────────────────────────────────────
// "when the store owner buys a sub, i need to keep track on that and how much i am earning at
// what time from who and when" — bloom_users.pro_until only ever holds the current window; this
// reads the durable bloom_pro_payments ledger the webhook now writes on every real charge.
function ProSubscriptions() {
  const [data, setData]       = useState(null);
  const [err, setErr]         = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/pro/admin/subscription-payments`, { headers: { "x-admin-token": ADMIN_ID } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
      } catch (e) { setErr(e.message || "Failed to load"); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{textAlign:"center",padding:"60px 0",color:"var(--ink-soft)"}}>Loading subscription revenue…</div>;
  if (err)     return <div style={{textAlign:"center",padding:"40px 0",color:"var(--red)"}}>Couldn't load subscriptions: {err}</div>;

  const { totals, active, payments, monthly } = data;
  const card = (label, val, hint, color = "var(--teal-deep)") => (
    <div style={{ flex:1, minWidth:150, background:"#fff", border:"1px solid var(--border)", borderRadius:4, padding:"18px 20px", boxShadow:"var(--shadow-sm)" }}>
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.06em", color:"var(--ink-soft)", textTransform:"uppercase" }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:800, color, marginTop:6 }}>₹{Number(val||0).toLocaleString("en-IN")}</div>
      {hint && <div style={{ fontSize:11, color:"var(--ink-soft)", marginTop:2 }}>{hint}</div>}
    </div>
  );
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

  return (
    <div>
      <h2 style={{ fontSize:22, margin:"0 0 4px", fontWeight:800 }}><Icon n="gemstone" size="1.1em" inline /> Bloom PRO Subscriptions</h2>
      <p style={{ color:"var(--ink-soft)", fontSize:13, margin:"0 0 20px" }}>Every logged Design Studio PRO charge (₹599/mo), who's currently active, and MRR. This starts empty until real subscriptions go live — stores marked PRO manually before then won't show here.</p>

      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:24 }}>
        {card("Total collected", totals.total, `${payments.length} payment${payments.length===1?"":"s"}`)}
        {card("This month", totals.month, "IST · calendar month")}
        {card("Today", totals.today, "IST")}
        {card("MRR", totals.mrr, `${totals.activeCount} active subscriber${totals.activeCount===1?"":"s"}`, "var(--green)")}
      </div>

      <div style={{ marginBottom:28 }}>
        <h3 style={{ fontSize:14, fontWeight:800, margin:"0 0 10px", color:"var(--ink)" }}>Active right now</h3>
        {active.length === 0 ? (
          <div style={{ textAlign:"center", padding:"24px 0", color:"var(--ink-soft)", background:"#fff", border:"1px solid var(--border)", borderRadius:4 }}>No store currently has an active PRO subscription.</div>
        ) : (
          <div style={{ background:"#fff", border:"1px solid var(--border)", borderRadius:4, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"var(--surface)" }}>
                  {["Store","PRO until","Subscription ID"].map((h,i)=>(
                    <th key={i} style={{ padding:"9px 14px", fontSize:11, fontWeight:700, color:"var(--ink-soft)", letterSpacing:"0.04em", textTransform:"uppercase", textAlign:"left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.map(a => (
                  <tr key={a.id} style={{ borderTop:"1px solid var(--border)" }}>
                    <td style={{ padding:"9px 14px", fontWeight:600 }}>{a.store_name || a.id}</td>
                    <td style={{ padding:"9px 14px", color:"var(--green)" }}>{fmtDate(a.pro_until)}</td>
                    <td style={{ padding:"9px 14px", fontFamily:"monospace", fontSize:11.5, color:"var(--ink-soft)" }}>{a.subscription_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {monthly.length > 0 && (
        <div style={{ marginBottom:28 }}>
          <h3 style={{ fontSize:14, fontWeight:800, margin:"0 0 10px", color:"var(--ink)" }}>Revenue history · by month</h3>
          <div style={{ background:"#fff", border:"1px solid var(--border)", borderRadius:4, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"var(--surface)" }}>
                  {["Month","Payments","Collected"].map((h,i)=>(
                    <th key={i} style={{ padding:"9px 14px", fontSize:11, fontWeight:700, color:"var(--ink-soft)", letterSpacing:"0.04em", textTransform:"uppercase", textAlign:i===0?"left":"right" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthly.map(m => (
                  <tr key={m.month} style={{ borderTop:"1px solid var(--border)" }}>
                    <td style={{ padding:"9px 14px", fontWeight:600 }}>{m.label}</td>
                    <td style={{ padding:"9px 14px", textAlign:"right", color:"var(--ink-soft)" }}>{m.count}</td>
                    <td style={{ padding:"9px 14px", textAlign:"right", fontWeight:800, color:"var(--teal-deep)" }}>₹{Number(m.total||0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h3 style={{ fontSize:14, fontWeight:800, margin:"0 0 10px", color:"var(--ink)" }}>Every payment</h3>
      {payments.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:"var(--ink-soft)" }}>No PRO payments logged yet — this starts at zero and grows once subscriptions go live.</div>
      ) : (
        <div style={{ background:"#fff", border:"1px solid var(--border)", borderRadius:4, overflow:"hidden" }}>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:600 }}>
              <thead>
                <tr style={{ background:"var(--surface)" }}>
                  {["Store","Charged at","Amount","Razorpay payment ID"].map((h,i)=>(
                    <th key={i} style={{ padding:"10px 14px", fontSize:11, fontWeight:700, color:"var(--ink-soft)", letterSpacing:"0.04em", textTransform:"uppercase", whiteSpace:"nowrap", textAlign: i===2?"right":"left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} style={{ borderTop:"1px solid var(--border)" }}>
                    <td style={{ padding:"10px 14px" }}>{p.store_name}</td>
                    <td style={{ padding:"10px 14px", whiteSpace:"nowrap", color:"var(--ink-soft)" }}>{fmtDate(p.charged_at)}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", whiteSpace:"nowrap", fontWeight:800, color:"var(--teal-deep)" }}>₹{Number(p.amount||0).toLocaleString("en-IN")}</td>
                    <td style={{ padding:"10px 14px", fontFamily:"monospace", fontSize:11.5, color:"var(--ink-soft)" }}>{p.razorpay_payment_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ADMIN STOCK MONITOR ──────────────────────────────────────────────────────
// Read-only audit of every stock movement, so we can answer "did this change happen
// on a real purchase, or without one?" Reads the bloom_stock_log ledger.
function StockMonitor({ stores }) {
  const [rows, setRows]       = useState(null);   // null = loading
  const [storeId, setStoreId] = useState("");
  const [source, setSource]   = useState("");     // "" = all
  const [error, setError]     = useState("");

  const storeName = (id) => stores?.find(s => s.id === id)?.store_name || id || "—";

  const load = async () => {
    setRows(null); setError("");
    try {
      let url = `${BACKEND_URL}/api/admin/stock-log?limit=500`;
      if (storeId) url += `&storeId=${encodeURIComponent(storeId)}`;
      const r = await fetch(url, { headers: { "x-admin-token": ADMIN_ID } });
      if (!r.ok) { setError(`HTTP ${r.status}`); setRows([]); return; }
      setRows(await r.json());
    } catch(e) { setError(e.message); setRows([]); }
  };
  useEffect(() => { load(); }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Colour + label per source
  const META = {
    "checkout-reserve": { c:"#B42318", l:"Checkout reserve" },
    "webhook-decrement":{ c:"#B42318", l:"Webhook decrement" },
    "webhook-reclaim":  { c:"#b45309", l:"Reclaim (late pay)" },
    "release-api":      { c:"#067647", l:"Release (cancel/beacon)" },
    "release-cron":     { c:"#067647", l:"Release (cron)" },
    "owner-edit":       { c:"#2563eb", l:"Owner edit" },
    "admin-edit":       { c:"#7c3aed", l:"Admin edit" },
  };
  const filtered = (rows || []).filter(r => !source || r.source === source);
  const counts = (rows || []).reduce((m, r) => { m[r.source] = (m[r.source]||0)+1; return m; }, {});

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:16}}>
        <div>
          <h2 style={{fontSize:22,margin:"0 0 4px",fontWeight:800}}><Icon n="analytics" size="1.1em" inline /> Stock Monitor</h2>
          <p style={{fontSize:13,color:"var(--ink-soft)",margin:0}}>Every stock movement, logged. Reserve/decrement = a checkout; release = an abandoned/cancelled checkout; edit = a store owner or admin change.</p>
        </div>
        <button onClick={load} style={{background:"var(--pink-btn)",color:"#fff",border:"none",borderRadius:4,padding:"9px 16px",fontWeight:700,fontSize:13,cursor:"pointer"}}>↻ Refresh</button>
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
        <select value={storeId} onChange={e=>setStoreId(e.target.value)} style={{padding:"9px 12px",borderRadius:4,border:"1px solid var(--border)",fontSize:13,background:"#fff"}}>
          <option value="">All stores</option>
          {(stores||[]).slice().sort((a,b)=>(a.store_name||"").localeCompare(b.store_name||"")).map(s=>(
            <option key={s.id} value={s.id}>{s.store_name||s.id}</option>
          ))}
        </select>
        <select value={source} onChange={e=>setSource(e.target.value)} style={{padding:"9px 12px",borderRadius:4,border:"1px solid var(--border)",fontSize:13,background:"#fff"}}>
          <option value="">All change types</option>
          {Object.keys(META).map(k=>(<option key={k} value={k}>{META[k].l}{counts[k]?` (${counts[k]})`:""}</option>))}
        </select>
      </div>

      {/* Summary chips */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
        {Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([k,n])=>(
          <span key={k} style={{fontSize:12,fontWeight:700,padding:"4px 10px",borderRadius:4,background:`${(META[k]?.c||"#888")}15`,color:META[k]?.c||"#888"}}>{META[k]?.l||k}: {n}</span>
        ))}
      </div>

      {error && <div style={{background:"#fef2f2",color:"#B42318",padding:"10px 14px",borderRadius:4,fontSize:13,marginBottom:12}}>Couldn't load ledger: {error}. (Has the `bloom_stock_log` table been created?)</div>}
      {rows === null ? (
        <div style={{textAlign:"center",padding:"50px 0",color:"var(--ink-soft)"}}>Loading ledger…</div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"50px 0",color:"var(--ink-soft)"}}>No stock movements logged yet{storeId?" for this store":""}.</div>
      ) : (
        <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:4}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:"var(--surface)",textAlign:"left"}}>
                {["When","Store","Product","Change type","Δ","Order","Note"].map(h=>(
                  <th key={h} style={{padding:"10px 12px",fontWeight:700,color:"var(--ink-soft)",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r=>(
                <tr key={r.id} style={{borderTop:"1px solid var(--border)"}}>
                  <td style={{padding:"9px 12px",whiteSpace:"nowrap",color:"var(--ink-soft)"}}>{r.created_at?new Date(r.created_at).toLocaleString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}):"—"}</td>
                  <td style={{padding:"9px 12px",whiteSpace:"nowrap"}}>{storeName(r.store_id)}</td>
                  <td style={{padding:"9px 12px",maxWidth:240}}>
                    <div style={{fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.product_name || "(deleted product)"}{r.variation_name?` · ${r.variation_name}`:""}</div>
                    <div style={{fontFamily:"monospace",fontSize:10,color:"var(--ink-soft)"}}>{r.product_id}</div>
                  </td>
                  <td style={{padding:"9px 12px",whiteSpace:"nowrap",fontWeight:700,color:META[r.source]?.c||"#555"}}>{META[r.source]?.l||r.source}</td>
                  <td style={{padding:"9px 12px",fontWeight:800,color:r.delta<0?"#B42318":r.delta>0?"#067647":"#555"}}>{r.delta>0?`+${r.delta}`:r.delta}</td>
                  <td style={{padding:"9px 12px",fontFamily:"monospace",fontSize:11,color:"var(--ink-soft)"}}>{r.order_id||"—"}</td>
                  <td style={{padding:"9px 12px",color:"var(--ink-soft)"}}>{r.note||""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── ADMIN STORE VIEW ─────────────────────────────────────────────────────────
function AdminStoreView() {
  const { storeId } = useParams();
  const [storeUser,   setStoreUser]   = useState(null);
  const [storeOrders, setStoreOrders] = useState([]);
  const [storeProds,  setStoreProds]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [activeTab,   setActiveTab]   = useState("dashboard");

  const authed = localStorage.getItem("bloom_admin_authed") === "1";

  useEffect(() => {
    if (!authed || !storeId) return;
    (async () => {
      try {
        const [userRow, prods, orders] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/store/${storeId}`, { headers: { "x-admin-token": ADMIN_ID } }).then(r=>r.ok?r.json():null).catch(()=>null),
          supa.selectAll("bloom_products", `user_id=eq.${storeId}&order=created_at.asc`),
          fetch(`${BACKEND_URL}/api/orders/store/${storeId}`, { headers: { "x-admin-token": ADMIN_ID } }).then(r=>r.json()).catch(()=>[]),
        ]);
        if (!userRow) { setError("Store not found"); setLoading(false); return; }
        setStoreUser(userRow);
        setStoreProds(prods);
        setStoreOrders((Array.isArray(orders)?orders:orders?.orders||[]).map(o=>({...o,date:o.order_date})));
      } catch(e) { setError(String(e)); }
      setLoading(false);
    })();
  }, [storeId, authed]);

  if (!authed) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--offwhite)"}}>
      <div style={{textAlign:"center",padding:32}}>
        <div style={{fontSize:48,marginBottom:16}}><Icon n="locked" size={48} inline /></div>
        <div style={{fontWeight:700,fontSize:18,marginBottom:8}}>Admin access required</div>
        <div style={{color:"var(--ink-soft)",marginBottom:24}}>Log in to the admin panel first, then use the <Icon n="show" size="1.1em" inline /> button on a store.</div>
        <a href="/admin" style={{background:"var(--teal)",color:"#fff",padding:"10px 24px",borderRadius:4,textDecoration:"none",fontWeight:700}}>Go to Admin →</a>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--offwhite)"}}>
      <div style={{width:40,height:40,border:"3px solid var(--border)",borderTopColor:"var(--teal)",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
    </div>
  );

  if (error || !storeUser) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--offwhite)"}}>
      <div style={{textAlign:"center",padding:32}}>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Could not load store</div>
        <div style={{color:"var(--ink-soft)",marginBottom:20}}>{error||"Store not found"}</div>
        <a href="/admin" style={{color:"var(--teal)",fontWeight:700}}>← Back to admin</a>
      </div>
    </div>
  );

  const TABS = [
    { id:"dashboard", label:"Dashboard" },
    { id:"orders",    label:"Orders"    },
    { id:"products",  label:"Products"  },
    { id:"reviews",   label:"Reviews & Q&A" },
    { id:"info",      label:"Store Info" },
  ];

  const tabBtn = (t) => ({
    background: activeTab===t.id ? "rgba(255,255,255,0.18)" : "transparent",
    color: activeTab===t.id ? "#fff" : "rgba(255,255,255,0.6)",
    border: "none", borderRadius:4, padding:"5px 12px",
    fontSize:12, fontWeight:700, cursor:"pointer",
    transition:"background 0.15s",
  });

  return (
    <div>
      {/* ── Admin banner with tab switcher ── */}
      <div style={{background:"#1e1b4b",color:"#fff",padding:"8px 20px",display:"flex",alignItems:"center",gap:12,fontSize:13,position:"sticky",top:0,zIndex:9999,flexWrap:"wrap"}}>
        <span style={{fontSize:15}}><Icon n="show" size={15} inline /></span>
        <span style={{fontWeight:700}}>{storeUser.store_name}</span>
        <span style={{color:"rgba(255,255,255,0.35)"}}>·</span>
        <span style={{color:"rgba(255,255,255,0.55)",fontSize:11}}>{storeUser.email}</span>
        <span style={{color:"rgba(255,255,255,0.35)"}}>·</span>
        <span style={{color:"rgba(255,255,255,0.55)",fontSize:11}}>UPI: {storeUser.upi_id||"—"}</span>
        <span style={{color:"rgba(255,255,255,0.35)"}}>·</span>
        {/* Tab buttons inline in the banner */}
        <div style={{display:"flex",gap:4}}>
          {TABS.map(t=>(
            <button key={t.id} style={tabBtn(t)} onClick={()=>setActiveTab(t.id)}>{t.label}</button>
          ))}
        </div>
        <div style={{flex:1}}/>
        <a href="/admin" style={{color:"#a5b4fc",fontWeight:700,textDecoration:"none",fontSize:12}}>← Back to Admin</a>
      </div>

      {/* ── Tab content — all using this store's data, not the session user ── */}
      {activeTab==="dashboard" && <Dashboard user={storeUser} products={storeProds} orders={storeOrders} live={false}/>}
      {activeTab==="orders"    && <Orders    orders={storeOrders} storeUser={storeUser}/>}
      {activeTab==="products"  && <Products  userId={storeUser.id} products={storeProds} setProducts={setStoreProds} user={storeUser} onUserUpdated={()=>{}}/>}
      {activeTab==="reviews"   && <StoreReviewsQNA storeId={storeUser.id} session={null} user={storeUser}/>}
      {activeTab==="info"      && (
        <div style={{maxWidth:560,margin:"40px auto",padding:"0 20px"}}>
          <h2 style={{fontSize:20,fontWeight:700,marginBottom:20}}>Store Info — live from DB</h2>
          {[
            ["Store Name",     storeUser.store_name],
            ["Store Slug",     storeUser.store_slug],
            ["Email",          storeUser.email],
            ["UPI ID",         storeUser.upi_id],
            ["Phone",          storeUser.phone],
            ["Instagram",      storeUser.instagram],
            ["Shipping Cost",  storeUser.shipping_cost != null ? `₹${storeUser.shipping_cost}` : null],
            ["Dispatch Days",  storeUser.dispatch_days],
            ["MailerSend Key", storeUser.mailersend_key ? "set" : null],
            ["Store ID",       storeUser.id],
            ["Joined",         storeUser.created_at ? new Date(storeUser.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : null],
          ].map(([label, val]) => (
            <div key={label} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:"1px solid var(--border)",alignItems:"flex-start"}}>
              <span style={{width:140,flexShrink:0,fontSize:12,fontWeight:700,color:"var(--ink-soft)",paddingTop:1}}>{label}</span>
              <span style={{fontSize:13,fontFamily:["UPI ID","Store ID"].includes(label)?"monospace":"inherit",color:val?"var(--ink)":"var(--ink-soft)",fontStyle:val?"normal":"italic"}}>
                {val || "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ADMIN PAGE ───────────────────────────────────────────────────────────────
// The admin API credential is now a short-lived SIGNED session token from
// /api/ops/session (password verified server-side) — no secret ships in this bundle.
// Loaded from storage on refresh; (re)set by adminLogin() below.
let ADMIN_ID = (typeof localStorage !== "undefined" && localStorage.getItem("bloom_admin_tok")) || "";

function AdminPage({ onProductDeleted }) {
  const isMobile  = useIsMobile();
  const [authed,        setAuthed]        = useState(false);
  const [adminForm,     setAdminForm]     = useState({id:"",pass:""});
  const [adminErr,      setAdminErr]      = useState("");
  const [adminLoading,  setAdminLoading]  = useState(false);
  const [tab,           setTab]           = useState("payouts");
  const [stores,        setStores]        = useState([]);
  const [customers,     setCustomers]     = useState([]);
  const [orders,        setOrders]        = useState([]);
  const [payouts,       setPayouts]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [storesRefreshing, setStoresRefreshing] = useState(false);
  const [adminRefreshing,  setAdminRefreshing]  = useState(false);
  const [confirmDlg,     setConfirmDlg]     = useState(null);
  const [expandedStore,  setExpandedStore]  = useState(null);
  const [refundingId,    setRefundingId]    = useState(null); // order currently being refunded
  const [selPayouts,     setSelPayouts]     = useState(new Set()); // selected payout IDs for bulk delete
  const [mainMode,       setMainMode]       = useState(false);
  const [mainMsg,        setMainMsg]        = useState("");
  const [mainToggling,   setMainToggling]   = useState(false);
  const [paysPaused,     setPaysPaused]     = useState(false);
  const [paysToggling,   setPaysToggling]   = useState(false);
  // Server-authoritative fee config (edited here, applied to the next checkout with no deploy).
  const [feeCfg,         setFeeCfg]         = useState(null);   // {gateway_factor, platform_fee_enabled, platform_fee_tiers}
  const [feePctDraft,    setFeePctDraft]    = useState("");     // editable total gateway fee % (e.g. "2.54")
  const [feeSaving,      setFeeSaving]      = useState(false);
  const [classifyBusy,   setClassifyBusy]   = useState(false);
  const [classifyMsg,    setClassifyMsg]    = useState("");
  const [orderFixBusy,   setOrderFixBusy]   = useState(false);
  const [orderFixMsg,    setOrderFixMsg]    = useState("");
  const [waBusy,         setWaBusy]         = useState(false);
  const [waMsg,          setWaMsg]          = useState("");
  const [healRunning,    setHealRunning]    = useState(false);
  const [healResult,     setHealResult]     = useState(null);
  const [payoutSearch,        setPayoutSearch]        = useState("");
  const [storeSearch,         setStoreSearch]         = useState("");
  const [customerSearch,      setCustomerSearch]      = useState("");
  const [storeFilters,        setStoreFilters]        = useState(new Set());
  const [customerFilters,     setCustomerFilters]     = useState(new Set());
  const [orderSearch,         setOrderSearch]         = useState("");
  // Render caps — only mount the first N rows so the Orders/Customers tabs paint
  // instantly even with thousands of records; "Show more" reveals the next batch.
  const [orderLimit,          setOrderLimit]          = useState(60);
  const [custLimit,           setCustLimit]           = useState(60);
  const [storeDetailId,       setStoreDetailId]       = useState(null);
  const [storeDetailSearch,   setStoreDetailSearch]   = useState("");
  const [storeProductsId,     setStoreProductsId]     = useState(null);
  const [storeProductsList,   setStoreProductsList]   = useState({});
  const [selectedPayouts,     setSelectedPayouts]     = useState(new Set());
  const [historyData,         setHistoryData]         = useState(null);
  const [historyExpanded,     setHistoryExpanded]     = useState(null);
  const [historyUpdating,     setHistoryUpdating]     = useState(null);
  const [syncingSettlements,  setSyncingSettlements]  = useState(false);
  const [todayData,           setTodayData]           = useState({ expected_today: [], estimate_today: [], settled_today: [], rzp_today_amount: null, rzp_today_status: null });
  const [txSettlements,       setTxSettlements]       = useState(null);
  const [txExpanded,          setTxExpanded]          = useState(null);
  const [txDetail,            setTxDetail]            = useState({});
  const [txLoadingDetail,     setTxLoadingDetail]     = useState(null);
  const [storeSort,           setStoreSort]           = useState("newest");
  const [customerSort,        setCustomerSort]        = useState("newest");
  const [orderSort,           setOrderSort]           = useState("newest");
  const [newOrderIds,         setNewOrderIds]         = useState(new Set());
  const [txPayDetail,         setTxPayDetail]         = useState({});
  const [txLoadingPay,        setTxLoadingPay]        = useState(null);
  const [txExpandedPay,       setTxExpandedPay]       = useState(null);
  const [mktStores,           setMktStores]           = useState([]);
  const [mktLoading,          setMktLoading]          = useState(false);
  const [mktSaving,           setMktSaving]           = useState(false);
  const [mktDirty,            setMktDirty]            = useState(false);
  const [mktDragId,           setMktDragId]           = useState(null);
  const mktAutoSaveTimer = useRef(null);

  const saveMktStores = async (storesToSave) => {
    setMktSaving(true);
    try {
      const updates = storesToSave.map(s => ({
        id: s.id,
        approved: s.approved === true,
        marketplace_tier: s.marketplace_tier || null,
        marketplace_rank: s.marketplace_rank ?? 999,
      }));
      const r = await fetch(`${BACKEND_URL}/api/admin/marketplace-stores/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-token": ADMIN_ID },
        body: JSON.stringify({ updates }),
      });
      if (r.ok) setMktDirty(false);
    } catch(e) {}
    setMktSaving(false);
  };

  const loadMktStores = async () => {
    setMktLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/marketplace-stores`, { headers: { "x-admin-token": ADMIN_ID } });
      if (r.ok) setMktStores(await r.json());
    } catch(e) {}
    setMktLoading(false);
  };

  // Auto-save 1.5 s after any change — so reloading always shows the latest state
  const onMktChange = (newStores) => {
    setMktStores(newStores);
    setMktDirty(true);
    if (mktAutoSaveTimer.current) clearTimeout(mktAutoSaveTimer.current);
    mktAutoSaveTimer.current = setTimeout(() => saveMktStores(newStores), 1500);
  };

  // Load data once admin logs in
  const reloadAdminData = async () => {
    setAdminRefreshing(true);
    const [allStores, allCustomers, allOrders, allPayouts] = await Promise.all([
      fetch(`${BACKEND_URL}/api/admin/users-all`, { headers: { "x-admin-token": ADMIN_ID } }).then(r=>r.ok?r.json():[]).catch(()=>[]),
      fetch(`${BACKEND_URL}/api/admin/customers`, { headers: { "Content-Type":"application/json", "x-admin-token": ADMIN_ID } }).then(r=>r.ok?r.json():[]).catch(()=>[]),
      fetch(`${BACKEND_URL}/api/admin/orders-all`, { headers: { "x-admin-token": ADMIN_ID } }).then(r=>r.ok?r.json():[]).catch(()=>[]),
      api.getPayouts(),
    ]);
    setStores(allStores.filter(u => u.id !== DEMO_ID));
    setCustomers(allCustomers);
    setOrders(allOrders);
    setPayouts(allPayouts || []);
    setLoading(false);
    setAdminRefreshing(false);
  };

  useEffect(() => {
    if (!authed) return;
    reloadAdminData();
    // Also fetch current maintenance status
    fetch(`${BACKEND_URL}/api/settings`)
      .then(r=>r.json())
      .then(d=>{ setMainMode(!!d.maintenance_mode); setMainMsg(d.maintenance_message||""); setPaysPaused(!!d.payments_paused);
        const gf = Number(d.gateway_factor) || 0.9746;
        setFeeCfg({ gateway_factor: gf, platform_fee_enabled: d.platform_fee_enabled !== false, platform_fee_tiers: Array.isArray(d.platform_fee_tiers) ? d.platform_fee_tiers : [] });
        setFeePctDraft(((1 - gf) * 100).toFixed(2));
      })
      .catch(()=>{});
    // Fetch today's settlement data
    fetch(`${BACKEND_URL}/api/admin/today-settlements`, { headers: { "x-admin-token": ADMIN_ID } })
      .then(r=>r.json())
      .then(d=>setTodayData(d || { expected_today: [], settled_today: [] }))
      .catch(()=>{});
  }, [authed]);

  // ── Keep-alive ping every 4 min so Render never sleeps + scheduled cron keeps firing ──
  useEffect(() => {
    const id = setInterval(() => {
      fetch(`${BACKEND_URL}/ping`).catch(()=>{});
    }, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // ── Persist checkbox selections to localStorage keyed by settlement ID ──────
  const todaySettlementId = todayData.rzp_settlements?.[0]?.id || null;
  useEffect(() => {
    if (!todaySettlementId) return;
    const saved = localStorage.getItem(`bloom_sel_${todaySettlementId}`);
    if (saved) { try { setSelectedPayouts(new Set(JSON.parse(saved))); } catch {} }
  }, [todaySettlementId]);
  useEffect(() => {
    if (!todaySettlementId) return;
    localStorage.setItem(`bloom_sel_${todaySettlementId}`, JSON.stringify([...selectedPayouts]));
  }, [selectedPayouts, todaySettlementId]);

  // ── Auto-save settlement snapshot when real Razorpay data loads ──────────────
  useEffect(() => {
    if (!authed || !todaySettlementId || !todayData.rzp_today_amount || stores.length === 0) return;
    const exp = todayData.expected_today || [];
    if (exp.length === 0) return;
    const totalGross = exp.reduce((s,o) => s + Number(o.amount||0), 0);
    const net = todayData.rzp_today_amount;
    const rawFactor = totalGross > 0 ? net / totalGross : 1.0;
    const factor = (rawFactor > 1 || rawFactor <= 0) ? 1.0 : rawFactor; // cap: >1 means incomplete sync
    const byStore = {};
    for (const o of exp) {
      const info = stores.find(s => s.id === o.store_id);
      if (!byStore[o.store_id]) byStore[o.store_id] = {
        store_id: o.store_id, store_name: info?.store_name || o.store_id,
        upi_id: info?.upi_id || null, phone: info?.phone || null,
        orders: 0, gross: 0, postTsfGross: 0, payout: 0, platformFee: 0, paid: false, paid_at: null
      };
      byStore[o.store_id].orders++;
      byStore[o.store_id].gross += Number(o.amount||0);
      byStore[o.store_id].platformFee += Number(o.platform_fee||0);
      // Customer paid the gateway (not absorbed) → store gets the full amount, no factor deduction.
      if (!gwAbsorbed(o)) byStore[o.store_id].postTsfGross += Number(o.amount||0);
    }
    // Post-TSF (customer-pays): store gets the full amount — the buyer already paid the
    // gateway fee on top. Pre-TSF (absorb): store nets the FIXED 2.54% gateway fee (×0.9746),
    // exactly what the seller agreed to — never the variable `factor` (it caps to 1.0 on an
    // incomplete sync, which would silently overpay absorb sellers). Matches net()/_bulkNet().
    for (const sg of Object.values(byStore)) {
      const preTsfGross = sg.gross - sg.postTsfGross;
      sg.payout = +(sg.postTsfGross + Math.round(preTsfGross * 0.9746)).toFixed(2);
    }
    const settledOn = todayData.rzp_settlements[0].settled_on
      ? new Date(todayData.rzp_settlements[0].settled_on * 1000).toISOString().slice(0,10)
      : new Date().toISOString().slice(0,10);
    fetch(`${BACKEND_URL}/api/admin/settlement-history/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-token": ADMIN_ID },
      body: JSON.stringify({
        settlement_id: todaySettlementId,
        settled_on: settledOn,
        gross_amount: totalGross,
        net_amount: net,
        factor,
        stores: Object.values(byStore)
      })
    }).catch(() => {});
  }, [todaySettlementId, todayData.rzp_today_amount, todayData.expected_today.length, stores.length]);

  // ── Real-time: poll for new orders every 30s when on orders tab ─────────────
  const adminKnownOrderIds = useRef(null);
  useEffect(() => {
    if (!authed) return;
    const poll = async () => {
      try {
        const fresh = await fetch(`${BACKEND_URL}/api/admin/orders-all`, { headers: { "x-admin-token": ADMIN_ID } }).then(r=>r.ok?r.json():null).catch(()=>null);
        if (!Array.isArray(fresh)) return;
        if (adminKnownOrderIds.current === null) {
          adminKnownOrderIds.current = new Set(fresh.map(o => o.id));
          setOrders(fresh);
          return;
        }
        const incoming = fresh.filter(o => !adminKnownOrderIds.current.has(o.id));
        if (incoming.length > 0) {
          incoming.forEach(o => adminKnownOrderIds.current.add(o.id));
          setNewOrderIds(prev => { const s = new Set(prev); incoming.forEach(o => s.add(o.id)); return s; });
          setOrders(fresh);
        }
      } catch {}
    };
    poll();
    const stopPoll = setVisibleInterval(poll, 30000);
    return () => stopPoll();
  }, [authed]);

  // Clear new-order highlights after 5 min
  useEffect(() => {
    if (newOrderIds.size === 0) return;
    const t = setTimeout(() => setNewOrderIds(new Set()), 5 * 60 * 1000);
    return () => clearTimeout(t);
  }, [newOrderIds.size]);

  const adminLogin = async () => {
    setAdminLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ops/session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: adminForm.pass }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.token) {
        ADMIN_ID = d.token;                                   // signed admin session token
        localStorage.setItem("bloom_admin_tok", d.token);
        localStorage.setItem("bloom_admin_authed", "1");
        setAuthed(true); setAdminErr("");
      } else {
        setAdminErr(r.status === 429 ? "Too many attempts. Wait a few minutes." : "Wrong password. Try again.");
      }
    } catch {
      setAdminErr("Couldn't reach the server. Check your connection.");
    }
    setAdminLoading(false);
  };

  if (!authed) {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--offwhite)",padding:24}}>
        {adminLoading&&(
          <div style={{position:"fixed",inset:0,background:"rgba(57,57,63,0.6)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:9999,gap:16}}>
            <div style={{width:48,height:48,border:"3px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
            <p style={{color:"#fff",fontFamily:"Syne",fontWeight:600,fontSize:16}}>Verifying credentials…</p>
          </div>
        )}
        <div className="card pop-in" style={{width:"100%",maxWidth:380,padding:40,textAlign:"center"}}>
          <div style={{fontSize:52,marginBottom:8}}><Icon n="locked" size={52} inline /></div>
          <h2 style={{fontSize:26,marginBottom:4}}>Admin Access</h2>
          <p style={{color:"var(--ink-soft)",fontSize:13,marginBottom:28}}>Bloom internal dashboard</p>
          <div style={{display:"flex",flexDirection:"column",gap:12,textAlign:"left"}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",display:"block",marginBottom:5,color:"var(--ink-soft)"}}>ADMIN ID</label>
              <input className="inp" placeholder="Enter admin ID" value={adminForm.id}
                onChange={e=>setAdminForm(f=>({...f,id:e.target.value}))}
                onKeyDown={e=>e.key==="Enter"&&adminLogin()}
                autoComplete="off"/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",display:"block",marginBottom:5,color:"var(--ink-soft)"}}>PASSWORD</label>
              <input className="inp" type="password" placeholder="Enter password" value={adminForm.pass}
                onChange={e=>setAdminForm(f=>({...f,pass:e.target.value}))}
                onKeyDown={e=>e.key==="Enter"&&adminLogin()}
                autoComplete="off"/>
            </div>
            {adminErr&&<p style={{fontSize:12,color:"var(--red)",fontWeight:600}}>{adminErr}</p>}
            <button className="btn-pink" style={{padding:14,borderRadius:4,marginTop:4,fontSize:14}} onClick={adminLogin} disabled={adminLoading}>
              {adminLoading?"Verifying…":"Access Dashboard →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // net(o):   what the store receives (product price). post-TSF=amount, pre-TSF=amount*0.9746
  // gross(o): what the customer paid. post-TSF=amount+tsf,  pre-TSF=amount (was already gross)
  const net   = (o) => sellerPayout(o); // gw_absorbed → amount×0.9746, else amount (fallback: tsf===0)
  const gross = (o) => Number(o.amount||0) + Number(o.tsf||0);
  // What the customer actually PAID for this order row: amount + gateway fee + platform fee (the row invariant). `gross` leaves the platform fee out,
  // so a payment's cards (and a multi-store card's total) came up short of the real Razorpay amount. Used for DISPLAY only.
  const paidTotal = (o) => gross(o) + Math.max(0, Number(o.platform_fee||0));
  // ── Indexed lookups, rebuilt only when the underlying data changes ───────────
  // The per-store helpers below used to each scan ALL orders, and they're called
  // for EVERY store (twice in the Payouts tab, and inside a sort comparator in the
  // Stores tab) on every render and every keystroke — i.e. O(stores × orders) each
  // time. That was the admin sluggishness. These maps make each helper O(orders for
  // this store), and the maps themselves rebuild only when orders/payouts/stores change.
  // NOTE: these are plain computed IIFEs, NOT useMemo — they sit BELOW the component's
  // `if (!authed) return` gate, so as hooks they'd be conditional and crash with React
  // #310 ("rendered more hooks…") on the login→authed transition. Computing them each
  // render is O(orders+customers) — still eliminates the old O(stores×orders) per-helper
  // scans, just without the hook. Do NOT convert these back to useMemo here.
  const ordersByStore = (() => {
    const m = new Map();
    for (const o of orders) { const a = m.get(o.store_id); if (a) a.push(o); else m.set(o.store_id, [o]); }
    return m;
  })();
  const payoutsByStore = (() => {
    const m = new Map();
    for (const p of payouts) { const a = m.get(p.store_id); if (a) a.push(p); else m.set(p.store_id, [p]); }
    return m;
  })();
  const storeById = (() => {
    const m = new Map();
    for (const s of stores) m.set(s.id, s);
    return m;
  })();
  const customerById = (() => {
    const m = new Map();
    for (const c of customers) m.set(c.id, c);
    return m;
  })();
  const ordersByCustomer = (() => {
    const m = new Map();
    for (const o of orders) { const a = m.get(o.customer_id); if (a) a.push(o); else m.set(o.customer_id, [o]); }
    return m;
  })();
  // Per-customer spend/count — the Customers tab sort used to run orders.filter()
  // four times PER comparison (O(customers·log·orders)); now it's O(1) lookups.
  const customerSpend = (() => {
    const m = new Map();
    for (const [cid, arr] of ordersByCustomer) {
      let spent = 0; for (const o of arr) spent += Number(o.amount||0);
      m.set(cid, { spent, count: arr.length });
    }
    return m;
  })();
  const _ordersOf = (sid) => ordersByStore.get(sid) || [];

  const storeOrders  = (sid) => _ordersOf(sid).filter(o => o.status === "confirmed");
  // Apply 0.9746 factor ONCE to all pre-TSF orders (not per-order) to avoid Math.round drift
  const totalEarned  = (sid) => {
    const so = storeOrders(sid);
    const postTSF = so.filter(o=>!gwAbsorbed(o)).reduce((s,o)=>s+Number(o.amount||0),0);
    const preGross = so.filter(o=>gwAbsorbed(o)).reduce((s,o)=>s+Number(o.amount||0),0);
    return postTSF + Math.round(preGross * 0.9746);
  };
  const totalPaidOut = (sid) => (payoutsByStore.get(sid) || []).reduce((s,p)=>s+Number(p.amount||0),0);
  // outstanding = net earnings from confirmed orders with NO payout_id (genuinely unpaid)
  // payout_id is the reliable source of truth — bloom_payouts may be empty/incomplete
  const outstanding  = (sid) => _bulkNet(_ordersOf(sid).filter(o => o.status === "confirmed" && !o.payout_id));

  // Settlement-based helpers
  const settledOrders  = (sid) => _ordersOf(sid).filter(o => o.rzp_settled && !o.payout_id);
  const unsettledOrders = (sid) => _ordersOf(sid).filter(o => !o.rzp_settled);
  // settledBalance = net from settled orders that have no payout_id stamped yet
  // Do NOT subtract totalPaidOut here — settledOrders already excludes paid orders (payout_id set)
  // Apply 0.9746 ONCE to combined pre-TSF total (not per-order) to avoid Math.round drift
  const _bulkNet = (arr) => {
    const post = arr.filter(o=>!gwAbsorbed(o)).reduce((s,o)=>s+Number(o.amount||0),0); // customer paid gateway → full amount
    const pre  = arr.filter(o=>gwAbsorbed(o)).reduce((s,o)=>s+Number(o.amount||0),0);   // store absorbed gateway → ×0.9746
    return post + Math.round(pre * 0.9746);
  };
  const settledBalance  = (sid) => _bulkNet(settledOrders(sid));
  const pendingFromRzp  = (sid) => _bulkNet(unsettledOrders(sid));
  const hasAnySynced    = orders.some(o => o.rzp_settled);
  // Bloom's own income (platform fee) on a store's orders. Separate from the seller payout:
  // payout = what we send the store; bloomFee = what Bloom keeps. Both come out of the same
  // settled gross. Absorb-store fees show here too (they're baked into a reduced amount).
  const _bulkFee        = (arr) => arr.reduce((s,o)=>s+Number(o.platform_fee||0),0);
  const settledFee      = (sid) => _bulkFee(settledOrders(sid));
  const outstandingFee  = (sid) => _bulkFee(orders.filter(o => o.store_id === sid && o.status === "confirmed" && !o.payout_id));

  // ── Refund ONE order, end-to-end ───────────────────────────────────────────
  // Dry-runs first so we can show the exact amount and warn when the payment also
  // covers sibling orders (multi-store cart). The backend refunds only this order's
  // gross, marks it refunded (dropping it from payouts + Bloom Earnings) and
  // restores stock. A sibling order on the same payment is never touched.
  // NOTE: refundingId's useState lives with the other hooks at the top of AdminPage —
  // it must sit ABOVE the `if (!authed) return` early exit, or the hook count changes
  // between the logged-out and logged-in renders and React blanks the page.
  const handleRefundOrder = async (o) => {
    if (refundingId) return;
    setRefundingId(o.id);
    const H = { "Content-Type": "application/json", "x-admin-token": ADMIN_ID };
    try {
      const dry = await fetch(`${BACKEND_URL}/api/admin/refund-order`, {
        method: "POST", headers: H, body: JSON.stringify({ orderId: o.id, dryRun: true }),
      }).then(r => r.json());
      if (dry.error) { alert(`Cannot refund ${o.id}:\n\n${dry.error}`); setRefundingId(null); return; }

      const b = dry.breakdown || {};
      const msg =
        `Refund order ${o.id}?\n\n` +
        `Customer gets back ₹${dry.refundAmount}\n` +
        `   • item + shipping   ₹${b.amount}\n` +
        `   • gateway fee       ₹${b.gateway_fee}\n` +
        `   • platform fee      ₹${b.platform_fee}\n\n` +
        (dry.multiOrderPayment
          ? `This payment ALSO covers other orders (multi-store cart).\nOnly this order's ₹${dry.refundAmount} is refunded — the others are untouched.\n\n`
          : "") +
        `The order will be marked refunded (removed from payouts and Bloom Earnings) and its stock restored.`;
      if (!window.confirm(msg)) { setRefundingId(null); return; }

      const res = await fetch(`${BACKEND_URL}/api/admin/refund-order`, {
        method: "POST", headers: H,
        body: JSON.stringify({ orderId: o.id, restoreStock: true, reason: "Cancelled by Bloom admin" }),
      }).then(r => r.json());

      if (!res.ok) { alert(`Refund FAILED for ${o.id}:\n\n${res.error || "Unknown error"}`); setRefundingId(null); return; }
      setOrders(prev => prev.map(x => x.id === o.id ? { ...x, status: "refunded" } : x));
      alert(
        `✓ Refunded ₹${res.refundAmount}\nRefund ID: ${res.refundId}\n` +
        (res.stockRestored ? "Stock restored." : "Stock NOT restored — check manually.") +
        (res.warning ? `\n\n${res.warning}` : "")
      );
    } catch (e) { alert("Refund error: " + e.message); }
    setRefundingId(null);
  };

  // Group settled-unpaid orders by settlement date for a store
  const settlementBatches = (sid) => {
    const settled = settledOrders(sid);
    const grouped = {};
    for (const o of settled) {
      const dateKey = o.rzp_settled_at ? o.rzp_settled_at.split("T")[0] : "unknown";
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(o);
    }
    return Object.entries(grouped)
      .sort(([a],[b]) => a.localeCompare(b))
      .map(([date, ords]) => ({
        date,
        orders: ords,
        amount: ords.reduce((s,o) => s+net(o),0),
        dateFormatted: date === "unknown" ? "Unknown date" : new Date(date).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}),
      }));
  };

  // ── Morning / Evening payout shifts (bucketed by ORDER placement time, IST) ──
  // Day shift   = order placed 06:00:00–17:59:59 IST → pay the SAME EVENING
  // Night shift = order placed 18:00:00–05:59:59 IST → pay the NEXT MORNING
  // Boundary is exact & non-overlapping: 05:59:59→night, 06:00:00→day,
  // 17:59:59→day, 18:00:00→night. Every order lands in exactly one shift, so an
  // order right on 5:59:59 can never fall in both columns or slip out of both.
  const orderShift = (o) => {
    const ts = o.created_at || o.order_date;
    if (!ts) return "day";
    const d = new Date(ts);
    if (isNaN(d)) return "day";
    const ist = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const h = ist.getHours();
    return (h >= 6 && h < 18) ? "day" : "night";
  };

  // Razorpay settlement cadence used for the per-order "when does this settle" label.
  // Was T+2; the new account (2026-07-29) is on SAME-DAY settlement (T+0), so a
  // weekday order settles the same day. Flip this one number if the cycle ever changes
  // (e.g. back to 2 for T+2). 0 = same-day: the loop below is skipped and it returns
  // the order's own date → today's orders correctly show "Settling today".
  const SETTLEMENT_WORKING_DAYS = 0;
  const t2SettlementDate = (orderDate) => {
    if (!orderDate) return null;
    const d = new Date(orderDate);
    if (isNaN(d)) return null;
    let count = 0;
    while (count < SETTLEMENT_WORKING_DAYS) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) count++;
    }
    return d;
  };

  const deletePayouts = (ids) => setConfirmDlg({
    title: ids.size>1 ? `Delete ${ids.size} Payouts` : "Delete Payout",
    msg:"This will remove the payout record(s). The actual transfer already happened — this only removes the log entry.",
    danger:true, okLabel:"Delete",
    onOk: async () => {
      setConfirmDlg(null);
      let allOk = true;
      for (const id of ids) { const ok = await api.deletePayout(id); if (!ok) allOk = false; }
      // Re-fetch from DB to confirm deletion actually persisted
      const fresh = await api.getPayouts();
      if (fresh !== null) setPayouts(fresh);
      else setPayouts(prev => prev.filter(p => !ids.has(p.id)));
      setSelPayouts(new Set());
      if (!allOk) alert("One or more payouts could not be deleted. Please try again.");
    }
  });

  // HALT / RESUME: blocks (or re-opens) every new purchase from one store. Server sets admin_halted + checkout_paused; the seller
  // can't undo it from Settings. Confirm first; only update the row when the server says ok.
  const haltStore = (store) => {
    const next = !store.admin_halted;
    setConfirmDlg({
      title: next ? `Halt ${store.store_name}?` : `Resume ${store.store_name}?`,
      msg: next
        ? "Customers will NOT be able to buy anything from this store (web and app). The store stays visible, and the owner cannot turn this off. Payments already in progress can still complete."
        : "This store will accept orders again.",
      danger: next, okLabel: next ? "Halt store" : "Resume store",
      onOk: async () => {
        setConfirmDlg(null);
        try {
          const r = await fetch(`${BACKEND_URL}/api/admin/halt-store/${store.id}`, { method:"PATCH", headers:{ "Content-Type":"application/json", "x-admin-token": ADMIN_ID }, body: JSON.stringify({ halted: next }) });
          if (!r.ok) { alert(`Could not ${next ? "halt" : "resume"} the store (${r.status}) — nothing changed.`); return; }
          setStores(prev => prev.map(s => s.id === store.id ? { ...s, admin_halted: next, checkout_paused: next } : s));
        } catch { alert("Network error — nothing changed."); }
      }
    });
  };

  const deleteStore = (store) => setConfirmDlg({
    title:`Delete Store: ${store.store_name}`,
    msg:`This permanently removes the account for ${store.email}. Orders and products in the database are NOT deleted.`,
    danger:true, okLabel:"Delete Store",
    onOk: async () => {
      setConfirmDlg(null);
      const r = await fetch(`${BACKEND_URL}/api/admin/reject-user/${store.id}`, {
        method: "DELETE",
        headers: { "x-admin-token": ADMIN_ID }
      });
      if (r.ok) setStores(prev => prev.filter(s => s.id !== store.id));
      else alert("Could not delete store — please try again.");
    }
  });

  const deleteCustomer = (c) => setConfirmDlg({
    title:`Delete Customer: ${c.name}`,
    msg:`This permanently removes the account for ${c.email}.`,
    danger:true, okLabel:"Delete Customer",
    onOk: async () => {
      setConfirmDlg(null);
      const ok = await api.deleteCustomer(c.id);
      // Re-fetch from DB to confirm deletion actually persisted
      const fresh = await api.getCustomers();
      if (fresh !== null) setCustomers(fresh);
      else if (ok) setCustomers(prev => prev.filter(x => x.id !== c.id));
      if (!ok) alert("Could not delete customer — please try again.");
    }
  });

  const markAsPaid = (store) => {
    const amt = outstanding(store.id);
    if (amt <= 0) return;
    setConfirmDlg({
      title:`Confirm Payout — ${store.store_name}`,
      msg:`Mark ₹${amt.toLocaleString()} as paid out to ${store.store_name}? This will be recorded and cannot be undone.`,
      danger:false, okLabel:`Mark ₹${amt.toLocaleString()} Paid`,
      onOk: async () => {
        setConfirmDlg(null);
        const rec = { id:`pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`, store_id:store.id, amount:amt, note:`Payout to ${store.store_name}`, paid_at:new Date().toISOString() };
        const saved = await api.insertPayout(rec);
        if (saved !== null) {
          setPayouts(prev=>[...prev, rec]);
        } else {
          alert("Could not save payout — please check your connection and try again.");
        }
      }
    });
  };

  const markPaidBatch = (store, batch) => {
    setConfirmDlg({
      title: `Pay ${store.store_name} — ${batch.dateFormatted}`,
      msg: `Mark ₹${batch.amount.toLocaleString()} as paid to ${store.store_name} for orders settled on ${batch.dateFormatted}?`,
      danger: false, okLabel: `Mark ₹${batch.amount.toLocaleString()} Paid`,
      onOk: async () => {
        setConfirmDlg(null);
        const payoutId = `pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
        const rec = { id: payoutId, store_id: store.id, amount: batch.amount, note: `Payout for orders settled ${batch.dateFormatted}`, paid_at: new Date().toISOString() };
        const [saved] = await Promise.all([
          api.insertPayout(rec),
          fetch(`${BACKEND_URL}/api/admin/orders/mark-paid-batch`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "x-admin-token": ADMIN_ID },
            body: JSON.stringify({ storeId: store.id, settledDate: batch.date, payoutId }),
          }),
        ]);
        if (saved !== null) {
          setPayouts(prev => [...prev, rec]);
          setOrders(prev => prev.map(o =>
            o.store_id === store.id && o.rzp_settled && o.rzp_settled_at?.startsWith(batch.date) && !o.payout_id
              ? { ...o, payout_id: payoutId }
              : o
          ));
        } else {
          alert("Could not save payout — please try again.");
        }
      }
    });
  };

  const toggleMaintenance = async () => {
    setMainToggling(true);
    const next = !mainMode;
    try {
      const r = await fetch(`${BACKEND_URL}/api/settings`, {
        method:"PATCH",
        headers:{"Content-Type":"application/json","x-admin-token": ADMIN_ID},
        body: JSON.stringify({ maintenance_mode: next }),
      });
      if (r.ok) setMainMode(next);
      else alert("Failed to update maintenance mode.");
    } catch(e) {
      alert("Network error — could not update maintenance mode.");
    }
    setMainToggling(false);
  };

  const togglePaymentsPaused = async () => {
    setPaysToggling(true);
    const next = !paysPaused;
    try {
      const r = await fetch(`${BACKEND_URL}/api/settings`, {
        method:"PATCH",
        headers:{"Content-Type":"application/json","x-admin-token": ADMIN_ID},
        body: JSON.stringify({ payments_paused: next }),
      });
      if (r.ok) setPaysPaused(next);
      else alert("Failed to update payments setting.");
    } catch(e) {
      alert("Network error — could not update payments setting.");
    }
    setPaysToggling(false);
  };

  // Save the gateway fee % → server config. Applies to the very next checkout, no deploy
  // and no app update (the server recomputes every charge from this). Platform-fee tiers
  // are left as-is (carried forward by the backend).
  const saveFeeConfig = async () => {
    const pct = parseFloat(feePctDraft);
    if (!(pct > 0 && pct < 10)) { alert("Enter a total gateway fee % between 0 and 10 (e.g. 2.54)."); return; }
    const factor = +(1 - pct/100).toFixed(4);
    if (!(factor > 0.9 && factor <= 1)) { alert("Fee % out of range."); return; }
    if (!window.confirm(`Set the gateway fee to ${pct}%? This changes what customers are charged on the NEXT checkout across web + every app version. No deploy needed.`)) return;
    setFeeSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/settings`, {
        method:"PATCH",
        headers:{"Content-Type":"application/json","x-admin-token": ADMIN_ID},
        body: JSON.stringify({ gateway_factor: factor, platform_fee_enabled: !!(feeCfg?.platform_fee_enabled) }),
      });
      if (r.ok) {
        const d = await r.json();
        const gf = Number(d.gateway_factor) || factor;
        setFeeCfg(prev => ({ ...(prev||{}), gateway_factor: gf, platform_fee_enabled: d.platform_fee_enabled !== false, platform_fee_tiers: Array.isArray(d.platform_fee_tiers) ? d.platform_fee_tiers : (prev?.platform_fee_tiers||[]) }));
        setFeePctDraft(((1 - gf) * 100).toFixed(2));
        alert(`✓ Gateway fee set to ${((1-gf)*100).toFixed(2)}% — live on the next checkout.`);
      } else {
        alert("Could not save fee config — please try again.");
      }
    } catch(e) { alert("Network error saving fee config."); }
    setFeeSaving(false);
  };

  const runClassify = async (force) => {
    if (force && !window.confirm("RE-classify ALL products (overwrites existing categories)? Use this only after changing the classifier rules.")) return;
    setClassifyBusy(true); setClassifyMsg("Classifying…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/classify-products`, {
        method:"POST",
        headers:{"Content-Type":"application/json","x-admin-token": ADMIN_ID},
        body: JSON.stringify(force ? { force:true } : {}),
      });
      if (r.ok) {
        const d = await r.json();
        const top = Object.entries(d.tally||{}).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([k,v])=>`${k}:${v}`).join("  ");
        setClassifyMsg(`✓ scanned ${d.scanned}, tagged ${d.updated}${top?` — ${top}`:""}`);
      } else {
        setClassifyMsg(`✗ failed (${r.status})`);
      }
    } catch(e) { setClassifyMsg("✗ network error"); }
    setClassifyBusy(false);
  };

  const runOrderBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm("Fix product ordering for all affected stores? Inserts app-uploaded products that got stranded at the bottom back to the top (after pinned), newest-first. Existing custom order is preserved. Safe to run again.")) return;
    setOrderFixBusy(true); setOrderFixMsg(dryRun ? "Auditing…" : "Fixing…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/backfill-product-order`, {
        method:"POST",
        headers:{"Content-Type":"application/json","x-admin-token": ADMIN_ID},
        body: JSON.stringify({ dryRun: !!dryRun }),
      });
      if (r.ok) {
        const d = await r.json();
        const worst = (d.report||[]).slice().sort((a,b)=>(b.inserted||0)-(a.inserted||0)).slice(0,4).map(s=>`${s.store}:${s.inserted}`).join("  ");
        setOrderFixMsg(`${dryRun?"AUDIT — would fix":"✓ fixed"} ${d.storesAffected} store(s), ${d.productsInserted} product(s)${d.staleDropped?`, dropped ${d.staleDropped} stale`:""}${worst?` — ${worst}`:""}`);
      } else {
        setOrderFixMsg(`✗ failed (${r.status})`);
      }
    } catch(e) { setOrderFixMsg("✗ network error"); }
    setOrderFixBusy(false);
  };

  // Re-send WhatsApp notifications missed during an outage (order confirms, seller
  // alerts, payouts). Dry-run first shows the counts; the real send is idempotent
  // (flags gate it) so it never double-messages someone who already got it.
  const runWaResend = async (dryRun) => {
    if (!dryRun && !window.confirm("Re-send the missed WhatsApp messages shown by the dry-run? Safe: it skips anyone who already received theirs. Note: payout messages may re-send to sellers who already got one today, since delivery tracking started just now.")) return;
    setWaBusy(true); setWaMsg(dryRun ? "Checking…" : "Sending…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/wa/resend-missed?hours=24&dryRun=${dryRun?1:0}`, {
        method:"POST",
        headers:{"Content-Type":"application/json","x-admin-token": ADMIN_ID},
      });
      if (r.ok) {
        const d = await r.json();
        const verb = dryRun ? "WOULD send" : "✓ Sent";
        setWaMsg(d.error ? `✗ ${d.error}` : `${verb}: ${d.orders_cust} order-confirmations, ${d.orders_seller} seller alerts, ${d.payouts} payout messages (last ${d.hours}h)`);
      } else {
        setWaMsg(`✗ failed (${r.status})`);
      }
    } catch(e) { setWaMsg("✗ network error"); }
    setWaBusy(false);
  };

  const healTSFAmounts = async () => {
    if (!window.confirm("Heal TSF order amounts? This queries Razorpay for every post-TSF order and corrects the stored amount. Safe to run multiple times.")) return;
    setHealRunning(true); setHealResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/heal-tsf-amounts`, {
        method:"POST", headers:{"Content-Type":"application/json","x-admin-token": ADMIN_ID},
      });
      const d = await r.json();
      setHealResult(d);
      if (d.fixed > 0) load(); // reload orders so UI reflects corrected amounts
    } catch(e) { setHealResult({ error: e.message }); }
    setHealRunning(false);
  };

  const tabs = ["payouts","transactions","history","stores","customers","orders","marketplace","stock","earnings","subscriptions","status"];
  const allRevenue   = orders.filter(o=>o.status==="confirmed").reduce((s,o)=>s+gross(o),0);
  const allPaidOut   = payouts.reduce((s,p)=>s+Number(p.amount||0),0);
  const allSettledNet = orders.filter(o=>o.status==="confirmed"&&o.rzp_settled).reduce((s,o)=>s+net(o),0);
  // Remaining = settled orders not yet linked to a payout (actual unpaid, not gross-vs-payouts delta)
  const allRemaining = Math.round(orders.filter(o=>o.status==="confirmed"&&o.rzp_settled&&!o.payout_id).reduce((s,o)=>s+net(o),0));
  // Total owed to stores: ALL unsettled confirmed orders (not yet paid out, regardless of settlement date)
  const allOwedToStores = Math.round(orders.filter(o=>o.status==="confirmed"&&!o.rzp_settled).reduce((s,o)=>s+net(o),0));

  return (
    <>
    {confirmDlg&&<ConfirmModal {...confirmDlg} onCancel={()=>setConfirmDlg(null)}/>}
    <div style={{minHeight:"100vh",background:"var(--offwhite)"}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,var(--ink) 0%,#2a2a2e 100%)",padding:isMobile?"28px 5vw":"40px 6vw"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:24,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <Wordmark size={26}/>
            <div>
              <h1 style={{color:"#fff",fontSize:isMobile?22:30,fontWeight:700}}>Bloom Admin</h1>
              <p style={{color:"rgba(255,255,255,0.55)",fontSize:12}}>Internal dashboard · Bloom HQ</p>
            </div>
          </div>
          {/* Heal TSF amounts + Refresh All */}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <button onClick={reloadAdminData} disabled={adminRefreshing}
              style={{background:adminRefreshing?"rgba(255,255,255,0.1)":"rgba(255,255,255,0.12)",border:"1.5px solid rgba(255,255,255,0.25)",borderRadius:4,padding:"8px 14px",fontSize:11,fontWeight:700,color:"#fff",cursor:adminRefreshing?"not-allowed":"pointer",letterSpacing:"0.04em"}}>
              {adminRefreshing?<><Icon n="pending" size="1.1em" inline />{" Refreshing…"}</>:<><Icon n="refresh" size="1.1em" inline />{" Refresh All Data"}</>}
            </button>
            <button onClick={healTSFAmounts} disabled={healRunning}
              style={{background:healRunning?"rgba(255,255,255,0.1)":"rgba(47,99,221,0.25)",border:"1.5px solid rgba(47,99,221,0.5)",borderRadius:4,padding:"8px 14px",fontSize:11,fontWeight:700,color:"#fff",cursor:healRunning?"not-allowed":"pointer",letterSpacing:"0.04em"}}>
              {healRunning?<><Icon n="pending" size="1.1em" inline />{" Healing…"}</>:<><Icon n="settings" size="1.1em" inline />{" Heal TSF Amounts"}</>}
            </button>
            {healResult&&<div style={{fontSize:10,color:healResult.error?"#ff9999":"rgba(47,99,221,0.9)",textAlign:"center"}}>
              {healResult.error ? `Error: ${healResult.error}` : `✓ Fixed ${healResult.fixed}, skipped ${healResult.skipped}`}
            </div>}
          </div>
          {/* Site controls — stacked column */}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {/* Maintenance mode toggle */}
            <div style={{display:"flex",alignItems:"center",gap:12,background:"rgba(255,255,255,0.08)",borderRadius:4,padding:"10px 16px",border:`1.5px solid ${mainMode?"#ff6b6b":"rgba(255,255,255,0.15)"}`}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.7)",letterSpacing:"0.05em"}}>MAINTENANCE MODE</div>
                <div style={{fontSize:11,color:mainMode?"#ff9999":"rgba(255,255,255,0.4)",marginTop:2}}>{mainMode?<><Icon n="dot" size="1.1em" inline />{" Site is offline for users"}</>:<><Icon n="dot" size="1.1em" inline />{" Site is live"}</>}</div>
              </div>
              <button
                onClick={toggleMaintenance}
                disabled={mainToggling}
                style={{
                  position:"relative",width:52,height:28,borderRadius:4,border:"none",cursor:mainToggling?"not-allowed":"pointer",
                  background:mainMode?"#ff3a3a":"rgba(255,255,255,0.25)",
                  transition:"background 0.25s",flexShrink:0,
                  opacity:mainToggling?0.6:1,
                }}
              >
                <div style={{position:"absolute",top:3,left:mainMode?24:3,width:22,height:22,borderRadius:"50%",background:"#fff",transition:"left 0.25s",boxShadow:"0 1px 4px rgba(0,0,0,0.3)"}}/>
              </button>
            </div>
            {/* Payments paused toggle */}
            <div style={{display:"flex",alignItems:"center",gap:12,background:"rgba(255,255,255,0.08)",borderRadius:4,padding:"10px 16px",border:`1.5px solid ${paysPaused?"#f59e0b":"rgba(255,255,255,0.15)"}`}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.7)",letterSpacing:"0.05em"}}>PAUSE PAYMENTS</div>
                <div style={{fontSize:11,color:paysPaused?"#fcd34d":"rgba(255,255,255,0.4)",marginTop:2}}>{paysPaused?<><Icon n="dot" size="1.1em" inline />{" Payments are paused"}</>:<><Icon n="dot" size="1.1em" inline />{" Payments live"}</>}</div>
              </div>
              <button
                onClick={togglePaymentsPaused}
                disabled={paysToggling}
                style={{
                  position:"relative",width:52,height:28,borderRadius:4,border:"none",cursor:paysToggling?"not-allowed":"pointer",
                  background:paysPaused?"#f59e0b":"rgba(255,255,255,0.25)",
                  transition:"background 0.25s",flexShrink:0,
                  opacity:paysToggling?0.6:1,
                }}
              >
                <div style={{position:"absolute",top:3,left:paysPaused?24:3,width:22,height:22,borderRadius:"50%",background:"#fff",transition:"left 0.25s",boxShadow:"0 1px 4px rgba(0,0,0,0.3)"}}/>
              </button>
            </div>
          </div>
        </div>
        {/* Razorpay gateway fee — server-authoritative; applies to the next checkout, no deploy/app update */}
        {feeCfg && (
          <div style={{background:"#fff",border:"1.5px solid var(--border)",borderRadius:4,padding:"14px 18px",marginTop:16,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
            <div style={{minWidth:180,flex:"1 1 220px"}}>
              <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.05em",color:"var(--ink-soft)"}}><Icon n="card" size="1.1em" inline /> RAZORPAY GATEWAY FEE</div>
              <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:2}}>Charged to the fee-bearer (customer, or absorb-store seller). Applies to the next checkout on web + every app version — no deploy.</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input type="number" step="0.01" min="0" max="10" value={feePctDraft} onChange={e=>setFeePctDraft(e.target.value)}
                style={{width:90,padding:"8px 10px",border:"1.5px solid var(--border-mid)",borderRadius:4,fontSize:15,fontWeight:700,textAlign:"right"}}/>
              <span style={{fontSize:15,fontWeight:700,color:"var(--ink)"}}>%</span>
            </div>
            <button onClick={saveFeeConfig} disabled={feeSaving}
              style={{background:"var(--teal)",color:"#fff",border:"none",borderRadius:4,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:feeSaving?"not-allowed":"pointer",opacity:feeSaving?0.6:1,whiteSpace:"nowrap"}}>
              {feeSaving?"Saving…":"Save fee"}
            </button>
            <div style={{fontSize:11,color:"var(--ink-soft)",flexBasis:"100%"}}>
              current: <strong style={{color:"var(--ink)"}}>{((1-(feeCfg.gateway_factor||0.9746))*100).toFixed(2)}%</strong>
              {" · Bloom platform fee: "}<strong style={{color:"var(--teal-deep)"}}>{feeCfg.platform_fee_enabled?"on (tiered, Bloom keeps)":"off"}</strong>
              {Array.isArray(feeCfg.platform_fee_tiers)&&feeCfg.platform_fee_tiers.length ? <span style={{marginLeft:6,opacity:0.7}}>[{feeCfg.platform_fee_tiers.map(t=>`≥₹${t.min}: ${(t.pct*100).toFixed((t.pct*100)%1?2:1)}%`).join(", ")}]</span> : null}
            </div>
          </div>
        )}
        {/* Product categorization — classify products into the taxonomy (mega-menu) */}
        <div style={{background:"#fff",border:"1.5px solid var(--border)",borderRadius:4,padding:"14px 18px",marginTop:16,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{minWidth:180,flex:"1 1 220px"}}>
            <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.05em",color:"var(--ink-soft)"}}><Icon n="tag" size="1.1em" inline /> PRODUCT CATEGORIES</div>
            <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:2}}>Auto-tag products into the Shop-by-category menu. New products classify on create; run this to backfill existing ones.</div>
          </div>
          <button onClick={()=>runClassify(false)} disabled={classifyBusy}
            style={{background:"var(--teal)",color:"#fff",border:"none",borderRadius:4,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:classifyBusy?"not-allowed":"pointer",opacity:classifyBusy?0.6:1,whiteSpace:"nowrap"}}>
            {classifyBusy?"Working…":"Classify new"}
          </button>
          <button onClick={()=>runClassify(true)} disabled={classifyBusy}
            style={{background:"transparent",color:"var(--ink-soft)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"9px 14px",fontSize:12,fontWeight:700,cursor:classifyBusy?"not-allowed":"pointer",opacity:classifyBusy?0.6:1,whiteSpace:"nowrap"}}>
            Re-classify all
          </button>
          {classifyMsg && <div style={{fontSize:11,color:"var(--ink)",flexBasis:"100%",fontWeight:600}}>{classifyMsg}</div>}
        </div>
        {/* Product ordering repair — un-strand app-uploaded products buried at the bottom */}
        <div style={{background:"#fff",border:"1.5px solid var(--border)",borderRadius:4,padding:"14px 18px",marginTop:16,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{minWidth:180,flex:"1 1 220px"}}>
            <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.05em",color:"var(--ink-soft)"}}><Icon n="settings" size="1.1em" inline /> PRODUCT ORDERING</div>
            <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:2}}>App-uploaded products can land at the bottom of a storefront. This lifts them back to the top (after pinned), newest-first. Custom order preserved. Audit first to see counts.</div>
          </div>
          <button onClick={()=>runOrderBackfill(true)} disabled={orderFixBusy}
            style={{background:"transparent",color:"var(--ink-soft)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"9px 14px",fontSize:12,fontWeight:700,cursor:orderFixBusy?"not-allowed":"pointer",opacity:orderFixBusy?0.6:1,whiteSpace:"nowrap"}}>
            Audit (dry-run)
          </button>
          <button onClick={()=>runOrderBackfill(false)} disabled={orderFixBusy}
            style={{background:"var(--teal)",color:"#fff",border:"none",borderRadius:4,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:orderFixBusy?"not-allowed":"pointer",opacity:orderFixBusy?0.6:1,whiteSpace:"nowrap"}}>
            {orderFixBusy?"Working…":"Fix ordering"}
          </button>
          {orderFixMsg && <div style={{fontSize:11,color:"var(--ink)",flexBasis:"100%",fontWeight:600}}>{orderFixMsg}</div>}
        </div>
        {/* WhatsApp recovery — re-send notifications missed during an outage */}
        <div style={{background:"#fff",border:"1.5px solid var(--border)",borderRadius:4,padding:"14px 18px",marginTop:16,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{minWidth:180,flex:"1 1 220px"}}>
            <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.05em",color:"var(--ink-soft)"}}><Icon n="mobile" size="1.1em" inline /> WHATSAPP RECOVERY</div>
            <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:2}}>Re-sends order confirmations, seller alerts & payout messages that failed to go out (e.g. during a DB outage) in the last 24h. Skips anyone who already got theirs. Preview first to see counts.</div>
          </div>
          <button onClick={()=>runWaResend(true)} disabled={waBusy}
            style={{background:"transparent",color:"var(--ink-soft)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"9px 14px",fontSize:12,fontWeight:700,cursor:waBusy?"not-allowed":"pointer",opacity:waBusy?0.6:1,whiteSpace:"nowrap"}}>
            Preview (dry-run)
          </button>
          <button onClick={()=>runWaResend(false)} disabled={waBusy}
            style={{background:"var(--teal)",color:"#fff",border:"none",borderRadius:4,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:waBusy?"not-allowed":"pointer",opacity:waBusy?0.6:1,whiteSpace:"nowrap"}}>
            {waBusy?"Working…":"Send missed"}
          </button>
          {waMsg && <div style={{fontSize:11,color:"var(--ink)",flexBasis:"100%",fontWeight:600}}>{waMsg}</div>}
        </div>
        {/* Summary stats */}
        <div style={{display:"grid",gridTemplateColumns:`repeat(${isMobile?2:6},1fr)`,gap:12}}>
          {[
            {label:"Total Revenue",    val:`₹${allRevenue.toLocaleString()}`,        c:"#fff"},
            {label:"Paid Out",         val:`₹${allPaidOut.toLocaleString()}`,         c:"var(--green)"},
            {label:"Owed to Stores",   val:`₹${allOwedToStores.toLocaleString()}`,   c:allOwedToStores>0?"#ffb347":"rgba(255,255,255,0.5)", sub:"Unsettled orders pipeline"},
            {label:"Still to Pay Out", val:`₹${allRemaining.toLocaleString()}`,       c:allRemaining>0?"#ff6b6b":"rgba(255,255,255,0.5)"},
            {label:"Stores",           val:stores.length,                             c:"#fff"},
            {label:"Customers",        val:customers.length,                          c:"#fff"},
          ].map((s,i)=>(
            <div key={i} style={{background:"rgba(255,255,255,0.10)",borderRadius:4,padding:"16px 18px"}}>
              <div style={{fontSize:isMobile?20:24,fontWeight:700,color:s.c}}>{s.val}</div>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"rgba(255,255,255,0.5)",textTransform:"uppercase",marginTop:3}}>{s.label}</div>
              {s.sub&&<div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:2}}>{s.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{background:"#fff",borderBottom:"1px solid var(--border)",padding:`0 ${isMobile?"5vw":"6vw"}`}}>
        <div style={{display:"flex",gap:0,overflowX:"auto"}}>
          {tabs.map(t=>(
            <button key={t} onClick={()=>setTab(t)}
              style={{background:"none",border:"none",borderBottom:`2.5px solid ${tab===t?"var(--pink-btn)":"transparent"}`,padding:"14px 20px",fontFamily:"Syne",fontSize:13,fontWeight:700,color:tab===t?"var(--pink-btn)":"var(--ink-soft)",cursor:"pointer",textTransform:"capitalize",flexShrink:0,transition:"all 0.2s"}}>
              {t==="payouts"?<><Icon n="rupee" size="1.1em" inline />{" Payouts"}</>:t==="transactions"?<><Icon n="card" size="1.1em" inline />{" Transactions"}</>:t==="history"?<><Icon n="document" size="1.1em" inline />{" History"}</>:t==="stores"?<><Icon n="store" size="1.1em" inline />{" Stores"}</>:t==="customers"?<><Icon n="bag" size="1.1em" inline />{" Customers"}</>:t==="orders"?<><Icon n="package" size="1.1em" inline />{" Orders"}</>:t==="marketplace"?<><Icon n="bag" size="1.1em" inline />{" Marketplace"}</>:t==="stock"?<><Icon n="analytics" size="1.1em" inline />{" Stock"}</>:t==="earnings"?<><BloomMark />{" Bloom Earnings"}</>:t==="subscriptions"?<><Icon n="gemstone" size="1.1em" inline />{" Subscriptions"}</>:<><Icon n="dot" size="1.1em" inline />{" Status"}</>}
            </button>
          ))}
        </div>
      </div>

      <div style={{padding:isMobile?"20px 5vw":"36px 6vw",maxWidth:1100,margin:"0 auto"}}>
        {loading ? (
          <div style={{textAlign:"center",padding:"60px 0"}}>
            <div style={{width:40,height:40,border:"3px solid var(--pink-btn)",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 16px"}}/>
            <p style={{color:"var(--ink-soft)"}}>Loading data…</p>
          </div>
        ) : (

        /* ── PAYOUTS TAB ── */
        tab==="payouts" ? (
          <div>
            {/* Top bar: search + sync */}
            <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:20,flexWrap:"wrap"}}>
              <input className="inp" style={{flex:1,minWidth:200,maxWidth:340}}
                placeholder="Search store name or email…"
                value={payoutSearch} onChange={e=>setPayoutSearch(e.target.value)}/>
              <button
                disabled={syncingSettlements}
                onClick={async()=>{
                  setSyncingSettlements(true);
                  try {
                    const r = await fetch(`${BACKEND_URL}/api/admin/sync-settlements`,{method:"POST",headers:{"x-admin-token":ADMIN_ID}});
                    const d = await r.json();
                    // Refresh orders after sync
                    const fresh = await fetch(`${BACKEND_URL}/api/admin/orders-all`, { headers: { "x-admin-token": ADMIN_ID } }).then(r=>r.ok?r.json():null).catch(()=>null);
                    if (fresh) setOrders(fresh);
                    // Refresh today data
                    fetch(`${BACKEND_URL}/api/admin/today-settlements`,{headers:{"x-admin-token":ADMIN_ID}})
                      .then(r2=>r2.json()).then(d2=>setTodayData(d2||{expected_today:[],settled_today:[]})).catch(()=>{});
                    alert(`Sync complete — ${d.updated||0} orders marked settled.`);
                  } catch(e){ alert("Sync failed: "+e.message); }
                  setSyncingSettlements(false);
                }}
                style={{background:"var(--teal)",color:"#fff",border:"none",borderRadius:4,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:syncingSettlements?"not-allowed":"pointer",opacity:syncingSettlements?0.6:1,whiteSpace:"nowrap",flexShrink:0}}>
                {syncingSettlements?"Syncing…":<><Icon n="refresh" size="1.1em" inline />{" Sync Settlements"}</>}
              </button>
            </div>

            {/* Today's Settlements card */}
            {(()=>{
              const istNow=new Date(new Date().toLocaleString("en-US",{timeZone:"Asia/Kolkata"}));
              const dow=istNow.getDay();
              const isWeekend=dow===0||dow===6;
              const dayName=dow===0?"Sunday":"Saturday";
              const hasRealData=todayData.rzp_today_amount>0;
              return (
              <div className="card" style={{padding:20,marginBottom:24,border:"1.5px solid var(--teal)",background:"#F0FFFE"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <span style={{fontSize:18}}><Icon n="rupee" size={18} inline /></span>
                  <h3 style={{fontSize:17,margin:0}}>Today's Settlements</h3>
                  {hasRealData&&<span style={{fontSize:10,background:"#D1FAE5",color:"#065F46",borderRadius:4,padding:"2px 8px",fontWeight:700,marginLeft:4}}>✓ Live from Razorpay</span>}
                </div>

                {/* Real Razorpay amount — always show this first */}
                {hasRealData&&(
                  <div style={{marginBottom:14,padding:"14px 16px",background:"#fff",borderRadius:4}}>
                    <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",marginBottom:10}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.07em",color:todayData.rzp_today_status==="processed"?"var(--green)":"var(--teal-deep)",marginBottom:3}}>
                          {todayData.rzp_today_status==="processed"?<><Icon n="success" size="1.1em" inline />{" DEPOSITED TO BANK TODAY"}</>:<><Icon n="bank" size="1.1em" inline />{" ARRIVING IN YOUR BANK TODAY"}</>}
                        </div>
                        <div style={{fontSize:28,fontWeight:700,color:"var(--teal-deep)"}}>
                          ₹{(todayData.rzp_today_amount||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}
                        </div>
                        <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:2}}>Net received from Razorpay · after fees</div>
                      </div>
                      {todayData.rzp_settlements&&todayData.rzp_settlements[0]&&(
                        <code style={{fontSize:10,background:"var(--surface)",padding:"3px 8px",borderRadius:4,color:"var(--ink-soft)",fontFamily:"monospace",flexShrink:0}}>
                          {todayData.rzp_settlements[0].id}
                        </code>
                      )}
                    </div>
                    {/* Per-store factor table — DISABLED. Superseded by the Morning/Evening shift view
                        below, which is driven by settled-but-unpaid orders and never resets at midnight.
                        Kept (returns null) for the exact-fee reference; safe to delete once shift view is trusted. */}
                    {(()=>{
                      return null;
                      /* eslint-disable */
                      const exp=todayData.expected_today||[];
                      if(exp.length===0) return null;
                      const totalGross=exp.reduce((s,o)=>s+Number(o.amount||0),0);
                      const net=todayData.rzp_today_amount||0;
                      const rawFactor=totalGross>0?net/totalGross:1.0;
                      const incompletSync=rawFactor>1||rawFactor<=0;
                      const factor=incompletSync?1.0:rawFactor;
                      const byStore={};
                      for(const o of exp){
                        const storeInfo=storeById.get(o.store_id);
                        if(!byStore[o.store_id])byStore[o.store_id]={orders:[],gross:0,platformFee:0,store_id:o.store_id,name:storeInfo?.store_name||o.store_id,upi:storeInfo?.upi_id||null,phone:storeInfo?.phone||null};
                        byStore[o.store_id].orders.push(o);
                        byStore[o.store_id].gross+=Number(o.amount||0);
                        byStore[o.store_id].platformFee+=Number(o.platform_fee||0);
                      }
                      const rows=Object.values(byStore);
                      // Bloom's own income on this settlement = Σ platform_fee across its orders
                      // (same definition as the Bloom Earnings tab). Stays in your account.
                      const bloomKeep=rows.reduce((s,sg)=>s+sg.platformFee,0);
                      // Post-TSF (customer-pays): customer already paid the gateway fee on top → full amount.
                      // Pre-TSF (absorb): store nets the FIXED 2.54% gateway fee (×0.9746) — exactly what the
                      // seller chose. NOT the variable factor (caps to 1.0 on incomplete sync → overpays absorb).
                      // Round the 0.9746 ONCE on the combined pre-TSF total (not per-order) to avoid
                      // Math.round drift — identical to _bulkNet()/totalEarned() used everywhere else.
                      const storeNetPayout=(sg)=>{
                        const post=sg.orders.filter(o=>!gwAbsorbed(o)).reduce((s,o)=>s+Number(o.amount||0),0);
                        const pre =sg.orders.filter(o=>gwAbsorbed(o)).reduce((s,o)=>s+Number(o.amount||0),0);
                        return post + Math.round(pre*0.9746);
                      };
                      const feeTotal=totalGross*factor-totalGross; // negative = fee taken
                      // A store is "already handled" if every order in the settlement already has a payout_id
                      // This covers stores paid from personal money before the settlement arrived
                      const alreadyHandledIds=new Set(
                        Object.values(byStore)
                          .filter(sg=>sg.orders.every(o=>{const fo=orders.find(x=>x.id===o.id);return !!(fo?.payout_id||o.payout_id);}))
                          .map(sg=>sg.store_id)
                      );
                      const paidTodayIds=alreadyHandledIds;
                      return (
                        <div>
                          {incompletSync&&(
                            <div style={{marginBottom:10,padding:"8px 12px",background:"#FEF9C3",border:"1px solid #FCD34D",borderRadius:4,fontSize:12,color:"#92400E"}}>
                              <Icon n="warning" size={12} inline /> <strong>Incomplete sync</strong> — only ₹{totalGross.toLocaleString("en-IN",{minimumFractionDigits:2})} of ₹{net.toLocaleString("en-IN",{minimumFractionDigits:2})} matched with Razorpay yet. Payout amounts below are exact (fixed 2.54% gateway fee per the seller's fee setting) — this only affects the reconciliation total. Re-sync after all orders are confirmed.
                            </div>
                          )}
                          <div style={{display:"flex",gap:16,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
                            <div style={{fontSize:12,color:"var(--ink-soft)"}}>Gross: <strong style={{color:"var(--ink)"}}>₹{totalGross.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></div>
                            <div style={{fontSize:12,color:"var(--ink-soft)"}}>Fee est.: <strong style={{color:"var(--red)"}}>~2.54%</strong></div>
                            <div style={{fontSize:12,color:"var(--ink-soft)"}}>Factor: <code style={{fontSize:11,background:"var(--surface)",padding:"1px 5px",borderRadius:4}}>{factor.toFixed(6)}{incompletSync?" (est.)":""}</code></div>
                            {bloomKeep>0&&<div style={{fontSize:12.5,color:"var(--teal-deep)",fontWeight:800}}><BloomMark /> Bloom keeps: ₹{bloomKeep.toLocaleString("en-IN")}</div>}
                            {rows.filter(sg=>!paidTodayIds.has(sg.store_id)).length>0&&(
                              <button style={{marginLeft:"auto",background:"var(--green)",color:"#fff",border:"none",borderRadius:4,padding:"7px 18px",fontSize:13,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}
                                onClick={async()=>{
                                  const unpaid=rows.filter(sg=>!paidTodayIds.has(sg.store_id));
                                  if(!window.confirm(`Mark all ${unpaid.length} stores as paid from today's settlement?`)) return;
                                  const settleDateStr=todayData.rzp_settlements?.[0]?.settled_on
                                    ?new Date(todayData.rzp_settlements[0].settled_on*1000).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})
                                    :new Date().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"});
                                  const settleDate=todayData.rzp_settlements?.[0]?.settled_on
                                    ?new Date(todayData.rzp_settlements[0].settled_on*1000).toISOString().slice(0,10)
                                    :new Date().toISOString().slice(0,10);
                                  let ok=0,fail=0,lastErr="";
                                  for(const sg of unpaid){
                                    const storeNet=storeNetPayout(sg);
                                    const payoutId=`pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
                                    const rec={id:payoutId,store_id:sg.store_id,amount:storeNet,note:`Settlement payout ${settleDateStr} — ${sg.name}`,paid_at:new Date().toISOString()};
                                    const saved=await api.insertPayout(rec);
                                    if(saved?.__payoutError){fail++;lastErr=`[${saved.status}] ${saved.message}`;}
                                    else{
                                      ok++;
                                      setPayouts(prev=>{const ids=new Set(prev.map(p=>p.id));return ids.has(rec.id)?prev:[...prev,rec];});
                                      fetch(`${BACKEND_URL}/api/admin/orders/mark-paid-batch`,{method:"PATCH",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:JSON.stringify({storeId:sg.store_id,settledDate:settleDate,payoutId})}).catch(()=>{});
                                    }
                                  }
                                  setSelectedPayouts(new Set());
                                  if(fail>0) alert(`${ok} paid. ${fail} failed.\n${lastErr}`);
                                  else alert(`✓ All ${ok} stores marked paid`);
                                }}>
                                ✓ Pay All Stores
                              </button>
                            )}
                          </div>
                          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:560}}>
                            <thead>
                              <tr style={{borderBottom:"1.5px solid var(--border)"}}>
                                <th style={{padding:"4px 8px 6px 0",width:28}}>
                                  <input type="checkbox"
                                    checked={rows.length>0&&rows.every(sg=>selectedPayouts.has(sg.name))}
                                    onChange={e=>{
                                      const s=new Set(selectedPayouts);
                                      if(e.target.checked) rows.forEach(sg=>s.add(sg.name));
                                      else rows.forEach(sg=>s.delete(sg.name));
                                      setSelectedPayouts(s);
                                    }}
                                    style={{cursor:"pointer",width:14,height:14}}/>
                                </th>
                                <th style={{textAlign:"left",padding:"4px 8px 6px 0",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>STORE</th>
                                <th style={{textAlign:"left",padding:"4px 8px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>UPI ID</th>
                                <th style={{textAlign:"left",padding:"4px 8px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>PHONE</th>
                                <th style={{textAlign:"right",padding:"4px 8px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>ORDERS</th>
                                <th style={{textAlign:"right",padding:"4px 0 6px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>GROSS</th>
                                <th style={{textAlign:"right",padding:"4px 0 6px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--teal-deep)"}}><BloomMark /> BLOOM</th>
                                <th style={{textAlign:"right",padding:"4px 0 6px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--green)"}}>→ PAY OUT</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((sg,i)=>{
                                const storeNet=storeNetPayout(sg);
                                const checked=selectedPayouts.has(sg.name);
                                const paidToday=paidTodayIds.has(sg.store_id);
                                return (
                                  <tr key={i} style={{borderBottom:"1px solid var(--border)",background:paidToday?"rgba(6,118,71,0.06)":checked?"rgba(47,99,221,0.07)":"transparent",transition:"background 0.15s",opacity:paidToday?0.7:1}}>
                                    <td style={{padding:"6px 8px 6px 0"}}>
                                      <input type="checkbox" checked={checked} disabled={paidToday}
                                        onChange={e=>{
                                          const s=new Set(selectedPayouts);
                                          e.target.checked?s.add(sg.name):s.delete(sg.name);
                                          setSelectedPayouts(s);
                                        }}
                                        style={{cursor:paidToday?"default":"pointer",width:14,height:14}}/>
                                    </td>
                                    <td style={{padding:"6px 8px 6px 0",fontWeight:600}}>
                                      {sg.name}
                                      {paidToday&&<span style={{marginLeft:7,fontSize:10,fontWeight:700,color:"var(--green)",background:"rgba(6,118,71,0.12)",borderRadius:4,padding:"1px 6px",letterSpacing:"0.04em"}}>✓ PAID</span>}
                                    </td>
                                    <td style={{padding:"6px 8px"}}>
                                      {sg.upi
                                        ? <code style={{fontSize:11,background:"var(--surface)",padding:"2px 6px",borderRadius:4,color:"var(--ink)"}}>{sg.upi}</code>
                                        : <span style={{fontSize:11,color:"var(--red)"}}><Icon n="warning" size="1.1em" inline /> no UPI</span>}
                                    </td>
                                    <td style={{padding:"6px 8px",fontSize:12,color:"var(--ink-soft)"}}>{sg.phone||"—"}</td>
                                    <td style={{padding:"6px 8px",textAlign:"right",color:"var(--ink-soft)"}}>{sg.orders.length}</td>
                                    <td style={{padding:"6px 0 6px 8px",textAlign:"right",color:"var(--ink-soft)"}}>₹{sg.gross.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                    <td style={{padding:"6px 0 6px 8px",textAlign:"right",fontWeight:700,color:"var(--teal-deep)"}}>{sg.platformFee>0?`₹${sg.platformFee.toLocaleString("en-IN")}`:"—"}</td>
                                    <td style={{padding:"6px 0 6px 8px",textAlign:"right",fontWeight:700,color:"var(--green)"}}>₹{storeNet.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          </div>
                          {/* Selected summary bar */}
                          {selectedPayouts.size>0&&(
                            <div style={{marginTop:10,padding:"10px 14px",background:"var(--teal-light)",border:"1.5px solid var(--teal)",borderRadius:4,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                              <span style={{fontSize:13,fontWeight:700,color:"var(--teal-deep)"}}>
                                {selectedPayouts.size} store{selectedPayouts.size!==1?"s":""} selected
                              </span>
                              <span style={{fontSize:15,fontWeight:700,color:"var(--green)"}}>
                                ₹{rows.filter(sg=>selectedPayouts.has(sg.name)).reduce((s,sg)=>s+storeNetPayout(sg),0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}
                              </span>
                              <button className="btn-green" style={{padding:"7px 16px",fontSize:13,borderRadius:4,fontWeight:700,whiteSpace:"nowrap"}}
                                onClick={async()=>{
                                  const sel=rows.filter(sg=>selectedPayouts.has(sg.name));
                                  const settleDateStr=todayData.rzp_settlements?.[0]?.settled_on
                                    ?new Date(todayData.rzp_settlements[0].settled_on*1000).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})
                                    :new Date().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"});
                                  const settleDate=todayData.rzp_settlements?.[0]?.settled_on
                                    ?new Date(todayData.rzp_settlements[0].settled_on*1000).toISOString().slice(0,10)
                                    :new Date().toISOString().slice(0,10);
                                  let ok=0,fail=0,lastErr="";
                                  for(const sg of sel){
                                    const storeNet=storeNetPayout(sg);
                                    // Random suffix prevents duplicate-key errors on re-click
                                    const payoutId=`pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
                                    const rec={id:payoutId,store_id:sg.store_id,amount:storeNet,note:`Settlement payout ${settleDateStr} — ${sg.name}`,paid_at:new Date().toISOString()};
                                    const saved=await api.insertPayout(rec);
                                    if(saved?.__payoutError){fail++;lastErr=`[${saved.status}] ${saved.message}`;}
                                    else{
                                      ok++;
                                      setPayouts(prev=>{const ids=new Set(prev.map(p=>p.id));return ids.has(rec.id)?prev:[...prev,rec];});
                                      // Stamp payout_id on orders so they leave the Pending Payouts batches
                                      fetch(`${BACKEND_URL}/api/admin/orders/mark-paid-batch`,{method:"PATCH",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:JSON.stringify({storeId:sg.store_id,settledDate:settleDate,payoutId})}).catch(()=>{});
                                    }
                                  }
                                  setSelectedPayouts(new Set());
                                  if(fail>0) alert(`${ok} payout${ok!==1?"s":""} saved. ${fail} failed.\n\nError: ${lastErr}`);
                                  else if(ok>0) alert(`✓ ${ok} payout${ok!==1?"s":""} recorded`);
                                }}>
                                ✓ Mark Paid
                              </button>
                              <button onClick={()=>setSelectedPayouts(new Set())}
                                style={{background:"none",border:"1px solid var(--teal)",borderRadius:4,padding:"5px 10px",fontSize:12,fontWeight:600,cursor:"pointer",color:"var(--teal-deep)"}}>
                                Clear
                              </button>
                            </div>
                          )}
                          {!hasRealData&&<div style={{fontSize:11,color:"var(--ink-soft)",marginTop:6}}><Icon n="warning" size="1.1em" inline /> Sync Settlements to verify exact amounts</div>}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ── Morning / Evening payout shifts — settled-but-unpaid orders, side by side.
                       Driven by order state (rzp_settled && !payout_id), so it never resets at
                       midnight and works under any settlement frequency (once or many per day). ── */}
                {(()=>{
                  const sup = orders.filter(o => o.status==="confirmed" && o.rzp_settled && !o.payout_id);
                  if (sup.length===0) return null;
                  const groups = { night:{}, day:{} };
                  for (const o of sup) {
                    const g = groups[orderShift(o)];
                    if (!g[o.store_id]) {
                      const si = storeById.get(o.store_id);
                      g[o.store_id] = { store_id:o.store_id, name:si?.store_name||o.store_id, upi:si?.upi_id||null, phone:si?.phone||null, orders:[], net:0, gross:0, bloom:0 };
                    }
                    g[o.store_id].orders.push(o);
                    g[o.store_id].net   += net(o);
                    g[o.store_id].gross += gross(o);
                    g[o.store_id].bloom += Number(o.platform_fee||0);
                  }
                  const keyOf  = (shk, sg) => `${shk}:${sg.store_id}`;
                  const rowsOf = (shk) => Object.values(groups[shk]).sort((a,b)=>b.net-a.net);
                  const toggle = (k) => setSelectedPayouts(prev => { const n=new Set(prev); n.has(k)?n.delete(k):n.add(k); return n; });
                  const toggleAll = (shk, on) => setSelectedPayouts(prev => { const n=new Set(prev); rowsOf(shk).forEach(sg=>{ const k=keyOf(shk,sg); on?n.add(k):n.delete(k); }); return n; });
                  // Pay a list of store-groups (bulk): one payout record + mark-paid-ids per STORE.
                  // A store can appear in BOTH the night and day shift — merge those groups by
                  // store_id first so it becomes ONE payout for the COMBINED amount and the seller
                  // gets a single WhatsApp for the full sum (not one message per shift).
                  const payGroups = (list) => {
                    if (!list.length) return;
                    const byStore = {};
                    for (const sg of list) {
                      const k = sg.store_id;
                      if (!byStore[k]) byStore[k] = { store_id: sg.store_id, name: sg.name, net: 0, orders: [] };
                      byStore[k].net += sg.net;
                      byStore[k].orders.push(...sg.orders);
                    }
                    const merged = Object.values(byStore);
                    const totalNet = merged.reduce((s,sg)=>s+sg.net,0);
                    setConfirmDlg({
                      title:`Mark ${merged.length} store${merged.length!==1?"s":""} paid`,
                      msg:`Total ₹${totalNet.toLocaleString("en-IN")} to ${merged.length} store${merged.length!==1?"s":""}. This records the payout and clears them from the list.`,
                      danger:false, okLabel:`Mark ₹${totalNet.toLocaleString("en-IN")} Paid`,
                      onOk: async () => {
                        setConfirmDlg(null);
                        let ok=0, fail=0;
                        for (const sg of merged) {
                          const payoutId=`pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
                          const ids = sg.orders.map(o=>o.id);
                          const rec = { id:payoutId, store_id:sg.store_id, amount:sg.net, note:`Payout — ${sg.name}`, paid_at:new Date().toISOString() };
                          const saved=await api.insertPayout(rec);
                          if(saved===null||saved?.__payoutError){fail++;continue;}
                          ok++;
                          setPayouts(prev=>prev.some(p=>p.id===rec.id)?prev:[...prev,rec]);
                          fetch(`${BACKEND_URL}/api/admin/orders/mark-paid-ids`,{method:"PATCH",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:JSON.stringify({order_ids:ids,payoutId})}).catch(()=>{});
                          const idset=new Set(ids);
                          setOrders(prev=>prev.map(o=>idset.has(o.id)?{...o,payout_id:payoutId}:o));
                        }
                        setSelectedPayouts(new Set());
                        if(fail>0) alert(`${ok} paid, ${fail} failed. Re-check and retry the failed ones.`);
                      }
                    });
                  };
                  const _th  = {textAlign:"left",padding:"4px 8px 6px 0",fontSize:10,fontWeight:700,letterSpacing:"0.05em",color:"var(--ink-soft)",whiteSpace:"nowrap"};
                  const _thR = {..._th, textAlign:"right"};
                  const _td  = {padding:"7px 8px 7px 0",fontSize:12,whiteSpace:"nowrap"};
                  const _tdR = {..._td, textAlign:"right"};
                  const shifts=[
                    {key:"night",icon:"night",title:"Night shift",window:"6 PM → 6 AM",pay:"pay in the morning"},
                    {key:"day",icon:"day",title:"Day shift",window:"6 AM → 6 PM",pay:"pay in the evening"},
                  ];
                  const renderCol=(sh)=>{
                    const rows=rowsOf(sh.key);
                    const total=rows.reduce((s,r)=>s+r.net,0);
                    const bloomTot=rows.reduce((s,r)=>s+r.bloom,0);
                    const count=rows.reduce((s,r)=>s+r.orders.length,0);
                    const allChecked=rows.length>0&&rows.every(sg=>selectedPayouts.has(keyOf(sh.key,sg)));
                    return (
                      <div key={sh.key} style={{flex:"1 1 440px",minWidth:0,background:"#fff",border:"1px solid var(--border)",borderRadius:4,padding:14}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2,flexWrap:"wrap"}}>
                          <span style={{fontSize:16,display:"inline-flex"}}><Icon n={sh.icon} size={16} /></span>
                          <strong style={{fontSize:14}}>{sh.title}</strong>
                          <span style={{fontSize:10,color:"var(--ink-soft)",background:"var(--surface)",borderRadius:4,padding:"2px 7px",fontWeight:700}}>{sh.window}</span>
                        </div>
                        <div style={{fontSize:11,color:"var(--ink-soft)",marginBottom:10}}>Orders placed {sh.window} IST · you {sh.pay}</div>
                        {rows.length===0 ? (
                          <div style={{fontSize:12,color:"var(--ink-soft)",padding:"14px 0",textAlign:"center"}}>✓ Nothing to pay in this shift</div>
                        ) : (
                          <>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                              <span style={{fontSize:12,color:"var(--ink-soft)"}}>{rows.length} store{rows.length!==1?"s":""} · {count} order{count!==1?"s":""} · Bloom ₹{bloomTot.toLocaleString("en-IN")}</span>
                              <button onClick={()=>payGroups(rows)} style={{background:"var(--green)",color:"#fff",border:"none",borderRadius:4,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>Pay all · ₹{total.toLocaleString("en-IN")}</button>
                            </div>
                            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                              <table style={{width:"100%",borderCollapse:"collapse",minWidth:440}}>
                                <thead>
                                  <tr style={{borderBottom:"1.5px solid var(--border)"}}>
                                    <th style={{..._th,width:24,paddingRight:0}}><input type="checkbox" checked={allChecked} onChange={e=>toggleAll(sh.key,e.target.checked)} style={{cursor:"pointer",width:14,height:14}}/></th>
                                    <th style={_th}>STORE</th>
                                    <th style={_th}>UPI</th>
                                    <th style={_th}>PHONE</th>
                                    <th style={_thR}>ORD</th>
                                    <th style={_thR}>GROSS</th>
                                    <th style={{..._thR,color:"var(--teal-deep)"}}>BLOOM</th>
                                    <th style={{..._thR,color:"var(--green)"}}>→ PAY OUT</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map(sg=>{
                                    const k=keyOf(sh.key,sg);
                                    const checked=selectedPayouts.has(k);
                                    return (
                                      <tr key={sg.store_id} style={{borderBottom:"1px solid var(--border)",background:checked?"rgba(47,99,221,0.06)":"transparent"}}>
                                        <td style={{..._td,paddingRight:0}}><input type="checkbox" checked={checked} onChange={()=>toggle(k)} style={{cursor:"pointer",width:14,height:14}}/></td>
                                        <td style={{..._td,fontWeight:600,maxWidth:130,overflow:"hidden",textOverflow:"ellipsis"}}>{sg.name}</td>
                                        <td style={_td}>{sg.upi ? <code style={{fontSize:11,background:"var(--surface)",padding:"1px 5px",borderRadius:4}}>{sg.upi}</code> : <span style={{color:"var(--red)",fontSize:11}}><Icon n="warning" size="1.1em" inline /> none</span>}</td>
                                        <td style={{..._td,color:"var(--ink-soft)"}}>{sg.phone||"—"}</td>
                                        <td style={{..._tdR,color:"var(--ink-soft)"}}>{sg.orders.length}</td>
                                        <td style={{..._tdR,color:"var(--ink-soft)"}}>₹{sg.gross.toLocaleString("en-IN")}</td>
                                        <td style={{..._tdR,color:"var(--teal-deep)",fontWeight:600}}>₹{sg.bloom.toLocaleString("en-IN")}</td>
                                        <td style={{..._tdR,fontWeight:700,color:"var(--green)"}}>₹{sg.net.toLocaleString("en-IN")}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  };
                  const allRows=Object.values(groups).flatMap(g=>Object.values(g));
                  const grand=allRows.reduce((s,r)=>s+r.net,0);
                  const grandGross=allRows.reduce((s,r)=>s+r.gross,0);
                  const grandBloom=allRows.reduce((s,r)=>s+r.bloom,0);
                  const grandOrders=allRows.reduce((s,r)=>s+r.orders.length,0);
                  const uniqueStores=new Set(allRows.map(r=>r.store_id)).size;   // a store that is in BOTH shifts counts once
                  const allSel=shifts.flatMap(sh=>rowsOf(sh.key).filter(sg=>selectedPayouts.has(keyOf(sh.key,sg))));
                  const selNet=allSel.reduce((s,sg)=>s+sg.net,0);
                  const selBloom=allSel.reduce((s,sg)=>s+sg.bloom,0);
                  return (
                    <div style={{marginTop:6,marginBottom:4}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                        <strong style={{fontSize:14}}><Icon n="rupee" size="1.1em" inline /> Ready to pay — Morning &amp; Evening</strong>
                        <span style={{fontSize:11,color:"var(--ink-soft)"}}>settled by Razorpay, not yet paid · tick stores then Mark Paid</span>
                      </div>
                      <div style={{display:"flex",gap:18,marginBottom:12,flexWrap:"wrap",alignItems:"center",fontSize:12,color:"var(--ink-soft)",padding:"9px 14px",background:"var(--surface)",borderRadius:4}}>
                        <span>{allRows.length} store{allRows.length!==1?"s":""} · {grandOrders} order{grandOrders!==1?"s":""} · <strong style={{color:"var(--ink)"}}>{uniqueStores} unique store{uniqueStores!==1?"s":""}</strong>{uniqueStores!==allRows.length&&<span style={{fontSize:10.5}}> ({allRows.length-uniqueStores} in both shifts)</span>}</span>
                        <span>Gross: <strong style={{color:"var(--ink)"}}>₹{grandGross.toLocaleString("en-IN")}</strong></span>
                        <span><BloomMark /> Bloom keeps: <strong style={{color:"var(--teal-deep)"}}>₹{grandBloom.toLocaleString("en-IN")}</strong></span>
                        <span style={{marginLeft:"auto"}}>Pay out to sellers: <strong style={{color:"var(--green)",fontSize:14}}>₹{grand.toLocaleString("en-IN")}</strong></span>
                      </div>
                      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-start"}}>
                        {shifts.map(renderCol)}
                      </div>
                      {allSel.length>0 && (
                        <div style={{marginTop:12,padding:"10px 14px",background:"var(--teal-light)",border:"1.5px solid var(--teal)",borderRadius:4,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",position:"sticky",bottom:8}}>
                          <span style={{fontSize:13,fontWeight:700,color:"var(--teal-deep)"}}>{allSel.length} store{allSel.length!==1?"s":""} selected</span>
                          <span style={{fontSize:12,color:"var(--ink-soft)"}}>Bloom earns ₹{selBloom.toLocaleString("en-IN")}</span>
                          <span style={{fontSize:15,fontWeight:700,color:"var(--green)"}}>₹{selNet.toLocaleString("en-IN")} payout</span>
                          <button className="btn-green" style={{padding:"7px 16px",fontSize:13,borderRadius:4,fontWeight:700,whiteSpace:"nowrap"}} onClick={()=>payGroups(allSel)}>✓ Mark Paid</button>
                          <button onClick={()=>setSelectedPayouts(new Set())} style={{background:"none",border:"1px solid var(--teal)",borderRadius:4,padding:"5px 10px",fontSize:12,fontWeight:600,cursor:"pointer",color:"var(--teal-deep)"}}>Clear</button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {isWeekend&&!hasRealData&&(
                  <div style={{marginBottom:12,background:"#FFF8E1",border:"1px solid #FFD54F",borderRadius:4,padding:"10px 14px"}}>
                    <p style={{fontSize:12,fontWeight:700,color:"#B8860B",marginBottom:4}}><Icon n="bank" size="1.1em" inline /> Weekend ({dayName})</p>
                    <p style={{fontSize:12,color:"var(--ink-soft)"}}>Settlement timing can differ on weekends/holidays — check Razorpay → Settlements for when today's deposit lands.</p>
                  </div>
                )}

                {/* Before Razorpay creates the settlement record */}
                {!hasRealData&&!isWeekend&&(
                  <div style={{padding:"14px 16px",background:"#F8F8F8",borderRadius:4,marginBottom:4}}>
                    <div style={{fontSize:13,color:"var(--ink)",marginBottom:10,lineHeight:1.6}}>
                      <Icon n="clock" size={13} inline /> <strong>Same-day settlement</strong> — Razorpay deposits to your bank on your account's same-day schedule (see Razorpay → Settlements for exact slots).
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      <div style={{display:"flex",gap:10,alignItems:"flex-start",fontSize:12,color:"var(--ink-soft)"}}>
                        <span style={{fontWeight:700,color:"var(--teal-deep)",flexShrink:0}}>Step 1</span>
                        <span>Wait for the same-day settlement to reach your bank</span>
                      </div>
                      <div style={{display:"flex",gap:10,alignItems:"flex-start",fontSize:12,color:"var(--ink-soft)"}}>
                        <span style={{fontWeight:700,color:"var(--teal-deep)",flexShrink:0}}>Step 2</span>
                        <span>Click <strong style={{color:"var(--ink)"}}>Sync Settlements</strong> — this matches Razorpay's data to each store's orders</span>
                      </div>
                      <div style={{display:"flex",gap:10,alignItems:"flex-start",fontSize:12,color:"var(--ink-soft)"}}>
                        <span style={{fontWeight:700,color:"var(--teal-deep)",flexShrink:0}}>Step 3</span>
                        <span>The <strong style={{color:"var(--ink)"}}>Pending Payouts</strong> section below will show exactly which store to pay and how much — with a Mark Paid button</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pre-sync estimate — when we don't yet have real Razorpay data */}
                {!hasRealData&&todayData.expected_today.length>0&&(
                  <div style={{marginBottom:12}}>
                    <p style={{fontSize:11,fontWeight:700,color:"var(--teal-deep)",letterSpacing:"0.07em",marginBottom:8}}>ORDERS MATCHED TO TODAY (ESTIMATE)</p>
                    {(()=>{
                      const byStore={};
                      for(const o of todayData.expected_today){
                        if(!byStore[o.store_id])byStore[o.store_id]={orders:[],gross:0,name:storeById.get(o.store_id)?.store_name||o.store_id};
                        byStore[o.store_id].orders.push(o);
                        byStore[o.store_id].gross+=Number(o.amount||0);
                      }
                      return <>
                        {Object.values(byStore).map((sg,i)=>{
                          const est=sg.gross*1.0;
                          return <div key={i} style={{fontSize:13,color:"var(--ink)",marginBottom:4}}>
                            ~₹{est.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})} from <strong>{sg.name}</strong> ({sg.orders.length} order{sg.orders.length!==1?"s":""})
                          </div>;
                        })}
                        <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:6}}><Icon n="warning" size="1.1em" inline /> Sync Settlements to get exact amounts</div>
                      </>;
                    })()}
                  </div>
                )}

                {todayData.settled_today.length>0&&(
                  <div>
                    <p style={{fontSize:11,fontWeight:700,color:"var(--green)",letterSpacing:"0.07em",marginBottom:6}}>ALREADY SYNCED TODAY</p>
                    <p style={{fontSize:13,color:"var(--ink)"}}>
                      {(()=>{
                        const settledGross=todayData.settled_today.reduce((s,o)=>s+Number(o.amount||0),0);
                        const expectedGross=(todayData.expected_today||[]).reduce((s,o)=>s+Number(o.amount||0),0);
                        const rawF=hasRealData&&expectedGross>0?todayData.rzp_today_amount/expectedGross:1.0;
                        const factor=(rawF>1||rawF<=0)?1.0:rawF;
                        const net=settledGross*factor;
                        return `₹${net.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
                      })()} · {todayData.settled_today.length} orders
                    </p>
                  </div>
                )}
              </div>
              );
            })()}


            {/* PENDING SECTION */}
            <h3 style={{fontSize:18,marginBottom:14}}>Pending Payouts</h3>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {stores
                .filter(store=>{
                  if(!hasAnySynced) return outstanding(store.id)>0;
                  // Show if has unpaid settled batches (money received, not yet paid to store)
                  const storeBatches=settlementBatches(store.id);
                  const storePayouts=(payoutsByStore.get(store.id)||[]);
                  const hasUnpaidBatch=storeBatches.some(batch=>!storePayouts.some(p=>Math.abs(Number(p.amount)-batch.amount)<=2));
                  // OR show if has unsettled orders sitting in Razorpay pipeline
                  const hasPending=pendingFromRzp(store.id)>0;
                  return hasUnpaidBatch||hasPending;
                })
                .filter(store=>{
                  if(!payoutSearch) return true;
                  const q=payoutSearch.toLowerCase();
                  return (store.store_name||"").toLowerCase().includes(q)||(store.email||"").toLowerCase().includes(q);
                })
                .map(store=>{
                  const sb      = settledBalance(store.id);
                  const pending = pendingFromRzp(store.id);
                  const batches = settlementBatches(store.id);
                  const owed    = outstanding(store.id);
                  const bloomFee = hasAnySynced ? settledFee(store.id) : outstandingFee(store.id);
                  return (
                    <div key={store.id} className="card" style={{overflow:"hidden"}}>
                      <div style={{padding:isMobile?16:22}}>
                        <div style={{display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
                          <div style={{flex:1,minWidth:180}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
                              <span style={{fontWeight:700,fontSize:16}}>{store.store_name}</span>
                              {outstanding(store.id)>0&&<span className="bdg-yellow"><Icon n="pending" size="1.1em" inline /> Payout due</span>}
                            </div>
                            <p style={{fontSize:13,color:"var(--ink-soft)",marginBottom:3}}><Icon n="email" size={13} inline /> {store.email}</p>
                            <p style={{fontSize:12,color:"var(--ink-soft)"}}>
                              UPI: <code style={{background:"var(--surface)",padding:"1px 6px",borderRadius:4,fontSize:11}}>{store.upi_id||"—"}</code>
                            </p>
                          </div>
                          <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
                            {hasAnySynced&&sb>0&&(
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--green)"}}>PAY SELLER (post-fees)</div>
                                <div style={{fontSize:20,fontWeight:700,color:"var(--green)"}}>₹{sb.toLocaleString()}</div>
                              </div>
                            )}
                            {bloomFee>0&&(
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--teal-deep)"}}><BloomMark /> BLOOM EARNS</div>
                                <div style={{fontSize:20,fontWeight:700,color:"var(--teal-deep)"}}>₹{bloomFee.toLocaleString("en-IN")}</div>
                              </div>
                            )}
                            {!hasAnySynced&&owed>0&&(
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>OWED</div>
                                <div style={{fontSize:20,fontWeight:700,color:"var(--pink-btn)"}}>₹{owed.toLocaleString()}</div>
                              </div>
                            )}
                            {hasAnySynced&&pending>0&&(
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--teal-deep)"}}>~EST. UNSETTLED</div>
                                <div style={{fontSize:18,fontWeight:700,color:"var(--teal)"}}>₹{pending.toLocaleString()}</div>
                              </div>
                            )}
                            <button onClick={()=>setExpandedStore(expandedStore===store.id?null:store.id)}
                              style={{background:"var(--blue)",color:"var(--ink)",border:"none",borderRadius:4,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                              {expandedStore===store.id?"▲ Hide":<><Icon n="copy" size="1.1em" inline />{" Details"}</>}
                            </button>
                            {!hasAnySynced&&owed>0&&(
                              <button className="btn-green" style={{padding:"8px 14px",fontSize:12,borderRadius:4}} onClick={()=>markAsPaid(store)}>
                                ✓ Mark Paid
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Settlement batches — always visible */}
                      {hasAnySynced&&batches.length>0&&(
                        <div style={{borderTop:"1px solid var(--border)",background:"var(--surface)",padding:isMobile?"12px 16px":"14px 20px"}}>
                          <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",marginBottom:10}}>SETTLED BY DATE — PAY OUT EACH BATCH</p>
                              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                                {batches.map((batch,bi)=>{
                                  // Use ±2 tolerance: rounding differences (e.g. 10433 vs 10434) still match
                                  const batchAlreadyPaid=payouts.some(p=>p.store_id===store.id&&Math.abs(Number(p.amount)-batch.amount)<=2);
                                  return (
                                  <div key={bi} style={{background:batchAlreadyPaid?"rgba(6,118,71,0.06)":"#fff",borderRadius:4,padding:"12px 16px",border:batchAlreadyPaid?"1.5px solid rgba(6,118,71,0.3)":"1.5px solid transparent"}}>
                                    <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap",marginBottom:batch.orders.length>0?10:0}}>
                                      <div style={{flex:1,minWidth:150}}>
                                        <div style={{fontWeight:700,fontSize:13,color:"var(--green)",marginBottom:3}}>Settled {batch.dateFormatted}</div>
                                        <div style={{fontSize:12,color:"var(--ink-soft)"}}>{batch.orders.length} order{batch.orders.length!==1?"s":""}</div>
                                      </div>
                                      <div style={{fontWeight:700,fontSize:18,color:"var(--green)"}}>₹{batch.amount.toLocaleString()}</div>
                                      {batchAlreadyPaid
                                        ? <span style={{fontSize:12,fontWeight:700,color:"var(--green)",background:"rgba(6,118,71,0.15)",borderRadius:4,padding:"7px 14px",letterSpacing:"0.03em"}}>✓ Paid</span>
                                        : <button className="btn-green" style={{padding:"7px 14px",fontSize:12,borderRadius:4}} onClick={()=>markPaidBatch(store,batch)}>
                                            ✓ Mark Paid
                                          </button>
                                      }
                                    </div>
                                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                                      {batch.orders.map(o=>(
                                        <div key={o.id} style={{display:"flex",gap:10,alignItems:"center",background:"var(--surface)",borderRadius:4,padding:"7px 10px",fontSize:12}}>
                                          <span style={{flex:1,color:"var(--ink)"}}>{o.customer_name||"Customer"}</span>
                                          <span style={{color:"var(--ink-soft)"}}>Ordered {o.order_date||o.date||"—"}</span>
                                          <span style={{fontWeight:700,color:"var(--pink-btn)"}}>₹{gross(o).toLocaleString()}</span>
                                          {Number(o.tsf||0)>0&&<span style={{color:"var(--ink-soft)",fontSize:10}}>({`+₹${o.tsf} fee`})</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                        </div>
                      )}

                      {/* Expanded details */}
                      {expandedStore===store.id&&(
                        <div style={{borderTop:"1px solid var(--border)",background:"var(--surface)",padding:isMobile?14:20}}>
                          {/* Arriving soon sub-section */}
                          {hasAnySynced&&unsettledOrders(store.id).length>0&&(
                            <div style={{marginBottom:8}}>
                              <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--teal-deep)",marginBottom:8}}>~EST. UNSETTLED (NOT YET SETTLED BY RAZORPAY)</p>
                              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                                {unsettledOrders(store.id).slice(0,5).map(o=>(
                                  <div key={o.id} style={{background:"#fff",borderRadius:4,padding:"10px 14px",display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                                    <div style={{flex:1,fontSize:13,color:"var(--ink)"}}>
                                      {o.customer_name||"Customer"} — {o.order_date||o.date||"—"}
                                    </div>
                                    <div style={{fontWeight:700,fontSize:14,color:"var(--teal)"}}>₹{gross(o).toLocaleString()}</div>
                                  </div>
                                ))}
                                {unsettledOrders(store.id).length>5&&<p style={{fontSize:12,color:"var(--ink-soft)"}}>+{unsettledOrders(store.id).length-5} more…</p>}
                              </div>
                            </div>
                          )}

                          {/* All orders — compact table */}
                          <div>
                            <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",marginBottom:8}}>ALL ORDERS — {store.store_name}</p>
                            {storeOrders(store.id).length===0
                              ? <p style={{fontSize:13,color:"var(--ink-soft)",textAlign:"center",padding:"16px 0"}}>No confirmed orders yet.</p>
                              : <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",marginRight:isMobile?-14:0}}>
                              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:460}}>
                                  <thead>
                                    <tr style={{borderBottom:"1.5px solid var(--border)"}}>
                                      <th style={{textAlign:"left",padding:"4px 8px 6px 0",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>DATE</th>
                                      <th style={{textAlign:"left",padding:"4px 8px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>CUSTOMER</th>
                                      <th style={{textAlign:"left",padding:"4px 8px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>ITEMS</th>
                                      <th style={{textAlign:"right",padding:"4px 8px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>AMOUNT</th>
                                      <th style={{textAlign:"center",padding:"4px 0 6px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>STATUS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...storeOrders(store.id)].sort((a,b)=>new Date(b.order_date||b.date||0)-new Date(a.order_date||a.date||0)).map(o=>{
                                      const custName=o.customer_name||customerById.get(o.customer_id)?.name||"Customer";
                                      const items=Array.isArray(o.items)?o.items.map(i=>`${i.name}${i.qty>1?` ×${i.qty}`:""}`).join(", "):o.productLabel||"—";
                                      return (
                                        <tr key={o.id} style={{borderBottom:"1px solid var(--border)",verticalAlign:"top"}}>
                                          <td style={{padding:"8px 8px 8px 0",color:"var(--ink-soft)",whiteSpace:"nowrap"}}>{o.order_date||o.date||"—"}</td>
                                          <td style={{padding:"8px"}}>
                                            <div style={{fontWeight:600}}>{custName}</div>
                                            {o.customer_phone&&<div style={{fontSize:11,color:"var(--ink-soft)"}}>{o.customer_phone}</div>}
                                            {o.delivery_address&&<div style={{fontSize:11,color:"var(--ink-soft)",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={o.delivery_address}><Icon n="location" size={11} inline /> {o.delivery_address}</div>}
                                          </td>
                                          <td style={{padding:"8px",color:"var(--ink-soft)",maxWidth:200}}>{items}</td>
                                          <td style={{padding:"8px",textAlign:"right",fontWeight:700,color:"var(--pink-btn)",whiteSpace:"nowrap"}}>
                                            ₹{gross(o).toLocaleString()}
                                            {Number(o.tsf||0)>0&&<div style={{fontSize:9,color:"var(--ink-soft)",fontWeight:400}}>you get ₹{Number(o.amount).toLocaleString()}</div>}
                                          </td>
                                          <td style={{padding:"8px 0 8px 8px",textAlign:"center",whiteSpace:"nowrap"}}>
                                            {o.rzp_settled&&o.payout_id
                                              ? <span style={{fontSize:10,background:"#D1FAE5",color:"#065F46",borderRadius:4,padding:"2px 7px",fontWeight:700}}>✓ Paid</span>
                                              : o.rzp_settled
                                              ? <span style={{fontSize:10,background:"#ECFDF5",color:"var(--green)",borderRadius:4,padding:"2px 7px",fontWeight:700}}><Icon n="instant" size="1.1em" inline /> Settled</span>
                                              : <span style={{fontSize:10,background:"#FEF9C3",color:"#92400E",borderRadius:4,padding:"2px 7px",fontWeight:700}}><Icon n="pending" size="1.1em" inline /> Pending</span>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            }
                          </div>
                        </div>
                      )}

                      {/* Per-store payout history strip */}
                      {(payoutsByStore.get(store.id)||[]).length>0&&(
                        <div style={{borderTop:"1px solid var(--border)",background:"#F0FDF4",padding:"12px 24px"}}>
                          <p style={{fontSize:10,fontWeight:700,letterSpacing:"0.07em",color:"var(--green)",marginBottom:8}}>PAYOUT HISTORY</p>
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {(payoutsByStore.get(store.id)||[]).map(p=>(
                              <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,background:"#fff",borderRadius:4,padding:"8px 12px",fontSize:13}}>
                                <span style={{fontWeight:700,color:"var(--green)",flexShrink:0}}>₹{Number(p.amount).toLocaleString()}</span>
                                <span style={{flex:1,color:"var(--ink-soft)",fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {new Date(p.paid_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                                  {p.note?` · ${p.note}`:""}
                                </span>
                                <button onClick={()=>deletePayouts(new Set([p.id]))}
                                  style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"4px 9px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                                  <Icon n="delete" size={12} inline />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              {!stores.some(store=>outstanding(store.id)>0)&&(
                <div style={{textAlign:"center",padding:"40px 0",color:"var(--ink-soft)"}}>
                  <div style={{fontSize:48,marginBottom:12}}><Icon n="success" size={48} inline /></div>
                  <p style={{fontSize:14}}>All stores are paid up!</p>
                </div>
              )}
            </div>

            {/* PAID section */}
            {stores.some(store=>{
              const earned=totalEarned(store.id);
              return earned>0&&outstanding(store.id)<=0&&totalPaidOut(store.id)>0;
            })&&(
              <div style={{marginTop:36}}>
                <h3 style={{fontSize:18,marginBottom:14}}>Paid Stores</h3>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {stores
                    .filter(store=>{
                      const earned=totalEarned(store.id);
                      return earned>0&&outstanding(store.id)<=0&&totalPaidOut(store.id)>0;
                    })
                    .filter(store=>{
                      if(!payoutSearch) return true;
                      const q=payoutSearch.toLowerCase();
                      return (store.store_name||"").toLowerCase().includes(q)||(store.email||"").toLowerCase().includes(q);
                    })
                    .map(store=>{
                      const storePayouts=(payoutsByStore.get(store.id)||[]);
                      return (
                        <div key={store.id} className="card" style={{padding:"14px 20px"}}>
                          <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                            <div style={{flex:1}}>
                              <span style={{fontWeight:700,fontSize:15}}>{store.store_name}</span>
                              <span className="bdg-green" style={{marginLeft:8,fontSize:10}}>✓ All settled</span>
                              <p style={{fontSize:12,color:"var(--ink-soft)",marginTop:3}}>{store.email}</p>
                            </div>
                            <div style={{fontSize:14,fontWeight:700,color:"var(--green)"}}>
                              ₹{storePayouts.reduce((s,p)=>s+Number(p.amount||0),0).toLocaleString()} paid out
                            </div>
                            <button onClick={()=>setExpandedStore(expandedStore===store.id?null:store.id)}
                              style={{background:"var(--surface)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                              {expandedStore===store.id?"▲ Hide":<><Icon n="copy" size="1.1em" inline />{" History"}</>}
                            </button>
                          </div>
                          {expandedStore===store.id&&storePayouts.length>0&&(
                            <div style={{marginTop:12,borderTop:"1px solid var(--border)",paddingTop:12,display:"flex",flexDirection:"column",gap:6}}>
                              {storePayouts.map(p=>(
                                <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,background:"var(--surface)",borderRadius:4,padding:"8px 12px",fontSize:13}}>
                                  <span style={{fontWeight:700,color:"var(--green)",flexShrink:0}}>₹{Number(p.amount).toLocaleString()}</span>
                                  <span style={{flex:1,color:"var(--ink-soft)",fontSize:12}}>
                                    {new Date(p.paid_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                                    {p.note?` · ${p.note}`:""}
                                  </span>
                                  <button onClick={()=>deletePayouts(new Set([p.id]))}
                                    style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"4px 9px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                                    <Icon n="delete" size={12} inline />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

        ) : tab==="transactions" ? (
          /* ── TRANSACTIONS TAB ── */
          <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
              <h2 style={{fontSize:isMobile?22:26,margin:0}}><Icon n="card" size="1.1em" inline /> Razorpay Settlements</h2>
              <button onClick={async()=>{
                if(txSettlements!==null) { setTxSettlements(null); return; }
                try {
                  const r=await fetch(`${BACKEND_URL}/api/admin/settlements`,{headers:{"x-admin-token":ADMIN_ID}});
                  const d=await r.json();
                  setTxSettlements(d.items||[]);
                } catch(e){ setTxSettlements([]); alert("Failed: "+e.message); }
              }} style={{background:"var(--teal)",color:"#fff",border:"none",borderRadius:4,padding:"9px 16px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                {txSettlements===null?"Load Settlements":"↺ Refresh"}
              </button>
            </div>

            {txSettlements===null&&(
              <div style={{textAlign:"center",padding:"48px 0",color:"var(--ink-soft)"}}>
                <div style={{fontSize:40,marginBottom:12}}><Icon n="card" size={40} inline /></div>
                <p style={{fontSize:14}}>Click "Load Settlements" to fetch settlement history from Razorpay</p>
              </div>
            )}

            {txSettlements!==null&&txSettlements.length===0&&(
              <div style={{textAlign:"center",padding:"48px 0",color:"var(--ink-soft)"}}>
                <p>No settlements found</p>
              </div>
            )}

            {txSettlements!==null&&txSettlements.length>0&&(
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {txSettlements.map(s=>{
                  const netAmt=(s.amount||0)/100;
                  const isOpen=txExpanded===s.id;
                  const detail=txDetail[s.id];
                  const isLoadingDetail=txLoadingDetail===s.id;
                  const settled=new Date(s.settled_on*1000).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"});
                  return (
                    <div key={s.id} className="card" style={{overflow:"hidden"}}>
                      {/* Settlement header row */}
                      <div style={{padding:"16px 20px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",cursor:"pointer"}}
                        onClick={async()=>{
                          if(isOpen){ setTxExpanded(null); return; }
                          setTxExpanded(s.id);
                          if(!txDetail[s.id]){
                            setTxLoadingDetail(s.id);
                            try {
                              const r=await fetch(`${BACKEND_URL}/api/admin/settlement/${s.id}`,{headers:{"x-admin-token":ADMIN_ID}});
                              const d=await r.json();
                              setTxDetail(prev=>({...prev,[s.id]:d}));
                            } catch(e){ setTxDetail(prev=>({...prev,[s.id]:{error:e.message}})); }
                            setTxLoadingDetail(null);
                          }
                        }}>
                        <div style={{flex:1,minWidth:160}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                            <span style={{fontWeight:700,fontSize:15}}>₹{netAmt.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                            <span style={{fontSize:10,padding:"2px 7px",borderRadius:4,background:s.status==="processed"?"var(--green-bg)":"var(--teal-light)",color:s.status==="processed"?"var(--green)":"var(--teal-deep)",fontWeight:700}}>{s.status}</span>
                          </div>
                          <div style={{fontSize:12,color:"var(--ink-soft)"}}>
                            {settled}&nbsp;·&nbsp;<code style={{fontFamily:"monospace",fontSize:11,background:"var(--surface)",padding:"1px 5px",borderRadius:4}}>{s.id}</code>
                          </div>
                        </div>
                        <span style={{fontSize:18,color:"var(--ink-soft)",transform:isOpen?"rotate(180deg)":"none",transition:"transform 0.2s"}}>▼</span>
                      </div>

                      {/* Expanded detail */}
                      {isOpen&&(
                        <div style={{borderTop:"1px solid var(--border)",padding:"16px 20px"}}>
                          {isLoadingDetail&&<div style={{textAlign:"center",padding:"20px 0",color:"var(--ink-soft)"}}>Loading…</div>}
                          {detail?.error&&<p style={{color:"var(--red)",fontSize:13}}>{detail.error}</p>}
                          {detail&&!detail.error&&(()=>{
                            const gross=(detail.settlement?.gross_amount||0)/100;
                            const deductions=(detail.settlement?.deductions||0)/100;
                            const fee=(detail.settlement?.fee||0)/100;
                            const tax=(detail.settlement?.tax||0)/100;
                            const net=(detail.settlement?.amount||0)/100;
                            const payments=detail.payments||[];
                            return (
                              <div>
                                {/* Amount breakdown */}
                                <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                                  {[
                                    {label:"Gross",val:gross,color:"var(--ink)"},
                                    {label:"Fee",val:fee,color:"var(--red)",prefix:"−"},
                                    {label:"GST on fee",val:tax,color:"var(--red)",prefix:"−"},
                                    {label:"Net received",val:net,color:"var(--green)",bold:true},
                                  ].map(item=>(
                                    <div key={item.label} style={{padding:"8px 14px",background:"var(--surface)",borderRadius:4,minWidth:110}}>
                                      <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",marginBottom:3}}>{item.label.toUpperCase()}</div>
                                      <div style={{fontSize:15,fontWeight:item.bold?700:600,color:item.color}}>
                                        {item.prefix||""}₹{item.val.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Per-store payout table — derived from recon payments + orderMap */}
                                {(()=>{
                                  const orderMap=detail.orderMap||{};
                                  const byStore={};
                                  for(const pay of payments){
                                    const ord=orderMap[pay.payment_id];
                                    if(!ord) continue;
                                    const sid=ord.store_id||"unknown";
                                    if(!byStore[sid]) byStore[sid]={name:ord.store_name||sid,count:0,gross:0};
                                    byStore[sid].count++;
                                    byStore[sid].gross+=(pay.gross_amount||0)/100;
                                  }
                                  const rows=Object.entries(byStore);
                                  if(rows.length===0) return null;
                                  const factor=gross>0?net/gross:1.0;
                                  return (
                                    <div style={{marginBottom:16}}>
                                      <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--teal-deep)",marginBottom:8}}>STORE PAYOUTS (ACTUAL FACTOR: {factor.toFixed(6)})</p>
                                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                                        <thead>
                                          <tr style={{borderBottom:"1.5px solid var(--border)"}}>
                                            <th style={{textAlign:"left",padding:"4px 8px 6px 0",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>STORE</th>
                                            <th style={{textAlign:"right",padding:"4px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>ORDERS</th>
                                            <th style={{textAlign:"right",padding:"4px 0 6px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)"}}>GROSS</th>
                                            <th style={{textAlign:"right",padding:"4px 0 6px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--green)"}}>PAY OUT</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {rows.map(([sid,sg],i)=>{
                                            const storeNet=sg.gross*factor;
                                            return (
                                              <tr key={i} style={{borderBottom:"1px solid var(--border)"}}>
                                                <td style={{padding:"7px 8px 7px 0",fontWeight:600}}>{sg.name}</td>
                                                <td style={{padding:"7px 8px",textAlign:"right",color:"var(--ink-soft)"}}>{sg.count}</td>
                                                <td style={{padding:"7px 0 7px 8px",textAlign:"right",color:"var(--ink-soft)"}}>₹{sg.gross.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                                <td style={{padding:"7px 0 7px 8px",textAlign:"right",fontWeight:700,color:"var(--green)"}}>₹{storeNet.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                })()}

                                {/* Payments table */}
                                {payments.length>0&&(
                                  <div>
                                    <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",marginBottom:8}}>INDIVIDUAL PAYMENTS ({payments.length})</p>
                                    <div style={{overflowX:"auto"}}>
                                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:600}}>
                                        <thead>
                                          <tr style={{borderBottom:"1.5px solid var(--border)"}}>
                                            {["Date","Payment ID","Customer","Method","Gross","Fee","Net",""].map(h=>(
                                              <th key={h} style={{textAlign:h==="Gross"||h==="Fee"||h==="Net"?"right":"left",padding:"4px 8px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",whiteSpace:"nowrap"}}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {payments.map((p,i)=>{
                                            const pgross=(p.gross_amount||0)/100;
                                            const pfee=(p.debit||0)/100;
                                            const pnet=(p.credit||0)/100;
                                            const pDate=p.created_at?new Date(p.created_at*1000).toLocaleDateString("en-IN",{day:"numeric",month:"short"}):"—";
                                            const pidShort=(p.payment_id||"").slice(0,14)+"…";
                                            const isPayOpen=txExpandedPay===p.payment_id;
                                            const payDetail=txPayDetail[p.payment_id];
                                            const isLoadingPay=txLoadingPay===p.payment_id;
                                            const bloomOrder=(detail.orderMap||{})[p.payment_id]||{};
                                            return (
                                              <Fragment key={p.payment_id||i}>
                                                <tr style={{borderBottom:isPayOpen?"none":"1px solid var(--border)",background:isPayOpen?"var(--teal-light)":"transparent"}}>
                                                  <td style={{padding:"7px 8px",whiteSpace:"nowrap"}}>{pDate}</td>
                                                  <td style={{padding:"7px 8px"}}><code style={{fontFamily:"monospace",fontSize:11,background:"var(--surface)",padding:"1px 5px",borderRadius:4}}>{pidShort}</code></td>
                                                  <td style={{padding:"7px 8px"}}>{bloomOrder.customer_name||"—"}</td>
                                                  <td style={{padding:"7px 8px",color:"var(--ink-soft)"}}>{p.method||"—"}</td>
                                                  <td style={{padding:"7px 8px",textAlign:"right"}}>₹{pgross.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                                  <td style={{padding:"7px 8px",textAlign:"right",color:"var(--red)"}}>−₹{pfee.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                                  <td style={{padding:"7px 8px",textAlign:"right",fontWeight:600,color:"var(--green)"}}>₹{pnet.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                                  <td style={{padding:"7px 8px"}}>
                                                    <button onClick={async()=>{
                                                      if(isPayOpen){ setTxExpandedPay(null); return; }
                                                      setTxExpandedPay(p.payment_id);
                                                      if(!txPayDetail[p.payment_id]){
                                                        setTxLoadingPay(p.payment_id);
                                                        try {
                                                          const r=await fetch(`${BACKEND_URL}/api/admin/payment/${p.payment_id}`,{headers:{"x-admin-token":ADMIN_ID}});
                                                          const d=await r.json();
                                                          setTxPayDetail(prev=>({...prev,[p.payment_id]:d}));
                                                        } catch(e){ setTxPayDetail(prev=>({...prev,[p.payment_id]:{error:e.message}})); }
                                                        setTxLoadingPay(null);
                                                      }
                                                    }} style={{background:"none",border:"1px solid var(--border-mid)",borderRadius:4,padding:"3px 8px",fontSize:11,fontWeight:700,cursor:"pointer",color:"var(--ink-soft)"}}>
                                                      {isPayOpen?"▲":"▼"}
                                                    </button>
                                                  </td>
                                                </tr>
                                                {isPayOpen&&(
                                                  <tr style={{borderBottom:"1px solid var(--border)"}}>
                                                    <td colSpan={8} style={{padding:"8px 12px 12px",background:"var(--teal-light)"}}>
                                                      {isLoadingPay&&<span style={{fontSize:12,color:"var(--ink-soft)"}}>Loading payment details…</span>}
                                                      {payDetail?.error&&<span style={{fontSize:12,color:"var(--red)"}}>{payDetail.error}</span>}
                                                      {payDetail&&!payDetail.error&&(()=>{
                                                        const pd=payDetail||{};
                                                        const bo=bloomOrder;
                                                        return (
                                                          <div style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:12}}>
                                                            {pd.vpa&&<div><span style={{color:"var(--ink-soft)"}}>UPI ID: </span><strong>{pd.vpa}</strong></div>}
                                                            {pd.contact&&<div><span style={{color:"var(--ink-soft)"}}>Phone: </span><strong>{pd.contact}</strong></div>}
                                                            {pd.email&&<div><span style={{color:"var(--ink-soft)"}}>Email: </span><strong>{pd.email}</strong></div>}
                                                            {pd.description&&<div><span style={{color:"var(--ink-soft)"}}>Note: </span><span>{pd.description}</span></div>}
                                                            {bo.id&&<div><span style={{color:"var(--ink-soft)"}}>Bloom ID: </span><code style={{fontFamily:"monospace",background:"var(--surface)",padding:"1px 5px",borderRadius:4}}>{bo.id}</code></div>}
                                                            {bo.store_name&&<div><span style={{color:"var(--ink-soft)"}}>Store: </span><strong>{bo.store_name}</strong></div>}
                                                            <a href={`https://dashboard.razorpay.com/app/payments/${p.payment_id}`} target="_blank" rel="noreferrer"
                                                              style={{color:"var(--teal-deep)",fontSize:12,fontWeight:700,textDecoration:"none"}}>
                                                              ↗ Razorpay Dashboard
                                                            </a>
                                                          </div>
                                                        );
                                                      })()}
                                                    </td>
                                                  </tr>
                                                )}
                                              </Fragment>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        ) : tab==="history" ? (
          /* ── HISTORY TAB ── */
          <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
              <h2 style={{fontSize:isMobile?22:26,margin:0}}><Icon n="document" size="1.1em" inline /> Settlement History</h2>
              <button onClick={async()=>{
                try {
                  // Step 1: fetch fresh today-settlements from backend (get latest expected_today)
                  const fresh = await fetch(`${BACKEND_URL}/api/admin/today-settlements`,{headers:{"x-admin-token":ADMIN_ID}}).then(r=>r.json()).catch(()=>null);
                  if (fresh) setTodayData(fresh);
                  const freshData = fresh || todayData;
                  // Step 2: re-save today's snapshot with fresh data
                  const sid = freshData.rzp_settlements?.[0]?.id;
                  const exp = freshData.expected_today || [];
                  if (sid && freshData.rzp_today_amount && exp.length > 0) {
                    const totalGross = exp.reduce((s,o)=>s+Number(o.amount||0),0);
                    const net = freshData.rzp_today_amount;
                    const rawF = totalGross>0?net/totalGross:1.0;
                    const factor = (rawF>1||rawF<=0)?1.0:rawF;
                    const byStore={};
                    for(const o of exp){
                      const info=storeById.get(o.store_id);
                      if(!byStore[o.store_id])byStore[o.store_id]={store_id:o.store_id,store_name:info?.store_name||o.store_id,upi_id:info?.upi_id||null,phone:info?.phone||null,orders:0,gross:0,postTsfGross:0,payout:0,platformFee:0,paid:false,paid_at:null};
                      byStore[o.store_id].orders++;
                      byStore[o.store_id].gross+=Number(o.amount||0);
                      if(!gwAbsorbed(o)) byStore[o.store_id].postTsfGross+=Number(o.amount||0);
                      byStore[o.store_id].platformFee+=Number(o.platform_fee||0);
                    }
                    // Payout per the seller's fee model (must match the live payout + line-897 snapshot):
                    // customer-pays (post-TSF) = full amount; absorb (pre-TSF) = amount × fixed 0.9746.
                    // NOT gross×factor — that caps to 1.0 on incomplete sync and overpays absorb sellers.
                    for(const sg of Object.values(byStore)) sg.payout=+(sg.postTsfGross + Math.round((sg.gross - sg.postTsfGross) * 0.9746)).toFixed(2);
                    const settledOn=freshData.rzp_settlements[0].settled_on?new Date(freshData.rzp_settlements[0].settled_on*1000).toISOString().slice(0,10):new Date().toISOString().slice(0,10);
                    await fetch(`${BACKEND_URL}/api/admin/settlement-history/save`,{method:"POST",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:JSON.stringify({settlement_id:sid,settled_on:settledOn,gross_amount:totalGross,net_amount:net,factor,stores:Object.values(byStore)})}).catch(()=>{});
                  }
                  // Step 3: fetch updated history
                  const r=await fetch(`${BACKEND_URL}/api/admin/settlement-history`,{headers:{"x-admin-token":ADMIN_ID}});
                  const d=await r.json();
                  setHistoryData(d.history||[]);
                } catch(e){ setHistoryData([]); alert("Failed: "+e.message); }
              }} style={{background:"var(--teal)",color:"#fff",border:"none",borderRadius:4,padding:"9px 16px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                {historyData===null?"Load History":"↺ Refresh"}
              </button>
            </div>

            {historyData===null&&(
              <div style={{textAlign:"center",padding:"48px 0",color:"var(--ink-soft)"}}>
                <div style={{fontSize:40,marginBottom:12}}><Icon n="document" size={40} inline /></div>
                <p style={{fontSize:14}}>Click "Load History" to see past settlements</p>
                <p style={{fontSize:12,marginTop:6}}>Settlements are auto-saved when Razorpay data loads each day</p>
              </div>
            )}

            {historyData!==null&&historyData.length===0&&(
              <div style={{textAlign:"center",padding:"48px 0",color:"var(--ink-soft)"}}>
                <p>No settlement history yet — it will appear here automatically from tomorrow.</p>
              </div>
            )}

            {historyData!==null&&historyData.length>0&&(
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {historyData.map(rec=>{
                  const isOpen=historyExpanded===rec.settlement_id;
                  const storeList=Array.isArray(rec.stores)?rec.stores:[];
                  // Paid follows the Payouts tab (orders carry the payout_id) — the stored flag only counts as a fallback.
                  const paidIdx=paidIndexOf(orders);
                  const stateOf=(sg)=>sg.paid?"paid":storePayState(paidIdx,sg.store_id,rec.settled_on);
                  const paidCount=storeList.filter(s=>{const st=stateOf(s);return st==="paid"||st==="none";}).length;
                  const allPaid=storeList.length>0&&paidCount===storeList.length;
                  const date=rec.settled_on?new Date(rec.settled_on).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}):"—";
                  return (
                    <div key={rec.settlement_id} className="card" style={{overflow:"hidden"}}>
                      {/* Header row */}
                      <div style={{padding:"16px 20px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",cursor:"pointer"}}
                        onClick={()=>setHistoryExpanded(isOpen?null:rec.settlement_id)}>
                        <div style={{flex:1,minWidth:180}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                            <span style={{fontWeight:700,fontSize:15}}>
                              ₹{Number(rec.net_amount||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </span>
                            {allPaid
                              ? <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,background:"var(--green-bg)",color:"var(--green)",fontWeight:700}}>✓ All Paid</span>
                              : paidCount>0
                                ? <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,background:"var(--yellow)",color:"#92400E",fontWeight:700}}>{paidCount}/{storeList.length} Paid</span>
                                : <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,background:"var(--red-light)",color:"var(--red)",fontWeight:700}}>Unpaid</span>
                            }
                          </div>
                          <div style={{fontSize:12,color:"var(--ink-soft)"}}>
                            {date}&nbsp;·&nbsp;
                            <code style={{fontFamily:"monospace",fontSize:11,background:"var(--surface)",padding:"1px 5px",borderRadius:4}}>{rec.settlement_id}</code>
                            &nbsp;·&nbsp;{storeList.length} stores
                          </div>
                        </div>
                        <div style={{display:"flex",gap:10,alignItems:"center"}}>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:10,color:"var(--ink-soft)"}}>Gross</div>
                            <div style={{fontSize:13,fontWeight:600}}>₹{Number(rec.gross_amount||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                          </div>
                          <span style={{fontSize:18,color:"var(--ink-soft)",transform:isOpen?"rotate(180deg)":"none",transition:"transform 0.2s"}}>▼</span>
                        </div>
                      </div>

                      {/* Expanded per-store table */}
                      {isOpen&&(
                        <div style={{borderTop:"1px solid var(--border)",padding:"16px 20px"}}>
                          <div style={{overflowX:"auto"}}>
                            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:500}}>
                              <thead>
                                <tr style={{borderBottom:"1.5px solid var(--border)"}}>
                                  {["STORE","UPI ID","PHONE","ORDERS","GROSS","BLOOM EARNS","PAY OUT","STATUS"].map(h=>(
                                    <th key={h} style={{textAlign:h==="ORDERS"||h==="GROSS"||h==="BLOOM EARNS"||h==="PAY OUT"?"right":"left",padding:"4px 8px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:h==="BLOOM EARNS"?"var(--teal-deep)":"var(--ink-soft)",whiteSpace:"nowrap"}}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {storeList.map((sg,i)=>(
                                  <tr key={i} style={{borderBottom:"1px solid var(--border)",background:(stateOf(sg)==="paid"||stateOf(sg)==="none")?"rgba(6,118,71,0.04)":"transparent"}}>
                                    <td style={{padding:"8px 8px 8px 0",fontWeight:600}}>{sg.store_name}</td>
                                    <td style={{padding:"8px"}}>
                                      {sg.upi_id
                                        ? <code style={{fontSize:11,background:"var(--surface)",padding:"2px 6px",borderRadius:4}}>{sg.upi_id}</code>
                                        : <span style={{color:"var(--ink-soft)"}}>—</span>}
                                    </td>
                                    <td style={{padding:"8px",color:"var(--ink-soft)",fontSize:12}}>{sg.phone||"—"}</td>
                                    <td style={{padding:"8px",textAlign:"right",color:"var(--ink-soft)"}}>{sg.orders}</td>
                                    <td style={{padding:"8px",textAlign:"right",color:"var(--ink-soft)"}}>₹{Number(sg.gross||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                    <td style={{padding:"8px",textAlign:"right",fontWeight:700,color:"var(--teal-deep)"}}>{Number(sg.platformFee||0)>0?`₹${Number(sg.platformFee).toLocaleString("en-IN")}`:"—"}</td>
                                    <td style={{padding:"8px",textAlign:"right",fontWeight:700,color:"var(--green)"}}>₹{Number(sg.payout||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                    <td style={{padding:"8px"}}>
                                      {sg.paid
                                        ? <div style={{display:"flex",alignItems:"center",gap:6}}>
                                            <span style={{fontSize:12,color:"var(--green)",fontWeight:700}}>✓ Paid</span>
                                            <span style={{fontSize:10,color:"var(--ink-soft)"}}>
                                              {sg.paid_at?new Date(sg.paid_at).toLocaleDateString("en-IN",{day:"numeric",month:"short"}):""}
                                            </span>
                                            <button
                                              disabled={historyUpdating===rec.settlement_id+sg.store_id}
                                              onClick={async()=>{
                                                setHistoryUpdating(rec.settlement_id+sg.store_id);
                                                const updated=storeList.map(s=>s.store_id===sg.store_id?{...s,paid:false,paid_at:null}:s);
                                                try {
                                                  await fetch(`${BACKEND_URL}/api/admin/settlement-history/${rec.settlement_id}`,{method:"PATCH",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:JSON.stringify({stores:updated})});
                                                  setHistoryData(prev=>prev.map(r=>r.settlement_id===rec.settlement_id?{...r,stores:updated}:r));
                                                } catch {}
                                                setHistoryUpdating(null);
                                              }}
                                              style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:"var(--ink-soft)",textDecoration:"underline",padding:0}}>undo</button>
                                          </div>
                                        : stateOf(sg)==="paid"
                                          ? <div style={{display:"flex",alignItems:"center",gap:6}}>
                                              <span style={{fontSize:12,color:"var(--green)",fontWeight:700}}>✓ Paid</span>
                                              <span style={{fontSize:10,color:"var(--ink-soft)"}}>via Payouts</span>
                                            </div>
                                        : stateOf(sg)==="none"
                                          ? <span style={{fontSize:12,color:"var(--ink-soft)"}}>Nothing due</span>
                                        : <button
                                            disabled={historyUpdating===rec.settlement_id+sg.store_id}
                                            onClick={async()=>{
                                              setHistoryUpdating(rec.settlement_id+sg.store_id);
                                              const paidAt = new Date().toISOString();
                                              const updated=storeList.map(s=>s.store_id===sg.store_id?{...s,paid:true,paid_at:paidAt}:s);
                                              try {
                                                await fetch(`${BACKEND_URL}/api/admin/settlement-history/${rec.settlement_id}`,{method:"PATCH",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:JSON.stringify({stores:updated})});
                                                setHistoryData(prev=>prev.map(r=>r.settlement_id===rec.settlement_id?{...r,stores:updated}:r));
                                                // Also insert a payout record so it shows up in Pending Payouts
                                                const payoutRec = { id:`pay-hist-${rec.settlement_id}-${sg.store_id}`, store_id:sg.store_id, amount:sg.payout||0, note:`Payout for settlement ${rec.settlement_id} (${rec.settled_on||"—"})`, paid_at:paidAt };
                                                const saved = await api.insertPayout(payoutRec);
                                                if (saved !== null) setPayouts(prev=>[...prev.filter(p=>p.id!==payoutRec.id), payoutRec]);
                                              } catch {}
                                              setHistoryUpdating(null);
                                            }}
                                            style={{background:"var(--green-bg)",color:"var(--green)",border:"1.5px solid rgba(6,118,71,0.3)",borderRadius:4,padding:"4px 10px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                                            {historyUpdating===rec.settlement_id+sg.store_id?"…":"Mark Paid"}
                                          </button>
                                      }
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        ) : tab==="stores" ? (
          /* ── STORES TAB ── */
          <div>
            {/* Header row */}
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,flexWrap:"wrap"}}>
              <h2 style={{fontSize:isMobile?20:26,margin:0}}>Stores <span style={{color:"var(--ink-soft)",fontSize:15,fontWeight:500}}>({stores.filter(s=>s.approved!==false).length} active{stores.filter(s=>s.approved===false).length>0?`, ${stores.filter(s=>s.approved===false).length} pending`:""})</span></h2>
              <div style={{flex:"1 1 280px",display:"flex",gap:8,flexWrap:"wrap"}}>
                <input className="inp" style={{flex:"1 1 140px",minWidth:120,fontSize:13}} placeholder="Search…" value={storeSearch} onChange={e=>setStoreSearch(e.target.value)}/>
                <select value={storeSort} onChange={e=>setStoreSort(e.target.value)}
                  style={{border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"7px 10px",fontSize:12,fontWeight:600,background:"var(--surface)",color:"var(--ink)",cursor:"pointer"}}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="az">A → Z</option>
                  <option value="za">Z → A</option>
                  <option value="revenue">Most revenue</option>
                  <option value="orders">Most orders</option>
                </select>
                {["has-upi","no-upi","has-ig","no-ig","banned","halted"].map(f=>{
                  const on=storeFilters.has(f);
                  return <button key={f} onClick={()=>{const s=new Set(storeFilters);on?s.delete(f):s.add(f);setStoreFilters(s);}}
                    style={{background:on?"var(--teal)":"var(--surface)",color:on?"#fff":"var(--ink)",border:"1.5px solid "+(on?"var(--teal)":"var(--border-mid)"),borderRadius:4,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    {f==="has-upi"?"UPI ✓":f==="no-upi"?"No UPI":f==="has-ig"?"Has IG":f==="no-ig"?"No IG":f==="halted"?<><Icon n="blocked" size="1.1em" inline />{" Halted"}</>:<><Icon n="blocked" size="1.1em" inline />{" Banned"}</>}
                  </button>;
                })}
                {storeFilters.size>0&&<button onClick={()=>setStoreFilters(new Set())} style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>✕</button>}
                <button onClick={async()=>{setStoresRefreshing(true);const fresh=await fetch(`${BACKEND_URL}/api/admin/users-all`, { headers: { "x-admin-token": ADMIN_ID } }).then(r=>r.ok?r.json():[]).catch(()=>[]);if(fresh)setStores(fresh.filter(u=>u.id!==DEMO_ID));setStoresRefreshing(false);}} disabled={storesRefreshing}
                  style={{background:"var(--surface)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",color:"var(--ink)",opacity:storesRefreshing?0.5:1}}>
                  {storesRefreshing?"Refreshing…":<><Icon n="refresh" size="1.1em" inline />{" Refresh"}</>}
                </button>
              </div>
            </div>
            {(()=>{
              const pending=stores.filter(s=>s.approved===false);
              const q=storeSearch.toLowerCase();
              const visible=[...stores]
                .filter(store=>{
                  if(store.approved===false)return false; // shown separately
                  if(q&&!((store.store_name||"").toLowerCase().includes(q)||(store.email||"").toLowerCase().includes(q)||(store.instagram||"").toLowerCase().includes(q)||(store.store_slug||"").toLowerCase().includes(q)))return false;
                  if(storeFilters.has("has-upi")&&!store.upi_id)return false;
                  if(storeFilters.has("no-upi")&&!!store.upi_id)return false;
                  if(storeFilters.has("has-ig")&&!store.instagram)return false;
                  if(storeFilters.has("no-ig")&&!!store.instagram)return false;
                  if(storeFilters.has("banned")&&!store.banned)return false;
                  if(storeFilters.has("halted")&&!store.admin_halted)return false;
                  return true;
                })
                .sort((a,b)=>{
                  if(storeSort==="newest") return new Date(b.created_at||0)-new Date(a.created_at||0);
                  if(storeSort==="oldest") return new Date(a.created_at||0)-new Date(b.created_at||0);
                  if(storeSort==="az")     return (a.store_name||"").localeCompare(b.store_name||"");
                  if(storeSort==="za")     return (b.store_name||"").localeCompare(a.store_name||"");
                  if(storeSort==="revenue")return totalEarned(b.id)-totalEarned(a.id);
                  if(storeSort==="orders") return storeOrders(b.id).length-storeOrders(a.id).length;
                  return 0;
                });
              const renderStoreRow = (store) => (
                <div style={{borderBottom:"1px solid var(--border-mid)",padding:"10px 4px",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",background:store.banned?"#fefce8":"transparent"}}>
                  <div style={{width:36,height:36,borderRadius:4,overflow:"hidden",flexShrink:0,background:"var(--surface)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,color:"var(--teal)"}}>
                    {store.store_image?<img src={thumbUrl(store.store_image, 80)} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(store.store_name||"?")[0].toUpperCase()}
                  </div>
                  <div style={{flex:"1 1 160px",minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      <span style={{fontWeight:700,fontSize:13,whiteSpace:"nowrap"}}>{store.store_name}</span>
                      {store.upi_id&&<span style={{fontSize:9,background:"#dcfce7",color:"#166534",borderRadius:4,padding:"1px 6px",fontWeight:700}}>UPI</span>}
                      {store.banned&&<span style={{fontSize:9,background:"#fef9c3",color:"#854d0e",borderRadius:4,padding:"1px 6px",fontWeight:700}}><Icon n="hide" size="1.1em" inline /> HIDDEN</span>}
                      {store.admin_halted&&<span style={{fontSize:9,background:"#fee2e2",color:"#991b1b",borderRadius:4,padding:"1px 6px",fontWeight:700}}><Icon n="blocked" size="1.1em" inline /> HALTED</span>}
                    </div>
                    <div style={{fontSize:11,color:"var(--ink-soft)",display:"flex",gap:8,flexWrap:"wrap",marginTop:2}}>
                      {store.instagram&&<span>@{store.instagram}</span>}
                      <span style={{fontFamily:"monospace"}}>/store/{store.store_slug}</span>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:"var(--ink-soft)",flex:"1 1 140px",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{store.email}</div>
                  <div style={{fontSize:11,color:"var(--ink-soft)",whiteSpace:"nowrap",flexShrink:0}}>
                    {store.created_at?new Date(store.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short"}):"—"}
                  </div>
                  <div style={{display:"flex",gap:12,flexShrink:0,textAlign:"right"}}>
                    <div><div style={{fontSize:10,color:"var(--ink-soft)",fontWeight:700}}>REV</div><div style={{fontSize:13,fontWeight:700,color:"var(--pink-btn)"}}>₹{totalEarned(store.id).toLocaleString()}</div></div>
                    <div><div style={{fontSize:10,color:"var(--ink-soft)",fontWeight:700}}>ORD</div><div style={{fontSize:13,fontWeight:700}}>{storeOrders(store.id).length}</div></div>
                  </div>
                  <div style={{display:"flex",gap:5,flexShrink:0}}>
                    <a href={`/store/${store.store_slug}`} target="_blank" rel="noreferrer" style={{background:"var(--surface)",color:"var(--ink)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"none"}}>↗</a>
                    <a href={`/admin/store/${store.id}`} target="_blank" rel="noreferrer" title="View store owner dashboard" style={{background:"#ede9fe",color:"#6d28d9",border:"1.5px solid #c4b5fd",borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"none"}}><Icon n="show" size={11} inline /></a>
                    <button onClick={()=>{setStoreDetailId(storeDetailId===store.id?null:store.id);setStoreDetailSearch("");setStoreProductsId(null);}} style={{background:storeDetailId===store.id?"var(--teal)":"var(--surface)",color:storeDetailId===store.id?"#fff":"var(--ink)",border:"1.5px solid "+(storeDetailId===store.id?"var(--teal)":"var(--border-mid)"),borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}><Icon n="copy" size={11} inline /></button>
                    <button title="Manage products" onClick={async()=>{const newId=storeProductsId===store.id?null:store.id;setStoreProductsId(newId);setStoreDetailId(null);if(newId&&!storeProductsList[newId]){const prods=await supa.selectAll("bloom_products",`user_id=eq.${newId}&order=created_at.asc`);setStoreProductsList(prev=>({...prev,[newId]:Array.isArray(prods)?prods:[]}));}}} style={{background:storeProductsId===store.id?"#f97316":"var(--surface)",color:storeProductsId===store.id?"#fff":"var(--ink)",border:"1.5px solid "+(storeProductsId===store.id?"#f97316":"var(--border-mid)"),borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}><Icon n="bag" size={11} inline /></button>
                    <button title={store.banned?"Show on marketplace":"Hide from all public listings"} onClick={async()=>{const nb=!store.banned;const r=await fetch(`${BACKEND_URL}/api/admin/ban-user/${store.id}`,{method:"PATCH",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:JSON.stringify({banned:nb})});if(r.ok)setStores(prev=>prev.map(s=>s.id===store.id?{...s,banned:nb}:s));else alert("Failed");}} style={{background:store.banned?"#fef9c3":"var(--surface)",color:store.banned?"#854d0e":"var(--ink-soft)",border:`1.5px solid ${store.banned?"#fbbf24":"var(--border-mid)"}`,borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>{store.banned?<><Icon n="hide" size="1.1em" inline />{" Hidden"}</>:<><Icon n="hide" size="1.1em" inline /></>}</button>
                    <button title={store.admin_halted?"Resume purchases from this store":"Halt — block ALL purchases from this store"} onClick={()=>haltStore(store)} style={{background:store.admin_halted?"#B42318":"#FEE2E2",color:store.admin_halted?"#fff":"#991b1b",border:"1.5px solid #fca5a5",borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>{store.admin_halted?"▶ Resume":<><Icon n="blocked" size="1.1em" inline />{" Halt"}</>}</button>
                    <button onClick={()=>deleteStore(store)} style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}><Icon n="delete" size={11} inline /></button>
                  </div>
                </div>
              );
              return <>
                {/* ── PENDING APPROVAL ── */}
                {pending.length>0&&(
                  <div style={{background:"#fffbeb",border:"1.5px solid #f59e0b",borderRadius:4,padding:16,marginBottom:20}}>
                    <div style={{fontWeight:700,fontSize:14,color:"#92400e",marginBottom:12}}><Icon n="pending" size="1.1em" inline /> Pending Approval ({pending.length})</div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {pending.map(store=>(
                        <div key={store.id} style={{display:"flex",alignItems:"center",gap:10,background:"#fff",borderRadius:4,padding:"10px 14px",flexWrap:"wrap"}}>
                          <div style={{flex:"1 1 140px",minWidth:0}}>
                            <div style={{fontWeight:700,fontSize:13}}>{store.store_name}</div>
                            <div style={{fontSize:11,color:"#666",display:"flex",gap:8,flexWrap:"wrap",marginTop:2}}>
                              <span>{store.email}</span>
                              {store.instagram&&<span>@{store.instagram}</span>}
                            </div>
                          </div>
                          <div style={{fontSize:11,color:"#666",whiteSpace:"nowrap"}}>
                            {store.created_at?new Date(store.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}):"—"}
                          </div>
                          {store.instagram&&(
                            <a href={`https://instagram.com/${store.instagram}`} target="_blank" rel="noreferrer"
                              style={{fontSize:11,color:"#be185d",fontWeight:700,textDecoration:"none",background:"#fce7f3",padding:"4px 10px",borderRadius:4}}>
                              Check IG ↗
                            </a>
                          )}
                          <div style={{display:"flex",gap:6,flexShrink:0}}>
                            <button onClick={async()=>{
                                const r=await fetch(`${BACKEND_URL}/api/admin/approve-user/${store.id}`,{method:"PATCH",headers:{"Content-Type":"application/json","x-admin-token":ADMIN_ID},body:"{}"});
                                if(r.ok)setStores(prev=>prev.map(s=>s.id===store.id?{...s,approved:true}:s));
                                else alert("Failed to approve");
                              }}
                              style={{background:"#dcfce7",color:"#166534",border:"none",borderRadius:4,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                              <Icon n="success" size="1.1em" inline /> Approve
                            </button>
                            <button onClick={async()=>{
                                if(!window.confirm(`Reject and delete ${store.store_name}?`))return;
                                const r=await fetch(`${BACKEND_URL}/api/admin/reject-user/${store.id}`,{method:"DELETE",headers:{"x-admin-token":ADMIN_ID}});
                                if(r.ok)setStores(prev=>prev.filter(s=>s.id!==store.id));
                                else alert("Failed to reject");
                              }}
                              style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                              <Icon n="delete" size="1.1em" inline /> Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* ── ACTIVE STORES TABLE ── */}
                {(storeSearch||storeFilters.size>0)&&<div style={{marginBottom:8,fontSize:12,color:"var(--ink-soft)"}}>{visible.length} store{visible.length!==1?"s":""} · ₹{visible.reduce((s,st)=>s+totalEarned(st.id),0).toLocaleString()} total</div>}
                <div style={{background:"var(--surface)",borderRadius:4,overflow:"hidden",padding:"0 12px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 4px",borderBottom:"1.5px solid var(--border-mid)"}}>
                    <div style={{width:36,flexShrink:0}}/>
                    <div style={{flex:"1 1 160px",fontSize:10,fontWeight:700,color:"var(--ink-soft)",letterSpacing:"0.06em"}}>STORE</div>
                    <div style={{flex:"1 1 140px",fontSize:10,fontWeight:700,color:"var(--ink-soft)",letterSpacing:"0.06em"}}>EMAIL</div>
                    <div style={{fontSize:10,fontWeight:700,color:"var(--ink-soft)",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>JOINED</div>
                    <div style={{display:"flex",gap:12,fontSize:10,fontWeight:700,color:"var(--ink-soft)",letterSpacing:"0.06em",flexShrink:0}}><span>REV</span><span>ORD</span></div>
                    <div style={{fontSize:10,fontWeight:700,color:"var(--ink-soft)",letterSpacing:"0.06em",flexShrink:0}}>ACTIONS</div>
                  </div>
                  {visible.map(store=>(
                    <div key={store.id}>
                      {renderStoreRow(store)}
                  {storeDetailId===store.id&&(()=>{
                    const sq=storeDetailSearch.toLowerCase();
                    const sOrders=[...storeOrders(store.id)]
                      .sort((a,b)=>new Date(b.order_date||b.date||0)-new Date(a.order_date||a.date||0))
                      .filter(o=>{
                        if(!sq) return true;
                        const items=Array.isArray(o.items)?o.items.map(i=>i.name||"").join(" ").toLowerCase():"";
                        const cust=customerById.get(o.customer_id);
                        return (o.customer_name||"").toLowerCase().includes(sq)||
                          (o.id||"").toLowerCase().includes(sq)||
                          items.includes(sq)||
                          String(o.amount||"").includes(sq)||
                          (o.delivery_address||"").toLowerCase().includes(sq)||
                          (o.customer_phone||"").includes(sq)||
                          (cust?.email||"").toLowerCase().includes(sq)||
                          (cust?.phone||"").includes(sq)||
                          (cust?.instagram||"").toLowerCase().includes(sq);
                      });
                    return (
                      <div style={{marginTop:16,borderTop:"1.5px solid var(--border-mid)",paddingTop:16}}>
                        <div style={{display:"flex",gap:10,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
                          <input className="inp" style={{flex:1,minWidth:200,maxWidth:400}}
                            placeholder="Search orders: customer, order ID, product, address…"
                            value={storeDetailSearch} onChange={e=>setStoreDetailSearch(e.target.value)} autoFocus/>
                          <span style={{fontSize:13,color:"var(--ink-soft)",whiteSpace:"nowrap"}}>
                            <strong>{sOrders.length}</strong> order{sOrders.length!==1?"s":""} · <strong style={{color:"var(--pink-btn)"}}>₹{sOrders.reduce((s,o)=>s+gross(o),0).toLocaleString()}</strong>
                          </span>
                        </div>
                        {sOrders.length===0?(
                          <div style={{textAlign:"center",padding:"30px 0",color:"var(--ink-soft)",fontSize:14}}>
                            {storeOrders(store.id).length===0?"No orders yet for this store.":"No orders match that search."}
                          </div>
                        ):(
                          <div style={{display:"flex",flexDirection:"column",gap:8}}>
                            {sOrders.map(o=>{
                              const items=Array.isArray(o.items)?o.items:[];
                              const itemLabel=items.length>0?items.map(i=>`${i.name}${i.qty>1?` ×${i.qty}`:""}`).join(", "):o.productLabel||"—";
                              const orderDt=o.order_date||o.date;
                              const cust=customerById.get(o.customer_id);
                              const custEmail=cust?.email||null;
                              const custPhone=o.customer_phone||cust?.phone||null;
                              const custIg=cust?.instagram||null;
                              return (
                                <div key={o.id} style={{background:"var(--surface)",borderRadius:4,padding:"12px 16px",border:"1px solid var(--border-mid)"}}>
                                  <div style={{display:"flex",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
                                    <div style={{flex:1,minWidth:180}}>
                                      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                                        <span style={{fontWeight:700,fontSize:14}}>{o.customer_name||"Customer"}</span>
                                        <span style={{fontSize:11,fontFamily:"monospace",color:"var(--ink-soft)",background:"var(--bg)",borderRadius:4,padding:"1px 6px"}}>{o.id}</span>
                                      </div>
                                      <div style={{fontSize:13,color:"var(--ink-soft)",marginBottom:5}}>{itemLabel}</div>
                                      {/* Customer contact row */}
                                      <div style={{display:"flex",flexDirection:"column",gap:2,marginBottom:o.delivery_address?6:0}}>
                                        {custEmail&&<span style={{fontSize:12,color:"var(--ink-soft)"}}><Icon n="email" size={12} inline /> {custEmail}</span>}
                                        {custPhone&&<span style={{fontSize:12,color:"var(--ink-soft)"}}><Icon n="phone" size={12} inline /> {custPhone}</span>}
                                        {custIg&&<span style={{fontSize:12,color:"var(--ink-soft)"}}><Icon n="camera" size="1.1em" inline /> @{custIg}</span>}
                                      </div>
                                      {o.delivery_address&&(
                                        <div style={{fontSize:12,color:"var(--ink-soft)",whiteSpace:"pre-line",marginTop:4,background:"var(--bg)",borderRadius:4,padding:"6px 10px"}}>
                                          <Icon n="package" size={12} inline /> {o.delivery_address}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{textAlign:"right",flexShrink:0}}>
                                      <div style={{fontSize:18,fontWeight:700,color:"var(--pink-btn)"}}>₹{gross(o).toLocaleString()}</div>
                                      {Number(o.tsf||0)>0&&<div style={{fontSize:11,color:"var(--ink-soft)",marginTop:1}}>you get ₹{Number(o.amount).toLocaleString()} · fee ₹{Number(o.tsf).toLocaleString()}</div>}
                                      <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:3}}>{orderDt?new Date(orderDt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}):"—"}</div>
                                      {o.status==="refunded"
                                        ? <div style={{marginTop:6,fontSize:11,fontWeight:800,color:"var(--red)"}}>↩ Refunded</div>
                                        : (String(o.upi_ref||"").startsWith("pay_") && (
                                            <button onClick={()=>handleRefundOrder(o)} disabled={refundingId===o.id}
                                              title="Refund this order only — siblings on the same payment are untouched"
                                              style={{marginTop:7,background:"transparent",border:"1.5px solid var(--red)",color:"var(--red)",borderRadius:4,padding:"4px 11px",fontSize:11,fontWeight:700,cursor:refundingId===o.id?"wait":"pointer",opacity:refundingId===o.id?0.6:1}}>
                                              {refundingId===o.id ? "Refunding…" : "↩ Refund"}
                                            </button>
                                          ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Products dropdown ── */}
                  {storeProductsId===store.id&&(()=>{
                    const storeProdsAdmin=storeProductsList[store.id];
                    return (
                      <div style={{marginTop:16,borderTop:"1.5px solid #fed7aa",paddingTop:16}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#9a3412",letterSpacing:"0.05em",marginBottom:10}}>
                          <Icon n="bag" size="1.1em" inline /> PRODUCTS {storeProdsAdmin?`(${storeProdsAdmin.length})`:""}
                        </div>
                        {!storeProdsAdmin?(
                          <div style={{fontSize:13,color:"var(--ink-soft)",padding:"10px 0"}}>Loading…</div>
                        ):storeProdsAdmin.length===0?(
                          <div style={{fontSize:13,color:"var(--ink-soft)",padding:"10px 0"}}>No products in this store.</div>
                        ):(
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {storeProdsAdmin.map(p=>{
                              const thumb=(Array.isArray(p.images)&&p.images[0])||p.img||null;
                              return (
                                <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,background:"#fff7ed",borderRadius:4,padding:"8px 12px",border:"1px solid #fed7aa"}}>
                                  <div style={{width:36,height:36,borderRadius:4,overflow:"hidden",flexShrink:0,background:"var(--surface)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                                    {thumb?<img src={thumbUrl(thumb, 100)} alt="" style={{width:36,height:36,objectFit:"cover"}}/>:<span style={{fontSize:18}}><Icon n="package" size={18} inline /></span>}
                                  </div>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontWeight:600,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name||"Unnamed"}</div>
                                    <div style={{fontSize:11,color:"var(--ink-soft)"}}>₹{Number(p.price||0).toLocaleString()} · Stock: {p.stock??p.stock===0?0:"—"}</div>
                                  </div>
                                  <button
                                    onClick={async()=>{
                                      if(!window.confirm(`Delete "${p.name}" from ${store.store_name}? This cannot be undone.`)) return;
                                      const ok=await api.deleteProduct(p.id, { adminToken: ADMIN_ID });
                                      if(ok){setStoreProductsList(prev=>({...prev,[store.id]:(prev[store.id]||[]).filter(x=>x.id!==p.id)}));onProductDeleted?.(store.id,p.id);}
                                      else alert("Delete failed. Try again.");
                                    }}
                                    style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                                    <Icon n="delete" size="1.1em" inline /> Delete
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
                </div>
              </>;
            })()}
          </div>

        ) : tab==="customers" ? (
          /* ── CUSTOMERS TAB ── */
          <div>
            <h2 style={{fontSize:isMobile?22:28,marginBottom:16}}>All Customers ({customers.length})</h2>
            {/* Search + filter */}
            <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
              <input className="inp" style={{flex:1,minWidth:200,maxWidth:360}}
                placeholder="Search name, email, Instagram, phone…"
                value={customerSearch} onChange={e=>setCustomerSearch(e.target.value)}/>
              <select value={customerSort} onChange={e=>setCustomerSort(e.target.value)}
                style={{border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"7px 10px",fontSize:12,fontWeight:600,background:"var(--surface)",color:"var(--ink)",cursor:"pointer"}}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="az">A → Z</option>
                <option value="za">Z → A</option>
                <option value="spent">Most spent</option>
                <option value="orders">Most orders</option>
              </select>
              {["has-upi","no-upi","has-ig","no-ig"].map(f=>{
                const on=customerFilters.has(f);
                return <button key={f} onClick={()=>{const s=new Set(customerFilters);on?s.delete(f):s.add(f);setCustomerFilters(s);}}
                  style={{background:on?"var(--teal)":"var(--surface)",color:on?"#fff":"var(--ink)",border:"1.5px solid "+(on?"var(--teal)":"var(--border-mid)"),borderRadius:4,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  {f==="has-upi"?"UPI ✓":f==="no-upi"?"No UPI":f==="has-ig"?"Has IG":"No IG"}
                </button>;
              })}
              {customerFilters.size>0&&<button onClick={()=>setCustomerFilters(new Set())} style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>✕ Clear</button>}
            </div>
            {(()=>{
              const visibleC=[...customers].filter(c=>{
                if(customerSearch){const q=customerSearch.toLowerCase();if(!((c.name||"").toLowerCase().includes(q)||(c.email||"").toLowerCase().includes(q)||(c.instagram||"").toLowerCase().includes(q)||(c.phone||"").includes(q)))return false;}
                if(customerFilters.has("has-upi")&&!c.upi_id)return false;
                if(customerFilters.has("no-upi")&&!!c.upi_id)return false;
                if(customerFilters.has("has-ig")&&!c.instagram)return false;
                if(customerFilters.has("no-ig")&&!!c.instagram)return false;
                return true;
              }).sort((a,b)=>{
                const aC=customerSpend.get(a.id)||{spent:0,count:0};
                const bC=customerSpend.get(b.id)||{spent:0,count:0};
                const aSpent=aC.spent, bSpent=bC.spent, aOrds=aC.count, bOrds=bC.count;
                if(customerSort==="newest") return new Date(b.created_at||0)-new Date(a.created_at||0);
                if(customerSort==="oldest") return new Date(a.created_at||0)-new Date(b.created_at||0);
                if(customerSort==="az")     return (a.name||"").localeCompare(b.name||"");
                if(customerSort==="za")     return (b.name||"").localeCompare(a.name||"");
                if(customerSort==="spent")  return bSpent-aSpent;
                if(customerSort==="orders") return bOrds-aOrds;
                return 0;
              });
              const cSum=visibleC.reduce((s,c)=>s+(customerSpend.get(c.id)?.spent||0),0);
              return <>
                {(customerFilters.size>0||customerSearch)&&<div style={{marginBottom:12,padding:"8px 14px",background:"var(--surface)",borderRadius:4,fontSize:13,display:"flex",gap:16,flexWrap:"wrap"}}>
                  <span style={{fontWeight:700}}>{visibleC.length} customer{visibleC.length!==1?"s":""}</span>
                  <span style={{color:"var(--ink-soft)"}}>Total spent: <strong style={{color:"var(--pink-btn)"}}>₹{cSum.toLocaleString()}</strong></span>
                </div>}
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {visibleC.slice(0, custLimit)
                .map(c=>{
                const custOrders = ordersByCustomer.get(c.id)||[];
                const spent = customerSpend.get(c.id)?.spent||0;
                return (
                  <div key={c.id} className="card" style={{padding:isMobile?14:20}}>
                    <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap"}}>
                      <div style={{width:44,height:44,borderRadius:"50%",background:"var(--pink)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:"var(--pink-btn-h)",flexShrink:0}}>
                        {(c.name||"?").charAt(0).toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:160}}>
                        <div style={{fontWeight:700,fontSize:15,marginBottom:3}}>{c.name||"—"}</div>
                        <p style={{fontSize:13,color:"var(--ink-soft)"}}><Icon n="email" size={13} inline /> {c.email}</p>
                        {c.phone&&<p style={{fontSize:12,color:"var(--ink-soft)"}}><Icon n="phone" size={12} inline /> {c.phone}</p>}
                        {c.instagram&&<p style={{fontSize:12,color:"var(--ink-soft)"}}><Icon n="camera" size="1.1em" inline /> @{c.instagram}</p>}
                        {c.upi_id&&<p style={{fontSize:12,color:"var(--ink-soft)"}}>UPI: {c.upi_id}</p>}
                      </div>
                      <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:11,color:"var(--ink-soft)",fontWeight:700,letterSpacing:"0.06em"}}>SPENT</div>
                          <div style={{fontSize:18,fontWeight:700,color:"var(--pink-btn)"}}>₹{spent.toLocaleString()}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:11,color:"var(--ink-soft)",fontWeight:700,letterSpacing:"0.06em"}}>ORDERS</div>
                          <div style={{fontSize:18,fontWeight:700}}>{custOrders.length}</div>
                        </div>
                        <button onClick={()=>deleteCustomer(c)}
                          style={{background:"#FEE2E2",color:"#C05",border:"none",borderRadius:4,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                          <Icon n="delete" size="1.1em" inline /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {visibleC.length===0&&(
                <div style={{textAlign:"center",padding:"60px 0",color:"var(--ink-soft)"}}>
                  <div style={{fontSize:48,marginBottom:12}}><Icon n="bag" size={48} inline /></div>
                  <p>{customers.length===0?"No customers yet.":"No customers match these filters."}</p>
                </div>
              )}
              {visibleC.length>custLimit&&(
                <button onClick={()=>setCustLimit(l=>l+120)}
                  style={{alignSelf:"center",marginTop:6,background:"var(--surface)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"10px 22px",fontSize:13,fontWeight:700,color:"var(--ink)",cursor:"pointer"}}>
                  Show more · {visibleC.length-custLimit} more customer{visibleC.length-custLimit!==1?"s":""}
                </button>
              )}
                </div>
              </>;
            })()}
          </div>
        ) : tab === "orders" ? (
          /* ── ORDERS TAB ── */
          <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,flexWrap:"wrap"}}>
              <h2 style={{fontSize:isMobile?22:28,margin:0}}>All Orders ({orders.length})</h2>
              {newOrderIds.size>0&&<span style={{background:"#dcfce7",color:"#166534",borderRadius:4,padding:"4px 12px",fontSize:12,fontWeight:700,animation:"liveDot 2s ease infinite"}}>+{newOrderIds.size} new</span>}
            </div>
            <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
              <input className="inp" style={{flex:1,minWidth:200,maxWidth:420}}
                placeholder="Search customer, store, order ID, product…"
                value={orderSearch} onChange={e=>setOrderSearch(e.target.value)}/>
              <select value={orderSort} onChange={e=>setOrderSort(e.target.value)}
                style={{border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"7px 10px",fontSize:12,fontWeight:600,background:"var(--surface)",color:"var(--ink)",cursor:"pointer"}}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="high">Amount: High → Low</option>
                <option value="low">Amount: Low → High</option>
              </select>
            </div>
            {(()=>{
              const q=orderSearch.toLowerCase();
              const visible=[...orders]
                .sort((a,b)=>{
                  // Sort by created_at (real DB timestamp) — order_date is date-only
                  // ("19 Jun 2026") so same-day orders would otherwise collapse to midnight.
                  if(orderSort==="newest") return new Date(b.created_at||b.order_date||b.date||0)-new Date(a.created_at||a.order_date||a.date||0);
                  if(orderSort==="oldest") return new Date(a.created_at||a.order_date||a.date||0)-new Date(b.created_at||b.order_date||b.date||0);
                  if(orderSort==="high")   return Number(b.amount||0)-Number(a.amount||0);
                  if(orderSort==="low")    return Number(a.amount||0)-Number(b.amount||0);
                  return 0;
                })
                .filter(o=>{
                  if(!q) return true;
                  const storeName=(storeById.get(o.store_id)?.store_name||"").toLowerCase();
                  const items=Array.isArray(o.items)?o.items.map(i=>i.name||"").join(" ").toLowerCase():"";
                  return (o.customer_name||"").toLowerCase().includes(q)||
                    storeName.includes(q)||
                    (o.id||"").toLowerCase().includes(q)||
                    items.includes(q)||
                    (o.productLabel||"").toLowerCase().includes(q);
                });
              // Group confirmed orders by upi_ref for multi-store display
              const upiGroups = {};
              const singleItems = [];
              for (const o of visible) {
                if (o.upi_ref && o.status==="confirmed") {
                  if (!upiGroups[o.upi_ref]) upiGroups[o.upi_ref] = [];
                  upiGroups[o.upi_ref].push(o);
                } else {
                  singleItems.push({ _type:"single", order:o });
                }
              }
              const displayItems = [...singleItems];
              for (const [ref, grp] of Object.entries(upiGroups)) {
                if (grp.length > 1) {
                  displayItems.push({ _type:"multi", orders:grp, upi_ref:ref });
                } else {
                  displayItems.push({ _type:"single", order:grp[0] });
                }
              }
              displayItems.sort((a, b) => {
                const aD = a._type==="multi" ? (a.orders[0].created_at||a.orders[0].order_date||a.orders[0].date||0) : (a.order.created_at||a.order.order_date||a.order.date||0);
                const bD = b._type==="multi" ? (b.orders[0].created_at||b.orders[0].order_date||b.orders[0].date||0) : (b.order.created_at||b.order.order_date||b.order.date||0);
                if(orderSort==="newest") return new Date(bD)-new Date(aD);
                if(orderSort==="oldest") return new Date(aD)-new Date(bD);
                const aA = a._type==="multi" ? a.orders.reduce((s,o)=>s+gross(o),0) : gross(a.order);
                const bA = b._type==="multi" ? b.orders.reduce((s,o)=>s+gross(o),0) : gross(b.order);
                if(orderSort==="high") return bA-aA;
                if(orderSort==="low")  return aA-bA;
                return 0;
              });

              return <>
                {q&&<div style={{marginBottom:12,padding:"8px 14px",background:"var(--surface)",borderRadius:4,fontSize:13}}>
                  <strong>{visible.length}</strong> order{visible.length!==1?"s":""} · Total: <strong style={{color:"var(--pink-btn)"}}>₹{visible.reduce((s,o)=>s+gross(o),0).toLocaleString()}</strong>
                </div>}
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {displayItems.slice(0, orderLimit).map((item, idx) => {
                    if (item._type === "multi") {
                      // ── Multi-store checkout card ──
                      const grp = item.orders;
                      const firstO = grp[0];
                      const totalGross = grp.reduce((s,o)=>s+paidTotal(o),0);
                      const allItems = grp.flatMap(o => Array.isArray(o.items)?o.items:[]);
                      const itemLabel = allItems.map(i=>`${i.name}${i.qty>1?` ×${i.qty}`:""}`).join(", ")||"—";
                      const orderDt = firstO.created_at||firstO.order_date||firstO.date;
                      const anyNew = grp.some(o=>newOrderIds.has(o.id));
                      const anySettled = grp.every(o=>o.rzp_settled);
                      const anyPaid = grp.every(o=>o.payout_id);
                      return (
                        <div key={`multi-${item.upi_ref}`} className="card" style={{padding:isMobile?14:18,border:anyNew?"1.5px solid var(--green)":"1.5px solid rgba(47,99,221,0.2)",background:"rgba(47,99,221,0.02)",transition:"border 0.3s"}}>
                          {anyNew&&<div style={{fontSize:10,fontWeight:700,color:"var(--green)",letterSpacing:"0.06em",marginBottom:6}}><Icon n="dot" size="1.1em" inline /> NEW ORDER</div>}
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                            <span style={{fontSize:10,fontWeight:800,background:"rgba(47,99,221,0.15)",color:"var(--pink-btn)",borderRadius:4,padding:"2px 8px",letterSpacing:"0.04em"}}><Icon n="bag" size={10} inline /> {grp.length} STORES</span>
                            {anyPaid&&<span style={{fontSize:10,background:"#D1FAE5",color:"#065F46",borderRadius:4,padding:"2px 7px",fontWeight:700}}>✓ Paid out</span>}
                            {anySettled&&!anyPaid&&<span style={{fontSize:10,background:"#ECFDF5",color:"var(--green)",borderRadius:4,padding:"2px 7px",fontWeight:700}}><Icon n="instant" size="1.1em" inline /> Settled</span>}
                            {!anySettled&&<span style={{fontSize:10,background:"#FEF9C3",color:"#92400E",borderRadius:4,padding:"2px 7px",fontWeight:700}}><Icon n="pending" size="1.1em" inline /> Pending</span>}
                            <span style={{fontSize:11,fontFamily:"monospace",color:"var(--ink-soft)",opacity:0.6,marginLeft:"auto"}}>{item.upi_ref}</span>
                          </div>
                          <div style={{display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
                            <div style={{flex:1,minWidth:200}}>
                              <div style={{fontWeight:700,fontSize:15,marginBottom:4}}>{firstO.customer_name||"Customer"}</div>
                              <div style={{fontSize:12,color:"var(--ink)",marginBottom:6,lineHeight:1.5}}>{itemLabel}</div>
                              <div style={{display:"flex",flexDirection:"column",gap:2,marginBottom:4}}>
                                {grp.map(o=>{
                                  const st=storeById.get(o.store_id);
                                  return <div key={o.id} style={{fontSize:12,color:"var(--ink-soft)"}}><Icon n="store" size={12} inline /> <strong>{st?.store_name||o.store_id||"—"}</strong> · ₹{paidTotal(o).toLocaleString()}</div>;
                                })}
                              </div>
                              <div style={{fontSize:11,color:"var(--ink-soft)"}}>
                                <Icon n="calendar" size={11} inline /> {orderDt?new Date(orderDt).toLocaleString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}):"—"}
                              </div>
                              {firstO.delivery_address&&(
                                <div style={{marginTop:8,padding:"8px 10px",background:"var(--surface)",borderRadius:4,fontSize:12}}><Icon n="location" size={12} inline /> {firstO.delivery_address}</div>
                              )}
                            </div>
                            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                              <div style={{fontSize:22,fontWeight:700,color:"var(--pink-btn)"}}>₹{totalGross.toLocaleString()}</div>
                              <div style={{fontSize:11,color:"var(--ink-soft)"}}>{grp.length} stores · 1 payment</div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // ── Single-store card (existing logic) ──
                    const o = item.order;
                    const store=storeById.get(o.store_id);
                    const items=Array.isArray(o.items)?o.items:[];
                    const itemLabel=items.length>0?items.map(i=>`${i.name}${i.qty>1?` ×${i.qty}`:""}`).join(", "):o.productLabel||"—";
                    const orderDt=o.order_date||o.date;
                    const settleDt=t2SettlementDate(orderDt);
                    const now=new Date();
                    const settled=o.rzp_settled;
                    const paid=settled&&o.payout_id;
                    const settleStr=settleDt?settleDt.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}):"—";
                    const settleIsToday=settleDt&&settleDt.toDateString()===now.toDateString();
                    const settlePast=settleDt&&settleDt<now&&!settleIsToday;
                    const isNew=newOrderIds.has(o.id);
                    return (
                      <div key={o.id} className="card" style={{padding:isMobile?14:18,border:isNew?"1.5px solid var(--green)":"1.5px solid transparent",transition:"border 0.3s"}}>
                        {isNew&&<div style={{fontSize:10,fontWeight:700,color:"var(--green)",letterSpacing:"0.06em",marginBottom:6}}><Icon n="dot" size="1.1em" inline /> NEW ORDER</div>}
                        <div style={{display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
                          <div style={{flex:1,minWidth:200}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
                              <span style={{fontWeight:700,fontSize:15}}>{o.customer_name||"Customer"}</span>
                              {paid&&<span style={{fontSize:10,background:"#D1FAE5",color:"#065F46",borderRadius:4,padding:"2px 7px",fontWeight:700}}>✓ Paid out</span>}
                              {settled&&!paid&&<span style={{fontSize:10,background:"#ECFDF5",color:"var(--green)",borderRadius:4,padding:"2px 7px",fontWeight:700}}><Icon n="instant" size="1.1em" inline /> Settled</span>}
                              {!settled&&<span style={{fontSize:10,background:"#FEF9C3",color:"#92400E",borderRadius:4,padding:"2px 7px",fontWeight:700}}><Icon n="pending" size="1.1em" inline /> Pending</span>}
                            </div>
                            <div style={{fontSize:13,color:"var(--ink)",marginBottom:3}}>{itemLabel}</div>
                            <div style={{fontSize:12,color:"var(--ink-soft)",marginBottom:2}}>
                              <Icon n="store" size={12} inline /> <strong>{store?.store_name||o.store_id||"—"}</strong>
                            </div>
                            <div style={{fontSize:11,color:"var(--ink-soft)"}}>
                              <Icon n="calendar" size={11} inline /> {(o.created_at||orderDt)?new Date(o.created_at||orderDt).toLocaleString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}):"—"}
                              <span style={{marginLeft:8,fontFamily:"monospace",fontSize:10,opacity:0.6}}>{o.id}</span>
                            </div>
                            {o.delivery_address?(
                              <div style={{marginTop:8,padding:"8px 10px",background:"var(--surface)",borderRadius:4,fontSize:12,color:"var(--ink)"}}>
                                <div style={{whiteSpace:"pre-line",lineHeight:1.5}}><Icon n="location" size="1.1em" inline /> {o.delivery_address}</div>
                              </div>
                            ):o.customer_phone?(
                              <div style={{marginTop:8,padding:"8px 10px",background:"var(--surface)",borderRadius:4,fontSize:12,color:"var(--ink)"}}>
                                <div><Icon n="phone" size="1.1em" inline /> {o.customer_phone}</div>
                              </div>
                            ):(
                              <div style={{marginTop:6,fontSize:11,color:"var(--red)",fontWeight:600}}><Icon n="warning" size="1.1em" inline /> No address on file</div>
                            )}
                          </div>
                          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                            <div style={{fontSize:20,fontWeight:700,color:"var(--pink-btn)"}}>₹{paidTotal(o).toLocaleString()}</div>
                            {Number(o.tsf||0)>0&&<div style={{fontSize:11,color:"var(--ink-soft)"}}>store gets ₹{Number(o.amount).toLocaleString()} · fee ₹{Number(o.tsf).toLocaleString()}</div>}
                            {settled
                              ? <div style={{fontSize:11,color:"var(--green)",fontWeight:700}}>✓ In Razorpay</div>
                              : <div style={{fontSize:11,color:settleIsToday?"var(--teal-deep)":settlePast?"var(--ink-soft)":"var(--ink-soft)",fontWeight:settleIsToday?700:400}}>
                                  {settleIsToday?<><Icon n="bank" size="1.1em" inline />{" Settling today"}</>:settlePast?`Expected ${settleStr} (delayed?)`:`Transfers ~${settleStr}`}
                                </div>
                            }
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {visible.length===0&&(
                    <div style={{textAlign:"center",padding:"60px 0",color:"var(--ink-soft)"}}>
                      <div style={{fontSize:48,marginBottom:12}}><Icon n="package" size={48} inline /></div>
                      <p>{orders.length===0?"No orders yet.":"No orders match your search."}</p>
                    </div>
                  )}
                  {displayItems.length>orderLimit&&(
                    <button onClick={()=>setOrderLimit(l=>l+120)}
                      style={{alignSelf:"center",marginTop:6,background:"var(--surface)",border:"1.5px solid var(--border-mid)",borderRadius:4,padding:"10px 22px",fontSize:13,fontWeight:700,color:"var(--ink)",cursor:"pointer"}}>
                      Show more · {displayItems.length-orderLimit} more
                    </button>
                  )}
                </div>
              </>;
            })()}
          </div>
        ) : tab === "marketplace" ? (
          /* ── MARKETPLACE MANAGER TAB ── */
          <MarketplaceManager
            stores={mktStores}
            orders={orders}
            loading={mktLoading}
            saving={mktSaving}
            dirty={mktDirty}
            dragId={mktDragId}
            onLoad={loadMktStores}
            onSave={() => saveMktStores(mktStores)}
            onChange={onMktChange}
            onDragId={setMktDragId}
          />
        ) : tab === "stock" ? (
          /* ── STOCK MONITOR TAB ── */
          <StockMonitor stores={stores}/>
        ) : tab === "earnings" ? (
          /* ── BLOOM EARNINGS TAB ── */
          <BloomEarnings/>
        ) : tab === "subscriptions" ? (
          /* ── PRO SUBSCRIPTIONS TAB ── */
          <ProSubscriptions/>
        ) : (
          /* ── STATUS TAB ── */
          <HealthMonitor/>
        )
        )}
      </div>
    </div>
    </>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
// Sends a logged-out visitor to the customer login and remembers the page they were heading to, so that after they log in
// they land back on it (handleCustomerLogin reads bloom_redirect) instead of on the home page.
function LoginThenBack() {
  const loc = useLocation();
  try { localStorage.setItem("bloom_redirect", loc.pathname + loc.search); } catch { /* ignore */ }
  return <Navigate to="/customer/login" replace/>;
}

export default function App() {
  const navigate = useNavigate();
  const appLoc = useLocation(); // the nav's store theme follows the page the shopper is on
  // ── Business session state ─────────────────────────────────────────────────
  const [session, setSession]         = useState(null);
  const [users, setUsers]             = useState([DEMO_USER]);
  const [allProducts, setAllProducts] = useState({ [DEMO_ID]: DEMO_PRODUCTS });
  const [allOrders, setAllOrders]     = useState({ [DEMO_ID]: DEMO_ORDERS });
  const [currentStore, setCurrentStore] = useState(DEMO_ID);
  // ── Per-user cart (each customer/business gets their own persistent cart) ──
  // Key helpers
  const cartKeyFor = (cust, sess) => {
    if (cust?.id) return `bloom_cart_cust_${cust.id}`;
    if (sess?.role === "business" && sess?.userId) return `bloom_cart_biz_${sess.userId}`;
    return "bloom_cart_guest";
  };
  const loadCart = (key) => {
    try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
  };

  // Determine the correct cart key synchronously on startup by peeking at the
  // persisted session tokens — avoids always starting as "guest" and then
  // triggering a spurious guest→customer merge on every page refresh that
  // re-adds items the customer deliberately deleted.
  const initCartKey = () => {
    try {
      const cust = JSON.parse(localStorage.getItem("bloom:cust") || "null");
      if (cust?.customerId) return `bloom_cart_cust_${cust.customerId}`;
      const sess = JSON.parse(localStorage.getItem("bloom:sess") || "null");
      if (sess?.role === "business" && sess?.userId) return `bloom_cart_biz_${sess.userId}`;
    } catch {}
    return "bloom_cart_guest";
  };

  const cartKeyRef = useRef(initCartKey());
  const cartRef    = useRef([]);

  // Load from the correct key right away — no stale guest-cart bleed
  const [cart, setCart] = useState(() => normalizeCart(loadCart(cartKeyRef.current)));

  // Keep cartRef in sync so identity-swap handler always sees latest cart
  useEffect(() => { cartRef.current = cart; }, [cart]);

  // One line per product + variation, never above its stock — however the items got in (Discover tile, store page, product page).
  useEffect(() => { const n = normalizeCart(cart); if (n !== cart) setCart(n); }, [cart]);

  // Hosted checkout is the default; ?pay=modal opts a browser back to the old modal.
  // Also: if the customer abandoned a hosted checkout (hit back on Razorpay's page),
  // release the hold the moment they land back on the site — skip the success page,
  // which represents a completed payment.
  useEffect(() => {
    syncHostedPayFlag();
    syncMopPreviewFlag(); // MOP: ?mop=1/?mop=0 persists the cart's method-selector preview per-browser
    try {
      const abandon = localStorage.getItem("bloom_abandon_ids");
      if (abandon && window.location.pathname !== "/order-success") {
        api.releaseStock(abandon.split(",").filter(Boolean));
        localStorage.removeItem("bloom_abandon_ids");
      }
    } catch { /* ignore */ }
  }, []);

  // ── Customer session state ─────────────────────────────────────────────────
  const [customer, setCustomer] = useState(null); // logged-in customer account
  // True from first paint until the saved customer login (if any) has been looked up. Without it, a route that needs a
  // customer (e.g. "Track my order" right after paying, when Razorpay reloads the whole site) sees "nobody logged in"
  // for a moment and sends an already-logged-in shopper to the login page.
  const [custRestoring, setCustRestoring] = useState(() => { try { return !!CustSession.load()?.customerId; } catch { return false; } });
  // Platform-wide product list — needed ONLY by /directory (per-store product counts/previews). Loaded on demand.
  const allProductsLoadedRef = useRef(false);
  const loadAllProductsFlat = () => { allProductsLoadedRef.current = true; return supa.selectAll("bloom_products", "select=id,user_id,name,price,mrp,img,images,stock,category,collection_id,bogo_eligible,is_pinned,variations,variation_images,advanced_variations,description,created_at,is_live,hidden,scheduled_at,custom_question,require_upload&order=created_at.asc"); };
  useEffect(() => {
    if (!/^\/directory/.test(appLoc.pathname) || allProductsLoadedRef.current) return;
    loadAllProductsFlat().then(flat => {
      if (!flat.length) { allProductsLoadedRef.current = false; return; }
      const prods = { [DEMO_ID]: DEMO_PRODUCTS };
      for (const p of flat) { (prods[p.user_id] = prods[p.user_id] || []).push(p); }
      setAllProducts(prev => { const ownId = Session.load()?.userId; return ownId && prev[ownId] ? { ...prods, [ownId]: prev[ownId] } : prods; });
    });
  }, [appLoc.pathname]);

  // Persist cart to the current user's key whenever cart changes. SKIP the initial
  // mount write: loadCart already seeded state FROM localStorage, so the first run is
  // redundant — and on the /order-success page load it races the post-payment clear.
  // Without this guard, this effect fires on mount with the just-bought (stale) cart and
  // re-writes it into localStorage right after OrderSuccess's clearCart() wiped it,
  // leaving purchased items stuck in the cart ("paid but still in cart"). Skipping the
  // mount write closes that race; genuine cart edits (2nd run onward) still persist.
  const cartSaveMounted = useRef(false);
  useEffect(() => {
    if (!cartSaveMounted.current) { cartSaveMounted.current = true; return; }
    localStorage.setItem(cartKeyRef.current, JSON.stringify(cart));
  }, [cart]);

  // Belt-and-suspenders: save synchronously right before page unloads
  useEffect(() => {
    const save = () => localStorage.setItem(cartKeyRef.current, JSON.stringify(cartRef.current));
    window.addEventListener("beforeunload", save);
    return () => window.removeEventListener("beforeunload", save);
  }, []);

  // Hard-clear the cart everywhere so a post-payment reload can't re-hydrate it.
  // Resets the ref too (blocks the beforeunload re-save race) and wipes every
  // per-identity key (blocks the stale-key mismatch when guest→customer swapped
  // mid-checkout). Call ONLY on a confirmed purchase.
  const clearCart = () => {
    setCart([]);
    cartRef.current = [];
    // Guard: mark the cart as just-cleared so the identity key-swap effect can't resurrect
    // the just-bought items when the customer object finishes loading async on /order-success
    // (that effect re-saves the old cart — the "paid but still in cart" bug for logged-in users).
    try { sessionStorage.setItem("bloom_cart_cleared_at", String(Date.now())); } catch {}
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith("bloom_cart_"))
        .forEach(k => localStorage.removeItem(k));
    } catch {}
  };

  // Remove ONLY the specific item(s) just purchased (e.g. via "Buy Now") from the cart —
  // React state + every per-identity localStorage key — so a bought item never lingers in
  // the cart, WITHOUT wiping unrelated items. Matches on product id + chosen variation.
  // Called on the /order-success page (payment already confirmed) — see OrderSuccess.
  const removePurchasedItems = (bought) => {
    if (!Array.isArray(bought) || !bought.length) return;
    const vOf  = (c) => (c.selectedVariation?.name || c.selectedOption || c.selectedCombo || c.variation_name || null);
    const keep = (c) => !bought.some(b => c && c.id === b.id && (b.variation == null || vOf(c) === b.variation));
    setCart(prev => (Array.isArray(prev) ? prev.filter(keep) : prev));
    if (Array.isArray(cartRef.current)) cartRef.current = cartRef.current.filter(keep);
    try {
      Object.keys(localStorage).filter(k => k.startsWith("bloom_cart_")).forEach(k => {
        try { const a = JSON.parse(localStorage.getItem(k) || "[]"); if (Array.isArray(a)) localStorage.setItem(k, JSON.stringify(a.filter(keep))); } catch {}
      });
    } catch {}
  };

  // Backstop for "paid but still in cart": items from a checkout whose orders the server reports as paid are
  // removed from the cart even if this browser never reached /order-success (paid in a UPI app and returned
  // later, late webhook confirmation, returned in another browser). Runs on load, whenever the customer comes
  // back to the tab, and every 15s while a checkout is still waiting (a no-op without network when none is).
  const removePurchasedRef = useRef(null);
  removePurchasedRef.current = removePurchasedItems;
  useEffect(() => {
    const run = () => checkPendingCheckouts({ backendUrl: BACKEND_URL, onPaid: (items) => removePurchasedRef.current?.(items) });
    run();
    const onVisible = () => { if (document.visibilityState === "visible") run(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", run);
    const stopPoll = setVisibleInterval(run, 15000);
    return () => { document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", run); stopPoll(); };
  }, []);

  // Helper: merge guest cart items into customer's cart when they log in
  const mergeCartItems = (guestCart, customerCart) => {
    const merged = [...customerCart];
    guestCart.forEach(guestItem => {
      const existsInCustomer = merged.find(
        c => c.id === guestItem.id &&
             c.selectedVariation?.name === guestItem.selectedVariation?.name
      );
      if (!existsInCustomer) {
        merged.push(guestItem);
      }
    });
    return merged;
  };

  // When logged-in identity changes, save old cart and load the new user's cart
  useEffect(() => {
    const newKey = cartKeyFor(customer, session);
    if (newKey !== cartKeyRef.current) {
      const oldKey = cartKeyRef.current;
      // If a completed purchase JUST cleared the cart, treat the old cart as empty — otherwise
      // this effect (running when the customer object settles on /order-success) re-saves/merges
      // the stale just-bought items back, leaving them "paid but still in cart" for logged-in users.
      let clearedAt = 0; try { clearedAt = Number(sessionStorage.getItem("bloom_cart_cleared_at") || 0); } catch {}
      const justCleared = clearedAt && (Date.now() - clearedAt < 8000);
      const oldCart = justCleared ? [] : cartRef.current;

      // Load the new user's saved cart
      const saved = loadCart(newKey);

      // Merge: if switching FROM guest TO customer, merge guest items into customer's cart
      let finalCart = saved;
      if (oldKey === "bloom_cart_guest" && newKey.startsWith("bloom_cart_cust_")) {
        finalCart = mergeCartItems(oldCart, saved);
        // Clear the guest cart so stale items can't bleed back in on future refreshes
        localStorage.removeItem(oldKey);
      } else {
        // Non-merge swap: save current cart to old key before switching
        localStorage.setItem(oldKey, JSON.stringify(oldCart));
      }

      cartKeyRef.current = newKey;
      setCart(finalCart);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, session?.userId, session?.role]);

  // ── Maintenance mode + payments paused ───────────────────────────────────
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMsg,  setMaintenanceMsg]  = useState("");
  const [paymentsPaused,  setPaymentsPaused]  = useState(false);


  // ── Realtime: subscribes to new orders for logged-in business owner ───────
  const liveConnected = useRealtimeOrders(
    session?.role==="business"&&session?.userId!==DEMO_ID ? session.userId : null,
    (newOrder) => {
      // Fires automatically when Supabase pushes a new order — no refresh needed
      setAllOrders(prev => ({
        ...prev,
        [newOrder.store_id]: [newOrder, ...(prev[newOrder.store_id]||[])],
      }));
      // Re-fetch products so stock changes from this order are reflected immediately
      if (newOrder.store_id) {
        supa.selectAll("bloom_products", `user_id=eq.${newOrder.store_id}&order=created_at.asc`)
          .then(prods => {
            if (Array.isArray(prods) && prods.length > 0) {
              setAllProducts(prev => ({ ...prev, [newOrder.store_id]: prods }));
            }
          })
          .catch(() => {});
      }
    }
  );

  // ── Load Supabase data on mount ───────────────────────────────────────────
  useEffect(() => {
    // The public store list feeds the cart, store pages and nav. It used to be applied only AFTER the big
    // all-products download finished — and silently skipped if that one request failed — so on a slow or flaky
    // connection the app had no stores for a long time (cart: "Store not found"). Now it is applied the moment it
    // arrives, independent of the products batch, and retried a few times if it comes back empty.
    const applyStoreList = (supaUsers, savedSess) => {
      // supaUsers = public_stores (no PII). If a seller is logged in, keep their
      // FULL own-row (loaded via /api/me in loadUserData) over its PII-less twin,
      // so currentUser still has email/upi for Settings + owner-checkout prefill.
      setUsers(prev => {
        // MERGE each public row over whatever we already hold, never replace outright:
        // public_stores carries no private fields (upi_id / phone / email), so a plain
        // replace silently dropped them from app state and Settings rendered a blank UPI.
        const base = [DEMO_USER, ...supaUsers.map(s => {
          const old = prev.find(p => p.id === s.id);
          return old ? { ...old, ...s } : s;
        })];
        const ownId = savedSess?.userId;
        if (ownId) {
          const own = prev.find(x => x.id === ownId && x.email !== undefined);
          if (own) {
            // Own full row (from /api/me) merged over its PII-less public twin (main's fix:
            // never REPLACE, or the seller's private fields vanish). If the store isn't in
            // public_stores yet (brand-new / 0 products) it isn't in `base` — PREPEND it.
            return base.some(x => x.id === ownId)
              ? base.map(x => x.id === ownId ? { ...x, ...own } : x)
              : [DEMO_USER, own, ...supaUsers.filter(x => x.id !== ownId)];
          }
        }
        return base;
      });
    };
    const loadStoreList = async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        const rows = await supa.select("public_stores", "order=created_at.asc");
        if (Array.isArray(rows) && rows.length > 0) return rows;
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
      return [];
    };

    // Customer session restore with retries. Only a definite 401/403/404 means "not a valid session"; anything else
    // (timeout, 5xx, 429, offline) is transient → retry, then keep retrying quietly in the background.
    const restoreCustomer = async (savedCust) => {
      const once = async () => {
        try {
          const r = await fetch(`${BACKEND_URL}/api/customer/${savedCust.customerId}`, { headers: { "x-customer-id": savedCust.customerId } });
          if (r.ok) return { ok: true, row: await r.json() };
          if (r.status === 401 || r.status === 403 || r.status === 404) return { ok: true, row: null };
        } catch { /* transient */ }
        return { ok: false };
      };
      for (let i = 0; i < 3; i++) {
        const res = await once();
        if (res.ok) { if (res.row) { setCustomer(res.row); } setCustRestoring(false); return res.row ? [res.row] : []; }
        await new Promise(r => setTimeout(r, 800 * (i + 1)));
      }
      // Still failing: stop blocking the UI, keep the saved login, and keep trying in the background.
      setCustRestoring(false);
      let tries = 0;
      const tick = async () => {
        if (++tries > 12) return;
        const res = await once();
        if (res.ok) { if (res.row) setCustomer(res.row); return; }
        setTimeout(tick, 5000);
      };
      setTimeout(tick, 4000);
      return [];
    };

    const boot = async () => {
      // Fire maintenance check + Supabase loads in parallel — no waiting
      const [_, supaResult] = await Promise.allSettled([
        // Maintenance check (independent — doesn't block store data)
        fetch(`${BACKEND_URL}/api/settings`)
          .then(r=>r.json())
          .then(d=>{ setMaintenanceMode(!!d.maintenance_mode); setMaintenanceMsg(d.maintenance_message||""); setPaymentsPaused(!!d.payments_paused); })
          .catch(()=>{}),

        // All Supabase data in parallel
        (async () => {
          try {
            const savedSess = Session.load();
            const savedCust = CustSession.load();

            // Session restore + directory data all fire at the same time
            const [, custRows, supaUsers, allProdsFlat] = await Promise.all([
              // Restore business session
              savedSess?.userId ? loadUserData(savedSess) : Promise.resolve(),
              // Restore customer session
              // Restored in its OWN track: the login shows the moment this answers, not after the (multi-MB) product
              // list finishes. A temporary failure (slow server, 429, network) is retried — it must never look "signed out".
              savedCust?.customerId ? restoreCustomer(savedCust) : Promise.resolve([]),
              // All store users for directory (public-safe columns only — no PII/creds). Applied as soon as it lands.
              loadStoreList().then(rows => { if (rows.length) applyStoreList(rows, savedSess); return rows; }),
              // Every product on the platform (~2,600 rows / several MB) is ONLY used by the /directory page. It used to
              // download on EVERY visit — the main cause of the slow, sluggish site on phones. Now it loads on demand
              // (see the /directory effect below). Store pages, product pages and the marketplace fetch their own data.
              /^\/directory/.test(window.location.pathname) ? loadAllProductsFlat() : Promise.resolve([]),
            ]);

            if (Array.isArray(custRows) && custRows.length > 0) setCustomer(custRows[0]);
            setCustRestoring(false);

            if (supaUsers.length > 0 && allProdsFlat.length > 0) {
              const prods = { [DEMO_ID]: DEMO_PRODUCTS };
              for (const p of allProdsFlat) {
                if (!prods[p.user_id]) prods[p.user_id] = [];
                prods[p.user_id].push(p);
              }
              // Keep the logged-in seller's own full list (loadUserData fetched every column incl. `hidden`);
              // the flat public list is column-limited and must not overwrite it.
              setAllProducts(prev => {
                const ownId = savedSess?.userId;
                return ownId && prev[ownId] ? { ...prods, [ownId]: prev[ownId] } : prods;
              });
            }
          } catch(e) {
            console.info("Supabase load error (demo mode):", e.message);
            setCustRestoring(false);
          }
        })(),
      ]);
    };
    boot();
  }, []);

  // ── Load a specific user's data from Supabase ─────────────────────────────
  const loadUserData = async (sess, { skipNavigate = false } = {}) => {
    // Don't redirect if already on a public page (store preview, directory, etc.)
    const onPublicPage = /^\/(store|directory|pricing|design-studio|review\/)/.test(window.location.pathname);
    if (!sess?.userId || sess.userId===DEMO_ID) {
      setSession(sess||{role:"business",userId:DEMO_ID});
      if (!skipNavigate && !onPublicPage) navigate("/dashboard");
      return;
    }
    // Own row: fresh login passes it directly (sess.user); a restored session
    // fetches it via the token-gated /api/me (never reads password_hash to the browser).
    let u = sess.user;
    let definitelyInvalid = false;
    if (!u) {
      // Retry transient failures (timeout / 5xx / 429 / offline). Only a definite 401/403 may end the login —
      // a slow server used to log sellers out ("signed out" reports).
      for (let i = 0; i < 4 && !u && !definitelyInvalid; i++) {
        try {
          const r = await fetch(`${BACKEND_URL}/api/me`, { headers: { "Authorization": `Bearer ${sess.token || ""}` } });
          if (r.ok) u = (await r.json()).user;
          else if (r.status === 401 || r.status === 403 || r.status === 404) definitelyInvalid = true;
        } catch {}
        if (!u && !definitelyInvalid && i < 3) await new Promise(res => setTimeout(res, 800 * (i + 1)));
      }
    }
    if (!u) { if (definitelyInvalid) Session.clear(); return; }
    setUsers(prev => prev.find(x=>x.id===u.id) ? prev.map(x=>x.id===u.id?u:x) : [DEMO_USER,u,...prev.filter(x=>x.id!==DEMO_ID&&x.id!==u.id)]);

    const prods  = await supa.selectAll("bloom_products",`user_id=eq.${u.id}&order=created_at.asc`);
    const orders = await api.getOrdersByStore(u.id);
    setAllProducts(prev=>({...prev,[u.id]:prods}));
    setAllOrders(prev=>({...prev,[u.id]:orders.map(o=>({...o,date:o.order_date}))}));
    setSession(sess);
    if (!skipNavigate && !onPublicPage) navigate("/dashboard");
  };

  // ── Called on login/signup — saves session and loads data ────────────────
  const setSessionAndLoad = async (sess) => {
    const { user, ...persist } = sess;   // persist only {userId, role, token}; keep the full row in memory only
    Session.save(persist);
    if (sess.role==="customer") { setSession(persist); navigate("/directory"); return; }
    await loadUserData(sess);
  };

  // ── Update user in state (from Settings save) ─────────────────────────────
  const handleUserUpdated = (updated) => {
    setUsers(prev=>prev.map(u=>u.id===updated.id?updated:u));
  };

  // ── New order placed (from Storefront or Cart) ────────────────────────────
  const handleOrderPlaced = (storeId, order) => {
    setAllOrders(prev=>({
      ...prev,
      [storeId]: [order, ...(prev[storeId]||[])],
    }));
  };

  // ── Customer login handler ─────────────────────────────────────────────────
  const handleCustomerLogin = (c) => {
    setCustomer(c);
    const redirect = localStorage.getItem("bloom_redirect");
    localStorage.removeItem("bloom_redirect");
    navigate(redirect || "/");
  };

  const handleCustomerLogout = () => {
    // Save customer cart before switching back to guest
    localStorage.setItem(cartKeyRef.current, JSON.stringify(cartRef.current));
    CustSession.clear();
    setCustomer(null);
    // Switch to guest cart
    const guestKey = "bloom_cart_guest";
    cartKeyRef.current = guestKey;
    setCart(loadCart(guestKey));
    navigate("/");
  };

  const logout = async () => {
    // Save business cart before clearing session
    localStorage.setItem(cartKeyRef.current, JSON.stringify(cartRef.current));
    Session.clear();
    setSession(null);
    // Switch to guest cart
    const guestKey = "bloom_cart_guest";
    cartKeyRef.current = guestKey;
    setCart(loadCart(guestKey));
    navigate("/");
  };

  // ── Derived state ─────────────────────────────────────────────────────────
  const currentUser   = users.find(u=>u.id===session?.userId);
  // A logged-in seller will almost certainly open their dashboard pages next — fetch them quietly once the browser is idle.
  useEffect(() => {
    if (!currentUser?.id) return;
    const run = () => prefetchSellerPages();
    const h = typeof requestIdleCallback === "function" ? requestIdleCallback(run, { timeout: 4000 }) : setTimeout(run, 2500);
    return () => { typeof cancelIdleCallback === "function" && typeof h === "number" && cancelIdleCallback(h); };
  }, [currentUser?.id]);
  const myProducts    = allProducts[currentUser?.id] || [];
  const myOrders      = allOrders[currentUser?.id]   || [];
  const cartCount     = cart.reduce((s,i)=>s+i.qty,0);
  // A store's theme (Black / Neutral) belongs to that store's pages only — it must not stay on the nav after the shopper leaves.
  const navTemplate   = navTemplateFor({ isSeller: session?.role==="business", sellerTemplate: currentUser?.template, users, pathname: appLoc.pathname, subdomainSlug: getStoreSubdomain(), cart, slugToSubdomain });

  const setMyProducts = (prods) => {
    // Support both direct value and functional update (prev => newValue)
    if (typeof prods === "function") {
      setAllProducts(prev => {
        const cur = prev[currentUser?.id] || [];
        return { ...prev, [currentUser?.id]: prods(cur) };
      });
    } else {
      setAllProducts(prev => ({ ...prev, [currentUser?.id]: prods }));
    }
  };

  const goToDemo = () => {
    setSession(s=>s||{role:"customer"});
    navigate("/store/bloom-by-priya");
  };

  // Business owners can also shop — use their account as an effective customer
  const effectiveCustomer = customer || (session?.role === "business" && currentUser ? {
    id: currentUser.id,
    name: currentUser.name || currentUser.store_name || "Store Owner",
    email: currentUser.email || "",
    phone: currentUser.phone || "",
  } : null);

  // ── Nav page name from URL ────────────────────────────────────────────────
  // Used just for active nav highlight
  const location = window.location.pathname.replace("/","") || "home";

  return (
    <div className="noise-bg" style={{paddingTop: paymentsPaused && !maintenanceMode ? 56+38 : 56}}>
      <FontLoader/>
      <Nav
        page={location}
        setPage={(p) => {
          if (p==="dashboard"||p==="products"||p==="orders"||p==="settings"||p==="reviews"||p==="restock-requests"||p==="marketing") navigate(`/${p}`);
          else if (p==="login")     navigate("/login");
          else if (p==="signup")    navigate("/signup");
          else if (p==="directory") navigate("/directory");
          else if (p==="cart")      navigate("/cart");
          else                      navigate("/");
        }}
        session={session}
        orders={myOrders}
        live={liveConnected}
        onLogout={logout}
        cartCount={cartCount}
        customer={customer}
        onCustomerLogout={handleCustomerLogout}
        template={navTemplate}
        restockEnabled={currentUser?.restock_enabled !== false}
        storeSlug={currentUser?.store_slug}
        showDesignStudio={dsAllowed(session?.userId)}
      />
      {/* ── Payments paused banner (sitewide, non-intrusive) ── */}
      {paymentsPaused && !maintenanceMode && !window.location.pathname.startsWith("/admin") && (
        <div style={{
          position:"fixed", top:56, left:0, right:0, zIndex:200,
          background:"linear-gradient(90deg,#f59e0b,#fbbf24)",
          padding:"9px 20px", textAlign:"center",
          fontSize:13, fontWeight:600, color:"#1a1200",
          boxShadow:"0 2px 8px rgba(245,158,11,0.3)",
          letterSpacing:"0.01em",
        }}>
          <Icon n="bag" size="1.1em" inline /> We're not accepting transactions right now — but feel free to browse and explore stores!
        </div>
      )}
      {/* The red "temporary system issue" incident banner used to sit here. Removed —
          it covered the top of every seller page (including the Design Studio topbar)
          and wasn't wanted. Maintenance mode below still gates the app properly. */}
      {/* ── Maintenance gate (admin always passes through) ── */}
      {maintenanceMode && !window.location.pathname.startsWith("/admin") && (
        <MaintenancePage message={maintenanceMsg}/>
      )}
      {(!maintenanceMode || window.location.pathname.startsWith("/admin")) && (
      <Suspense fallback={<RouteFallback/>}>
      <Routes>
        {/* ── Public routes ── */}
        <Route path="/"          element={(() => {
          // On storename.bloomhq.in, the root shows that store's storefront (not the
          // marketplace). slugOverride falls back to the raw subdomain until `users`
          // loads and we can map it to the real store_slug.
          const sub = getStoreSubdomain();
          if (!sub) return <Marketplace cart={cart} setCart={setCart} customer={effectiveCustomer} setPage={p=>navigate(`/${p}`)} goToDemo={goToDemo} paymentsPaused={paymentsPaused}/>;
          const subStore = users.find(u => slugToSubdomain(u.store_slug) === sub);
          return <StorefrontPage users={users} allProducts={allProducts} allOrders={allOrders} setAllOrders={setAllOrders} cart={cart} setCart={setCart} customer={customer} setCurrentStore={setCurrentStore} setUsers={setUsers} paymentsPaused={paymentsPaused} slugOverride={subStore?.store_slug || sub}/>;
        })()}/>
        <Route path="/discover"  element={<Navigate to="/" replace/>}/>
        <Route path="/login"     element={<Auth mode="login"  setPage={p=>navigate(`/${p}`)} setSessionAndLoad={setSessionAndLoad}/>}/>
        <Route path="/signup"    element={<Auth mode="signup" setPage={p=>navigate(`/${p}`)} setSessionAndLoad={setSessionAndLoad}/>}/>
        <Route path="/directory" element={
          <StoreDirectory
            users={users} allProducts={allProducts}
            setCurrentStore={setCurrentStore}
            setPage={p=>navigate(`/${p}`)}
            setSession={setSession}
          />
        }/>
        <Route path="/discover" element={<Navigate to="/" replace/>}/>
        <Route path="/cart" element={
          <Cart cart={cart} setCart={setCart} clearCart={clearCart} storeUser={users.find(u=>u.id===(currentStore!==DEMO_ID?currentStore:cart[0]?.user_id))||DEMO_USER} onOrderPlaced={handleOrderPlaced} customer={effectiveCustomer} paymentsPaused={paymentsPaused}/>
        }/>

        {/* ── Category browse pages (mega-menu) ── */}
        <Route path="/category/:cat"      element={<CategoryPage/>}/>
        <Route path="/category/:cat/:sub" element={<CategoryPage/>}/>

        {/* ── Product detail page (Marketplace only) ── */}
        <Route path="/product/:productId" element={
          <ProductPage cart={cart} setCart={setCart} customer={effectiveCustomer} session={session} onCart={()=>{}}/>
        }/>

        {/* ── Hosted-checkout result pages (Razorpay redirect flow) ── */}
        <Route path="/order-success"  element={<OrderSuccess setCart={setCart} clearCart={clearCart} removePurchasedItems={removePurchasedItems}/>}/>
        <Route path="/payment-failed" element={<PaymentFailed/>}/>

        {/* ── Password reset routes ── */}
        <Route path="/forgot-password"  element={<ForgotPassword/>}/>
        <Route path="/reset-password"   element={<ResetPassword/>}/>

        {/* ── Customer account routes ── */}
        <Route path="/customer/login"  element={<CustomerAuth mode="login"  setPage={p=>navigate(p)} onCustomerLogin={handleCustomerLogin}/>}/>
        <Route path="/customer/signup" element={<CustomerAuth mode="signup" setPage={p=>navigate(p)} onCustomerLogin={handleCustomerLogin}/>}/>
        <Route path="/customer/orders" element={
          effectiveCustomer
            ? <CustomerDashboard customer={effectiveCustomer} onLogout={handleCustomerLogout}/>
            : (custRestoring || (session?.role==="business" && !currentUser))
              ? <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--ink-soft)",fontSize:15}}>Loading your orders…</div>
              : <LoginThenBack/>
        }/>

        {/* ── Each store gets its own public URL: /store/:slug ── */}
        <Route path="/store/:slug" element={
          <StorefrontPage users={users} allProducts={allProducts} allOrders={allOrders} setAllOrders={setAllOrders} cart={cart} setCart={setCart} customer={customer} setCurrentStore={setCurrentStore} setUsers={setUsers} paymentsPaused={paymentsPaused}/>
        }/>

        {/* ── Business dashboard routes (require login) ── */}
        <Route path="/dashboard" element={
          session?.role==="business"&&currentUser
            ? <Dashboard user={currentUser} products={myProducts} orders={myOrders} live={liveConnected}/>
            : <Navigate to="/login" replace/>
        }/>
        <Route path="/products" element={
          session?.role==="business"&&currentUser
            ? <Products userId={currentUser.id} products={myProducts} setProducts={setMyProducts} user={currentUser} onUserUpdated={handleUserUpdated}/>
            : <Navigate to="/login" replace/>
        }/>
        {/* Design Studio — restricted rollout: allowlist-gated (src/config/dsAccess.js), not just
            paywalled. A business account that isn't allowlisted never mounts DesignStudioGate at
            all and is bounced to /dashboard, same as if the route didn't exist. Guard tolerates
            the users-still-loading race on hard refresh so it doesn't bounce to /login instead. */}
        <Route path="/design-studio" element={
          session?.role==="business"
            ? (currentUser
                ? (dsAllowed(session?.userId)
                    ? <Suspense fallback={<div style={{padding:"80px 20px",textAlign:"center",color:"var(--ink)",fontFamily:"Syne"}}>Loading your studio…</div>}>
                        <DesignStudioGate storeUser={currentUser} customer={customer} cart={cart} setCart={setCart}/>
                      </Suspense>
                    : <Navigate to="/dashboard" replace/>)
                : <div style={{padding:"80px 20px",textAlign:"center",color:"var(--ink)",fontFamily:"Syne"}}>Loading your studio…</div>)
            : <Navigate to="/login" replace/>
        }/>
        <Route path="/orders" element={
          session?.role==="business"&&currentUser
            ? <Orders orders={myOrders} storeUser={currentUser}/>
            : <Navigate to="/login" replace/>
        }/>
        <Route path="/settings" element={
          session?.role==="business"&&currentUser
            ? <Settings user={currentUser} onUserUpdated={handleUserUpdated}/>
            : <Navigate to="/login" replace/>
        }/>
        <Route path="/reviews" element={
          session?.role==="business"&&currentUser
            ? <StoreReviewsQNA storeId={currentUser.id} session={session} user={currentUser}/>
            : <Navigate to="/login" replace/>
        }/>
        <Route path="/restock-requests" element={
          session?.role==="business"&&currentUser
            ? <RestockRequests storeId={currentUser.id} template={currentUser.template}/>
            : <Navigate to="/login" replace/>
        }/>
        <Route path="/marketing" element={
          session?.role==="business"&&currentUser
            ? <Marketing storeId={currentUser.id} template={currentUser.template}/>
            : <Navigate to="/login" replace/>
        }/>
        <Route path="/marketing/compose/:kind" element={
          session?.role==="business"&&currentUser
            ? <MarketingCompose storeId={currentUser.id} storeName={currentUser.store_name} template={currentUser.template}/>
            : <Navigate to="/login" replace/>
        }/>

        {/* ── Admin ── */}
        <Route path="/admin" element={<AdminPage onProductDeleted={(userId,prodId)=>setAllProducts(prev=>({...prev,[userId]:(prev[userId]||[]).filter(p=>p.id!==prodId)}))}/>}/>
        <Route path="/admin/store/:storeId" element={<AdminStoreView/>}/>

        {/* ── Legal & info pages ── */}
        <Route path="/privacy" element={<PrivacyPolicy/>}/>
        <Route path="/about"   element={<About/>}/>
        <Route path="/terms"   element={<Terms/>}/>
        <Route path="/refund"  element={<Refund/>}/>
        <Route path="/review/:orderId" element={<ReviewOrder/>}/>

        {/* ── Catch-all ── */}
        <Route path="*" element={<Navigate to="/" replace/>}/>
      </Routes>
      </Suspense>
      )}
    </div>
  );
}
